import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, HostListener, Injector, Input, Output, OutputRefSubscription, Signal, TemplateRef, ViewChild, WritableSignal, afterNextRender, computed, effect, forwardRef, inject, model, output, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import type { Reference } from '@apollo/client/cache';
import { ApolloError } from '@apollo/client/errors';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { CalculateDocumentLineItemPrices, DEFAULT_DECIMAL_PRECISION, ICsvExportColumn, ProductFieldsInLineItem, buildUnitConversionsMetadataFromItems, clearLineItemDiscountPercentages, filterObjectByClass, grossPriceOf, isAutomaticDiscountLookupResolved, isDocumentLineItemMissingStockBacking, isLineItemDiscountPercentagesUnset, isTradingSetDocumentLineItem, lineItemContractFactorEffective, lineItemForAutomaticDiscountLookup, netPriceOf, pickPreferredProductPriceInfo, productPriceSourcePriority, resolveContractFactorTermViolation, shouldCommitAutomaticDiscountLookup, shouldLookupAutomaticLineItemDiscounts, userHasSuperAdminRole, xCalculateRoundPlaces, xDecimalPlacesAsFractionDigits, xDecimalPlacesAsString, xMultiply } from '@phoenix/common';
import { CurrencyCode, ErrorCode, LanguageCode, simpleDeepClone } from '@phx-erp/shared';
import { Apollo, gql } from 'apollo-angular';
import { deepEqual } from 'fast-equals';
import { MenuItem, SharedModule } from "primeng/api";
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { PopoverModule } from 'primeng/popover';
import { Table, TableCheckbox, TableContextMenuSelectEvent, TableHeaderCheckbox, TableModule, TableRowReorderEvent, TableRowSelectEvent, TableRowUnSelectEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, Subscription, catchError, debounceTime, lastValueFrom, skip } from 'rxjs';
import * as dot from 'ts-dot-prop';
import { v4 as uuidv4 } from 'uuid';
import { EditLinks } from '../../../../@core/constants/edit-links';
import { S_CONTRACT_ASSISTANT_GENERATE_ALL_UNTIL_REFERENCE_DATE, S_CONTRACT_ASSISTANT_SAVE_LINE_ITEMS_WITH_STOCK_ERRORS, S_CONTRACT_ASSISTANT_USE_REFERENCE_DATE_AS_DOCUMENT_DATE, S_DLI_ROW_GROUP_MODE } from "../../../../@core/constants/settings-keys";
import { MENTION_LANGUAGE_SOURCE, MentionLanguageSource } from '../../../../@core/directives/mention.directive';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../../@core/services/modal/modal.service';
import { ActionBarItem } from '../../../../@core/services/nav-builder/nav-builder-types';
import { SettingsService } from '../../../../@core/services/settings.service';
import { SidebarService } from '../../../../@core/sidebar/sidebar.service';
import { ColumnConfigEx, ColumnDefinitionEx, PrimeColumnDef } from '../../../../@core/types/column-config-types-extended';
import { FocusMode } from '../../../../@core/types/focus-mode.enum';
import { resolveCopyOptions } from '../../../../@core/utils/copy-with-options.helper';
import { GET_DOCUMENTLINEITEMS, GET_DOCUMENTLINEITEMS_SINGLE_PREDECESSOR, GET_DOCUMENTLINEITEMS_WITH_DOC, GET_DOCUMENTLINEITEMS_WITH_DOC_AND_PREDECESSOR } from '../../../../data/definitions/document-definition';
import { AccessoryAppliedOnDirection, AccessoryConfig, AccessoryConfigProductLink, AccessoryInsertMode, ColumnConfig, ConfigOriginType, CostCenter, CostObject, DliRelationLinkInformation, Document, DocumentDirection, DocumentInput, DocumentLineItem, DocumentLineItemInput, DocumentLineItemSearchInput, DocumentLineItemType, DocumentStatusType, DocumentType, Operator, PriceSourceType, Product, ProductInput, ProductPriceInfoInput, ProductType, ProductUnitConversionLink, SalesTaxCode, SalesTaxCodeDetail, SaveLineItemOptions, SearchInput, SortOrder, StockAction, StoragePlace, TaxLine, TaxType, Unit, VendorPriceListAddressLink } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { ColumnChooserRenameDialogComponent } from "../../../../shared/components/column-chooser-rename-dialog/column-chooser-rename-dialog.component";
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { PhxInputComponent } from '../../../../shared/components/phx-input/phx-input.component';
import { LocaleCurrencyPipe } from '../../../../shared/pipes/locale-currency.pipe';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { ServerSettingsService } from '../../../../shared/services/server-settings.service';
import { subscribeUntilComponentDestroyed } from '../../../../shared/utils/subscribe-until-component-destroyed';
import { DLILineSumInputComponent } from '../../../custom-formly/dli-line-sum-input/dli-line-sum-input.component';
import { StorageQuantityNumberInputComponent } from '../../../custom-formly/storage-quantity-number-input/storage-quantity-number-input.component';
import { Hotkey } from '../../../hotkeys/model/hotkey.model';
import { HotkeysService } from '../../../hotkeys/services/hotkeys.service';
import { AllowIn } from '../../../hotkeys/types-interfaces/hotkey.options';
import { CostCenterDropdownComponent } from '../../../masterdata/cost-types/controls/cost-centre-dropdown/cost-centre-dropdown.component';
import { CostObjectDropdownComponent } from '../../../masterdata/cost-types/controls/cost-object-dropdown/cost-object-dropdown.component';
import { SalesTaxCodeDropDownComponent } from '../../../masterdata/tax/controls/sales-tax-code-drop-down copy/sales-tax-code-drop-down.component';
import { SalesTaxCodeDetailService } from '../../../masterdata/tax/services/sales-tax-code-detail.service';
import { AccessoryConfigProductLinkListWidgetComponent } from '../../../products/components/accessory-config-product-link-list-widget/accessory-config-product-link-list-widget.component';
import { ProductListCustomColumnDialogComponent } from "../../../products/components/product-list-custom-column-dialog/product-list-custom-column-dialog.component";
import { ProductPriceInfoComponent, ProductPriceInfoExt } from '../../../products/modules/product-price-info/product-price-info-widget/product-price-info.component';
import { AccessoryConfigProductLinkService } from '../../../products/services/accessory-config-product-link.service';
import { ProductPriceInfoService } from '../../../products/services/product-price-info.service';
import { ProductService } from '../../../products/services/product.service';
import { VendorPriceListAddressLinkService } from '../../../products/services/vendor-price-list-address-link.service';
import { AllStoragePlacesIdentifier, getCustomColumnLoadSpec, getCustomColumnValue } from '../../../products/utils/custom-column-values';
import { TaskPanelService } from '../../../task/services/task-panel.service';
import { StockHistoryCardsComponent } from '../../../warehouse/components/stock-history-cards/stock-history-cards.component';
import { StoragePlaceDropdownComponent } from '../../../warehouse/controls/storage-place-dropdown/storage-place-dropdown.component';
import { ProductStockChartDiagramComponent } from '../../../warehouse/modules/product-stock-chart-diagram/product-stock-chart-diagram.component';
import { StoragePlaceService } from '../../../warehouse/services/storage-place.service';
import { DliProductDropDownComponent } from '../../modules/dli-product-dropdown/dli-product-dropdown.component';
import { ConversionResult, DliUnitConversionDropDownComponent } from '../../modules/dli-unit-conversion-drop-down/dli-unit-conversion-drop-down.component';
import { DocumentLineItemService } from '../../services/document-line-item.service';
import { DocumentService } from '../../services/document.service';
import { LineItemPriceHistoryApplyPayload } from '../../services/line-item-analytics.service';
import { LineItemDataService } from '../../services/line-item-data.service';
import {
  applyContractDetailsDefaultsIfNeeded,
  applyContractFactorToDli,
  formatQuantityWithContractFactor,
  isContractDocument,
  isContractRelatedDataChange,
  resolveContractFactorRaw,
  shouldShowContractLineItemFields,
} from '../../utils/contract-line-item-ui.helper';
import { ContractTermSnapshot, confirmContractTermChangesDialog, hasContractTermChanges, snapshotContractTerms } from '../../utils/contract-term-change';
import { DocumentLineItemPositionTextComponent } from '../document-line-item-position-text/document-line-item-position-text.component';
import { DocumentLineItemStatusColumnComponent } from '../document-line-item-status-column/document-line-item-status-column.component';
import { ItemPriceColumnComponent } from '../item-price-column/item-price-column.component';
import { AllocatableLineItemService } from '../warehouse-disposition/services/allocatable-line-item.service';
import { WidgetAddEditLineItemListDetailComponent } from '../widget-add-edit-line-item-list-detail/widget-add-edit-line-item-list-detail.component';
import {
  ManualOverrideResult,
  WidgetDliManualQuantityAndStoragePlaceChooserComponent,
} from '../widget-dli-manual-quantity-and-storage-place-chooser/widget-dli-manual-quantity-and-storage-place-chooser.component';
import { WidgetDocumentLineItemLinksComponent } from "../widget-document-line-item-links/widget-document-line-item-links.component";
import { WidgetEditDliStatusComponent } from '../widget-edit-dli-status/widget-edit-dli-status.component';
import { DocumentLineItemCsvImportResult, WidgetImportDliFromFileComponent } from '../widget-import-dli-from-file/widget-import-dli-from-file.component';
import { WidgetLineItemPriceHistoryComponent } from '../widget-line-item-price-history/widget-line-item-price-history.component';
import { graphQlDateOnly } from './graph-ql-date-only';
import { DocumentLineItemDisplayClass } from './types-interfaces';

interface DliEx extends DocumentLineItemDisplayClass {
  product?: Product;
  predecessorKey?: string;
  discountPromise?: Promise<void>;
}

/**
 * Per edit session state of the quantity / open quantity pair.
 * `transferredQuantity` (quantity − openQuantity) stays constant while editing, so changing one side
 * always recalculates the other one.
 */
interface OpenQuantityEditState {
  transferredQuantity: number;
  /** Open quantity when the row edit started — baseline for the sign check. */
  initialOpen: number;
  /** Last open quantity that passed validation — restored when an entry is rejected. */
  lastValidOpen: number;
  /** Last quantity seen on the row — detects changes made in the quantity column or the detail view. */
  lastQuantity: number;
}

export enum CostType {
  CENTER = "CENTER",
  OBJECT = "OBJECT"
}

@Component({
  selector: 'app-widget-add-edit-line-item-list',
  templateUrl: './widget-add-edit-line-item-list.component.html',
  styleUrls: ['./widget-add-edit-line-item-list.component.scss'],
  // grid cells and the line item detail get /t text templates in the line item language
  providers: [{ provide: MENTION_LANGUAGE_SOURCE, useExisting: forwardRef(() => WidgetAddEditLineItemListComponent) }],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TooltipModule,
    TableModule,
    TranslatePipe,
    LocaleCurrencyPipe,
    ButtonModule,
    PopoverModule,
    PhxCheckboxComponent,
    DLILineSumInputComponent,
    LocaleDatePipe,
    RouterModule,
    WidgetAddEditLineItemListDetailComponent,
    BaseListWrapperComponent,
    TooltipModule,
    TranslateModule,
    DrawerModule,
    PopoverModule,
    PhxCheckboxComponent,
    ProductStockChartDiagramComponent,
    StockHistoryCardsComponent,
    WidgetLineItemPriceHistoryComponent,
  ]
})
export class WidgetAddEditLineItemListComponent extends NewBaseListComponent<DliEx> implements MentionLanguageSource {
  injector = inject(Injector);
  private readonly settingsService = inject(SettingsService);

  // Store the initial value of noBaseEdit from Input to preserve parent component settings
  private initialNoBaseEdit: boolean;

  override destroyRef = inject(DestroyRef);

  private readonly allocatableLineItemService = inject(AllocatableLineItemService);

  DocumentStatusType = DocumentStatusType;

  // color mapping to price Source
  colorMappingPriceSource = {
    "CUSTOMERPRICE": "yellow",
    "PRICELIST": "red",
    "MANUALPRICE": "black",
  }

  @Input()
  /**
   * document id
   */
  id: string;

  @Input() showContractGenerateButton = false;
  @Input() contractGenerateDisabled = true;
  @Input() contractGenerateLoading = false;
  @Input() contractGenerateTooltip: string | null = null;
  @Input() contractSaveLineItemsWithStockErrors = false;
  @Output() contractGenerateClick = new EventEmitter<void>();
  @Output() contractSaveLineItemsWithStockErrorsChange = new EventEmitter<boolean>();

  readonly saveLineItemsWithStockErrors = this.settingsService.createSettingsSignal(
    S_CONTRACT_ASSISTANT_SAVE_LINE_ITEMS_WITH_STOCK_ERRORS,
    false,
  );
  readonly generateAllUntilCutOffDate = this.settingsService.createSettingsSignal(
    S_CONTRACT_ASSISTANT_GENERATE_ALL_UNTIL_REFERENCE_DATE,
    false,
  );
  readonly useCutOffDateAsDocumentDate = this.settingsService.createSettingsSignal(
    S_CONTRACT_ASSISTANT_USE_REFERENCE_DATE_AS_DOCUMENT_DATE,
    false,
  );

  onSaveLineItemsWithStockErrorsChange(enabled: boolean): void {
    this.saveLineItemsWithStockErrors.set(enabled);
  }

  ongenerateAllUntilCutOffDateChange(enabled: boolean): void {
    this.generateAllUntilCutOffDate.set(enabled);
  }

  onuseCutOffDateAsDocumentDateChange(enabled: boolean): void {
    this.useCutOffDateAsDocumentDate.set(enabled);
  }

  document = model<Document>();

  mentionLanguage(): LanguageCode | null {
    return this.dliService.lineItemLanguage(this.document());
  }

  quantityColumnHeader = viewChild<TemplateRef<unknown>>('quantityColumnHeader');
  openQuantityColumnHeader = viewChild<TemplateRef<unknown>>('openQuantityColumnHeader');
  requestedQuantityColumnHeader = viewChild<TemplateRef<unknown>>('requestedQuantityColumnHeader');
  transferredQuantityColumnHeader = viewChild<TemplateRef<unknown>>('transferredQuantityColumnHeader');

  readonly documentItemsQuantitySumDisplay = computed(() => {
    const doc = this.document();
    const sum = doc?.itemsQuantitySum;
    if (sum == null || sum === 0) {
      return null;
    }
    return xDecimalPlacesAsString(sum, 1); //doc?.decimalPlaces ?? this.decimalInput ?? DEFAULT_DECIMAL_PRECISION
  });

  readonly documentItemsOpenQuantitySumDisplay = computed(() => {
    const doc = this.document();
    const sum = doc?.itemsOpenQuantitySum;
    if (sum == null || sum === 0) {
      return null;
    }
    return xDecimalPlacesAsString(sum, 1); //doc?.decimalPlaces ?? this.decimalInput ?? DEFAULT_DECIMAL_PRECISION
  });

  readonly documentItemsRequestedQuantitySumDisplay = computed(() => {
    const doc = this.document();
    const sum = doc?.itemsRequestedQuantitySum;
    if (sum == null || sum === 0) {
      return null;
    }
    return xDecimalPlacesAsString(sum, 1); //doc?.decimalPlaces ?? this.decimalInput ?? DEFAULT_DECIMAL_PRECISION
  });

  readonly documentItemsTransferredQuantitySumDisplay = computed(() => {
    const doc = this.document();
    const sum = doc?.itemsTransferredQuantitySum;
    if (sum == null || sum === 0) {
      return null;
    }
    return xDecimalPlacesAsString(sum, 1);
  });

  //for change detection, see comment tagged with #currentlyLoadedDocumentId
  private currentlyLoadedDocumentId: string;

  get documentWithoutLineItems(): Document {
    return { ...this.document(), lineItems: null };
  }

  override dataTestId = model<string>();

  itemsChanged = output<DliEx[]>()

  StockAction = StockAction;
  ProductType = ProductType;
  CostType = CostType;
  TaxType = TaxType;
  DocumentLineItemType = DocumentLineItemType;

  formlyChangesSub = model<Subscription>();

  accessoryConfigAppliedSig: WritableSignal<AccessoryConfig[]> = signal<AccessoryConfig[]>([]);
  accessoryConfigAskSig: WritableSignal<AccessoryConfig[]> = signal<AccessoryConfig[]>([]);
  accessoryConfigAutoSig: WritableSignal<AccessoryConfig[]> = signal<AccessoryConfig[]>([]);
  accessoryConfigManualSig: WritableSignal<AccessoryConfig[]> = signal<AccessoryConfig[]>([]);

  loadDocumentTooSig = model<boolean>(false);
  cachedLastUnitConversion = model<ConversionResult>(null);
  /** Blocks unit-conversion value handler while scan/programmatic sync runs (avoids stale async loadUnitConversions). */
  private unitConversionValueSyncSuspended = 0;

  get accessoryConfigExisting(): boolean {
    if (this.accessoryConfigAskSig().length > 0 || this.accessoryConfigAutoSig().length > 0 || this.accessoryConfigManualSig().length > 0)
      return true;
    else return false;
  }

  tableWidth: number;
  newItemIndex: number;
  pDataTable: HTMLDivElement;
  /** when canceling editing a existing item, the "revert" data will be from here */
  clonedItems: DliEx[] = [];
  /** `clonedItems` is shallow - the nested contract line would follow in-place form edits */
  private contractTermsAtEditStart = new Map<string, ContractTermSnapshot>();
  addDLIPosNum: number;
  firstSelectedId: string;
  /** Set by SBOMH mode toggle dialog; applied once on the next {@link onRowEditSave}. */
  private pendingRecalculateTradingSetPrices = false;

  /** Right drawer for line-item stock history (replaces modal). */
  stockHistoryDrawerOpen = signal(false);
  /** DLI whose stock history is shown in the drawer; updates on prev/next navigation. */
  stockHistoryDliId = signal<string | undefined>(undefined);

  readonly stockHistoryDliIndex = computed(() => {
    const id = this.stockHistoryDliId();
    if (!id) {
      return -1;
    }
    return this.itemsSig().findIndex((item) => item.id === id);
  });

  /** 0-based index across all document line items (paginated list). */
  readonly stockHistoryGlobalIndex = computed(() => {
    this.itemsSig();
    const pageIndex = this.stockHistoryDliIndex();
    if (pageIndex < 0) {
      return -1;
    }
    const first = this.table()?.first ?? this.currentSearchInput?.skip ?? 0;
    return first + pageIndex;
  });

  readonly hasPreviousStockHistoryDli = computed(
    () => this.stockHistoryGlobalIndex() > 0
  );

  readonly hasNextStockHistoryDli = computed(() => {
    this.itemsSig();
    const globalIndex = this.stockHistoryGlobalIndex();
    const totalRecords = this.table()?.totalRecords ?? 0;
    return globalIndex >= 0 && globalIndex < totalRecords - 1;
  });

  readonly stockHistoryNavigating = signal(false);

  private stockHistoryNavRequestId = 0;

  private readonly stockHistoryCardsRef = viewChild(StockHistoryCardsComponent);

  /** Footer visible when drawer is open and more than one line item exists globally. */
  readonly showStockHistoryFooter = computed(() => {
    this.itemsSig();
    return this.stockHistoryDrawerOpen() && (this.table()?.totalRecords ?? 0) > 1;
  });

  readonly stockHistoryShowSort = computed(
    () => this.stockHistoryCardsRef()?.showSortControl() ?? false
  );

  readonly stockHistoryShowTransactionCount = computed(() => {
    const cards = this.stockHistoryCardsRef();
    return cards != null && !cards.loading() && !cards.loadFailed();
  });

  readonly stockHistoryTransactionCount = computed(
    () => this.stockHistoryCardsRef()?.transactionCount() ?? 0
  );

  readonly stockHistorySortIcon = computed(
    () => this.stockHistoryCardsRef()?.sortIcon() ?? ''
  );

  readonly stockHistorySortTooltip = computed(
    () => this.stockHistoryCardsRef()?.sortTooltipKey() ?? ''
  );

  /** When true, stock history cards show unit value beside quantity. */
  stockHistoryShowValue = signal(false);

  readonly stockHistoryValueToggleTooltipKey = computed(() =>
    this.stockHistoryShowValue()
      ? 'document-line-item.stock-history.hide-unit-value'
      : 'document-line-item.stock-history.show-unit-value'
  );

  /** Unit label for stock history cards when transactions omit unitId. */
  readonly stockHistoryLineItemUnitIdentifier = computed(() => {
    const dliId = this.stockHistoryDliId();
    if (!dliId) {
      return undefined;
    }
    const item = this.itemsSig().find((row) => row.id === dliId);
    if (!item) {
      return undefined;
    }
    const fromUnit = item.unit?.identifier?.trim();
    if (fromUnit) {
      return fromUnit;
    }
    const fromProduct = item.product?.unit?.identifier?.trim();
    if (fromProduct) {
      return fromProduct;
    }
    const fromBaseUnit = item.baseUnit?.identifier?.trim();
    if (fromBaseUnit) {
      return fromBaseUnit;
    }
    return item.unitIdentifier?.trim() || undefined;
  });

  toggleStockHistorySort(): void {
    this.stockHistoryCardsRef()?.toggleSortByTransactionDate();
  }

  toggleStockHistoryValue(): void {
    this.stockHistoryShowValue.update((v) => !v);
  }

  details: boolean = false;
  subColsOn: boolean[] = [];
  warehouseNumbers: boolean = false;
  detailIcon: string = "fa-caret-down";
  unstockedItemsSig = signal<DliEx[]>([])
  selectedQuantitySig = model<number>(0);
  selectedProductSig = model<Product>(null);
  selectedStoragePlaceIdSig = model<string>(null);
  storageQuantityNumberInput: StorageQuantityNumberInputComponent;
  discountPercentageInput: PhxInputNumberComponent;
  discountPercentage2Input: PhxInputNumberComponent;
  unitConversionDropDownComponent: DliUnitConversionDropDownComponent;
  storagePlaceDropdownComponent: StoragePlaceDropdownComponent;
  isStorableInput: PhxCheckboxComponent;
  private discountCellRefs = new Map<string, HTMLElement>();
  activeUnitConversions = model<ConversionResult[]>([]);
  activeUnitConversionValue = model<ConversionResult>();

  itemPriceInput = signal<ProductPriceInfoComponent>(null);

  @ViewChild('listWrapper')
  listWrapper: BaseListWrapperComponent;

  productPriceInfoComponent: ProductPriceInfoComponent;
  salesTaxCodeDropDownComponent: SalesTaxCodeDropDownComponent;

  canMoveUpSig: WritableSignal<boolean> = signal<boolean>(false);
  canMoveDownSig: WritableSignal<boolean> = signal<boolean>(false);
  canIndentSignal: WritableSignal<boolean> = signal<boolean>(false);
  canOutdentSignal: WritableSignal<boolean> = signal<boolean>(false);
  productStorageVisible = signal(false);
  warehouseSidebarProductId = signal<string | undefined>(undefined);

  priceHistoryDrawerOpen = signal(false);
  priceHistoryProductId = signal<string | undefined>(undefined);
  priceHistoryCustomerId = signal<string | undefined>(undefined);
  priceHistoryDocumentDirection = signal<DocumentDirection | undefined>(undefined);
  priceHistoryDocumentType = signal<string | undefined>(undefined);
  priceHistoryLineItemId = signal<string | undefined>(undefined);
  private warehouseContextMenuItem: MenuItem;

  tableAscendingSig = model<boolean>(true);
  upDownDirectionalNumberSig = model<number>(1);
  discountedLinePriceColumn = model<PrimeColumnDef>();
  discountedLinePriceWithTaxColumn = model<PrimeColumnDef>();

  override viewName = model<string>(undefined);

  showManualStockTransactionButton: Signal<boolean> = computed(() => {
    //TODO also check if we actually have a new quantity to choose
    // if ([StockAction.IN, StockAction.OUT].includes(this.document?.documentDefinition?.stockAction))
    if (this.editModel()?.product?.productType === ProductType.STORE && (this.document()?.documentDefinition?.stockAction === StockAction.IN || this.document()?.documentDefinition?.stockAction === StockAction.OUT)) {
      let itemPerEditModel = this.itemsSig().find(x => x.id === this.editModel()?.id);
      if (!itemPerEditModel?.transferredQuantity || itemPerEditModel.transferredQuantity < itemPerEditModel?.quantity)
        return true;
    }
    return false;
  });

  canFulfillOpenRequestedQuantitySig: Signal<boolean> = computed(() => {
    if (!this.isReserveOutDocument()) return false;
    if (this.inEditingSig()) return false;
    const doc = this.document();
    if (doc?.status === DocumentStatusType.CO || doc?.status === DocumentStatusType.RE) return false;
    return this.selectedItems().some(item => this.hasOpenRequestedQuantityGap(item));
  });

  statusChangeDisabled = computed(() => {
    let doc = this.document();
    let selectedItems = this.selectedItems();
    return selectedItems?.length != 1 || selectedItems[0]?.status === DocumentStatusType.RE || doc?.status === DocumentStatusType.RE || doc?.status === DocumentStatusType.CO;
  });

  // priceListsToLoad: string[] = [];
  storagePlacesToLoad: string[] = [];
  loadAllStoragePlaces = false;

  /**
   * This is used to add a new item at a specific index.
   * In save we need to know since then all DLIs need to be refreshed to get posText of previous and next items.
   */
  addDLIAtIndexModeSig = model<boolean>(false);

  private discountCheckTrigger = new Subject<DliEx>();
  /** Quantity-scale discounts must re-resolve even when percentages were already auto-applied. */
  private pendingForceDiscountReapply = false;
  private pendingStreamSavedItemsById = new Map<string, { item: DliEx; addedAt: number }>();
  private isRowEditSaveRunning = false;

  /** Toolbar ↑/↓: debounce API + refresh; accumulate `indexDelta` while `itemsSig` updates optimistically. */
  private readonly positionReorderDebounceMs = 1000;
  private positionReorderFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingPositionReorder: {
    documentId: string;
    itemId: string;
    /** Server position number at the start of the batch (before optimistic swaps). */
    positionNumberAtBatchStart: number;
    accumulatedDelta: number;
  } | null = null;

  //#region ProductPriceInfo Variables
  activeProductPriceInfos = model<ProductPriceInfoExt[]>([]);
  activeProductPriceInfoFilter: ProductPriceInfoInput;
  //#endregion

  isLineItemActionsDisabledSig = computed(() => {
    return this.itemsSig().length === 0 || this.loadingSig() || this.inEditingSig();
  });

  isDocumentLockedSig = computed(() => {
    const doc = this.document();
    return doc?.status === DocumentStatusType.RE
      || doc?.status === DocumentStatusType.CO
      || !!doc?.isLedgerLocked;
  });

  isLineItemActionsAndDocumentLockedSig = computed(() => {
    return this.isLineItemActionsDisabledSig() || this.isDocumentLockedSig();
  });

  override isReorderRowsActiveSig = computed(() => {
    return this.allowReorderRows && !this.inEditingSig() && !this.isDocumentLockedSig();
  });

  constructor(
    private dliService: DocumentLineItemService,
    private docService: DocumentService,
    private productPriceInfoService: ProductPriceInfoService,
    // private salesTaxCodeService: SalesTaxCodeService,
    private salesTaxCodeDetailService: SalesTaxCodeDetailService,
    private serverSettingsService: ServerSettingsService,
    private accessoryConfigProductLinkService: AccessoryConfigProductLinkService,
    private vendorPriceListAddressLinkService: VendorPriceListAddressLinkService,
    private productService: ProductService,
    private route: ActivatedRoute,
    public lineItemDataService: LineItemDataService,
    private sidebarService: SidebarService,
    private modalService: ModalService,
    private i18n: I18nService,
    private apollo: Apollo,
    private hotkeysService: HotkeysService,
    private taskPanelService: TaskPanelService,
    private storagePlaceService: StoragePlaceService,
    // private priceListEntryService: PriceListEntryService,
    private localeDatePipe: LocaleDatePipe,
  ) {
    super('DocumentLineItem', dliService);
    this.inlineEditMode = true;
    this.noInlineEditButton = true;
    this.addInDetail = false;
    this.noBaseEdit = true;
    this.noCopy = true;
    this.allowReorderRows = true;
    this.focusedColumnName.set('identifier');
    this.focusMode = FocusMode.ColumnName;
    this.crudOptions.enableStream = true;
    this.showCheckboxModeToggle.set(false);
    this.modelHasDecimalPlaces.set(true);
    this.noDisableOnEdit.set(true);

    effect(() => {
      if (this.selectedItems()) {
        this.refreshToolbarButtons();
      }
    });

    effect(() => {
      if (this.inEditingSig() !== undefined) {
        untracked(() => {
          this.disabledColumnChooserSig.set(this.inEditingSig());
          this.disabledPrintingSig.set(this.inEditingSig());
          this.disabledPopupMenuSig.set(this.inEditingSig());
          this.disabledColumnChooserSig.set(this.inEditingSig());
        })
      }
    });

    // Effect to handle document changes and update UI state
    effect(() => {
      const doc = this.document();
      untracked(async () => {
        if (doc) {
          this.lineItemDataService.setDocument(doc);
          this.id = doc.id;

          this.viewName.set(doc.documentDefinition?.identifier);

          // Handle document ID change for refresh
          if (this.currentlyLoadedDocumentId && this.currentlyLoadedDocumentId !== doc.id) {
            this.pendingStreamSavedItemsById.clear();
            await this.refresh();
          }
          this.currentlyLoadedDocumentId = doc.id;
          this.setToolbarMenu();
          this.cdRef.detectChanges();
        }
      });
    });

    effect(async () => {
      const isLocked = this.isDocumentLockedSig();
      if (isLocked) {
        // Update base class properties
        this.noAddNew = this.noBaseEdit = this.noDelete = this.noCopy = true;
        this.inlineEditMode = false;
      }
      else {
        this.noAddNew = this.noDelete = this.noCopy = false;
        // Respect the initial noBaseEdit value from Input when document is not locked
        this.noBaseEdit = this.initialNoBaseEdit ?? false;
        this.inlineEditMode = true;
      }
      // untracked(async () => {
      //   await this.refresh();
      // });
      this.cdRef.detectChanges();
    })

    effect(() => {
      let doc = this.lineItemDataService.doc();
      untracked(() => {
        if (this.document() !== doc)
          this.document.set(doc);
        if (!this.id)
          this.id = doc?.id;
      })
    })

    effect(() => {
      //this is important for statusbar to work
      let lastSelectedItem = this.lastSelectedItemSig();
      const selectedItems = this.selectedItems();
      untracked(() => {
        this.lineItemDataService.setDocumentLineItem(lastSelectedItem);
        this.lineItemDataService.setSelectedLineItems(selectedItems ?? []);
        if (lastSelectedItem) {
          this.selectedQuantitySig.set(lastSelectedItem.quantity);
          this.selectedProductSig.set(lastSelectedItem.product);
          this.selectedStoragePlaceIdSig.set(lastSelectedItem.storagePlaceId ?? lastSelectedItem.storagePlace?.id)
        }
        else
          this.resetDLIEditSignals();
      })
    })

    effect(() => {
      const toggled = this.sidebarService.toggledSig();
      setTimeout(() => {
        if (this.table()) {
          let width = this.getWidthOfTable() + 15;
          if (width > 0) {
            this.tableWidth = width;
            this.cdRef.detectChanges();
          }
        }
      }, 300);
    });

    effect(() => {
      let disabled = this.inEditingSig() || this.isDocumentLockedSig() || this.IsLoading;
      untracked(() => {
        this.actionBarDisabledSig.set(disabled);
      })
    });

    effect(() => {
      let product = this.lineItemDataService.product();
      if (product && this.storagePlaceDropdownComponent) {
        untracked(() => {
          this.storagePlaceDropdownComponent.setProductId(product.id);
        })
      }
    });

    //#region HK Add new Product
    let hkNewLineItem = new Hotkey('alt+1', (x, y) => {
      if (this.inEditingSig()) {
        this.onRowEditSave().then(() => {
          this.addNewItem();
        });
      }
      else {
        this.addNewItem();
      }
      return true;
    }, [AllowIn.Input, AllowIn.Textarea]);
    this.hotkeysService.add(hkNewLineItem);
    //#endregion

    //#region HK Add new Text
    this.taskPanelService.pauseGlobalShortcut();
    let hkNewText = new Hotkey('alt+t', (x, y) => {
      const anchor = this.selectedItems()?.[0] ?? this.lastSelectedItemSig();
      const run = () => this.addNewTextItem(anchor);
      if (this.inEditingSig()) {
        this.onRowEditSave().then(() => run());
      }
      else {
        run();
      }
      return true;
    }, [AllowIn.Input, AllowIn.Textarea]);
    this.hotkeysService.add(hkNewText);
    //#endregion

    //#region HK Price history
    let hkPriceHistory = new Hotkey('ctrl+h', () => {
      if (this.loadingSig() || this.selectedItems()?.length !== 1) {
        return true;
      }
      this.openPriceHistoryDrawer();
      return true;
    }, [AllowIn.Input, AllowIn.Textarea]);
    this.hotkeysService.add(hkPriceHistory);
    //#endregion

    this.destroyRef.onDestroy(() => {
      // this.hotkeysService.remove(hkSaveInLineEdit);
      this.hotkeysService.remove(hkNewLineItem);
      this.hotkeysService.remove(hkNewText);
      this.hotkeysService.remove(hkPriceHistory);
      this.taskPanelService.resumeGlobalShortcut();
      if (this.positionReorderFlushTimer != null) {
        clearTimeout(this.positionReorderFlushTimer);
        this.positionReorderFlushTimer = null;
      }
      this.pendingPositionReorder = null;
    });

    this.discountCheckTrigger.pipe(takeUntilDestroyed(this.destroyRef)).pipe(debounceTime(500)).subscribe(async (rowData) => {
      //if the row is deleted right after editing
      if (!this.editModel()?.product)
        return;

      rowData.discountPromise = (async () => {
        try {
          let searchModel = { ... this.editModel(), document: this.document() } as DocumentLineItem;
          const shouldApply = await this.shouldApplyAutomaticDiscounts(rowData);
          const percentagesUnset = isLineItemDiscountPercentagesUnset(searchModel);
          const forceReapply = this.pendingForceDiscountReapply;
          this.pendingForceDiscountReapply = false;
          if (shouldLookupAutomaticLineItemDiscounts({
            shouldApplyAutomaticDiscounts: shouldApply,
            percentagesUnset,
            forceReapply,
          })) {
            const lookupModel = lineItemForAutomaticDiscountLookup(searchModel, forceReapply);
            let discounts = await this.dliService.getApplicableDiscountsForDocumentLineItem(lookupModel);
            if (!shouldCommitAutomaticDiscountLookup({
              forceReapply,
              lookupResolved: isAutomaticDiscountLookupResolved(discounts),
            })) {
              return;
            }

            if (this.editModel() && (discounts.discountPercentage1 != this.editModel().discountPercentage || discounts.discountPercentage2 != this.editModel().discountPercentage2)) {
              this.editModel.update(x => {
                x.discountPercentage = discounts.discountPercentage1;
                x.discountPercentage2 = discounts.discountPercentage2;
                return x;
              });

              rowData.discountPercentage = discounts.discountPercentage1;
              rowData.discountPercentage2 = discounts.discountPercentage2;
              this.discountPercentageInput?.setValue(discounts.discountPercentage1);
              this.discountPercentage2Input?.setValue(discounts.discountPercentage2);
              CalculateDocumentLineItemPrices(rowData, this.document());
              rowData.dataChangedTrigger.next();
              this.cdRef.detectChanges();
            }
          }
        } catch (error) {
          console.error(error);
        }
      })();
    });

    let groupMode = this.baseServices.settingsService.getSetting(S_DLI_ROW_GROUP_MODE, undefined);
    if (groupMode?.length > 0)
      this.rowGroupMode.set(groupMode);
    else
      this.rowGroupMode.set(undefined);

    this.groupRowsBy.set("predecessorKey");

    effect(() => {
      let inEditing = this.inEditingSig();
      untracked(() => {
        if (!inEditing)
          this.addNewButtonTooltip.set(this.i18n.translate(_('document-line-item.create-new-line-item')))
        else
          this.addNewButtonTooltip.set(this.i18n.translate(_('document-line-item.save-and-create-new-line-item')))
      })
    });
  }



  setToolbarMenu = () => {
    this.actionBarLocationId = "document-line-item-list";
    this.actionBarStyleClass = 'py-0';

    let items: ActionBarItem[] = [
      {
        id: 'document-line-item-switch-direction',
        label: this.i18n.translate(_('document-line-item.switch-direction')),
        icon: 'fa-solid fa-arrows-up-down',
        disabledSig: this.isLineItemActionsAndDocumentLockedSig,
        dataTestId: 'action-bar-item-switch-direction',
        onClick: (event, context) => {
          (context.component as WidgetAddEditLineItemListComponent).switchDirection();
        },
      },
      {
        id: 'document-line-item-recalc-position-text',
        label: this.i18n.translate(_('document-line-item.recalc-position-text')),
        icon: 'fa-solid fa-arrow-down-1-9',
        disabledSig: this.isLineItemActionsAndDocumentLockedSig,
        dataTestId: 'action-bar-item-recalc-position-text',
        onClick: (event, context) => {
          (context.component as WidgetAddEditLineItemListComponent).recalcPositionTextAndRefresh();
        },
      },
      //add text position
      {
        id: 'document-line-item-add-text',
        label: this.i18n.translate(_('document-line-item.add-text')),
        icon: 'fa-solid fa-font',
        disabledSig: this.isDocumentLockedSig,
        dataTestId: 'action-bar-item-add-text',
        onClick: (event, context) => {
          (context.component as WidgetAddEditLineItemListComponent).addNewTextItem();
        },
      },
      {
        id: 'document-line-item-change-status',
        label: this.i18n.translate(_('document-line-item.change-status')),
        icon: 'fa-solid fa-pen',//-to-square',
        disabledSig: this.statusChangeDisabled,
        dataTestId: 'action-bar-item-edit-status',
        onClick: this.editStatus.bind(this)
      },
      {
        id: 'document-line-item-list-separator',
        separator: true
      },
      {
        id: 'document-line-item-group-by-predecessor',
        label: this.i18n.translate(_('document-line-item.group-by-predecessor')),
        icon: 'fa fa-object-group',
        dataTestId: 'action-bar-item-group-by-predecessor',
        onClick: this.toggleGroupMode.bind(this)
      }
    ];

    if (this.document()?.documentDefinition?.documentType === DocumentType.Order) {
      items.push({
        id: 'document-line-item-reset-allocations',
        label: this.i18n.translate(_('document-line-item.reset-allocations')),
        icon: 'fa-solid fa-rotate-left',
        disabledSig: this.isLineItemActionsAndDocumentLockedSig,
        dataTestId: 'action-bar-item-reset-allocations',
        onClick: this.resetAllAllocations.bind(this),
      });
    }

    //import via file
    items.push({
      id: 'document-line-item-import-via-file',
      label: this.i18n.translate(_('document-line-item.import-via-file')),
      icon: 'fa fa-file-import',
      dataTestId: 'action-bar-item-import-via-file',
      onClick: this.importViaFile.bind(this)
    });

    this.actionBarItemsSig.set([{
      id: 'document-line-item-list-actions',
      locationId: 'document-line-item-list',
      dataTestId: 'action-bar-item-actions-document-line-item-list',
      label: this.i18n.translate(_('common.actions')),
      //icon: 'fa fa-ellipsis-v',
      items: items
    }]);
    this.baseServices.navBuilderService.updateActionBarItems(this.actionBarItemsSig());
  }

  async editStatus() {
    let result = await lastValueFrom(this.modalService.fromComponent(WidgetEditDliStatusComponent, {
      closable: true,
      size: 'sm',
      title: this.i18n.translate(_("document.edit-status")),
      setLocalsSig: {
        documentLineItem: this.selectedItems()[0]
      }
    }));

    if (result) {
      await this.afterDocumentLineItemsMutated();//.then(() => {
      //   this.itemsChanged.emit(this.itemsSig());
      // });
    }
  }

  importViaFile() {
    this.modalService.fromComponent(WidgetImportDliFromFileComponent, {
      maximizable: true,
      closable: true,
      size: 'xl',
      title: this.i18n.translate(_('document-line-item.import-via-file')),
      verticalAlign: 'top',
      locals: {
        document: this.document()
      }
    }).subscribe(async (result: DocumentLineItemCsvImportResult | null) => {
      if (result) {
        this.notifyDocumentLineItemsChanged();
      }
      await this.refresh();
      await this.docService.refetchOnlyDocumentLineItemCausedChanges(this.document()?.id);
      if (result?.deferredCount > 0) {
        this.baseServices.notificationService.warning(
          this.i18n.translate(_('document-line-item.csv-import.reservation-deferred-summary'), { count: result.deferredCount }),
        );
      }
    })
  }

  async resetAllAllocations(): Promise<void> {
    const doc = this.document();
    if (!doc?.id || doc.documentDefinition?.documentType !== DocumentType.Order) {
      return;
    }
    if (this.isLineItemActionsAndDocumentLockedSig()) {
      return;
    }

    const confirmed = await lastValueFrom(this.modalService.showConfirmationDialog({
      title: this.i18n.translate(_('document-line-item.reset-allocations')),
      message: this.i18n.translate(_('document-line-item.reset-allocations-confirm')),
      btnYesText: this.i18n.translate(_('common.confirm')),
      btnNoText: this.i18n.translate(_('common.cancel')),
      size: 'xs',
    }));
    if (!confirmed) {
      return;
    }

    this.setLoading(true);
    try {
      const count = await this.allocatableLineItemService.resetDocumentAllocations(doc.id);
      if (count > 0) {
        this.baseServices.notificationService.success(
          this.i18n.translate(_('document-line-item.reset-allocations-success'), { count }),
        );
        await this.afterDocumentLineItemsMutated();
        await this.refresh(true);
      } else {
        this.baseServices.notificationService.info(
          this.i18n.translate(_('document-line-item.reset-allocations-none')),
        );
      }
    } catch (error) {
      //Error handled in interceptor
    } finally {
      this.setLoading(false);
    }
  }

  addNewTextItem(anchor?: DocumentLineItemDisplayClass | null) {
    const selected = anchor ?? this.selectedItems()?.[0] ?? this.lastSelectedItemSig();
    const defaults = { type: DocumentLineItemType.TEXT } as DocumentLineItem;
    if (selected?.type === DocumentLineItemType.SBOMH) {
      defaults.parentId = selected.id;
    } else if (selected?.type === DocumentLineItemType.SBOMI && selected.parentId) {
      defaults.parentId = selected.parentId;
    }
    const index = selected?.id
      ? this.itemsSig()?.findIndex((item) => item.id === selected.id)
      : -1;
    this.addNewItem(index >= 0 ? index : null, defaults);
  }

  addNewOptionalItem() {
    this.addNewItem(null, {
      isOptional: true
    } as DocumentLineItem);
  }

  override async customEnterEditInlineAfterSave() {
    await this.addNewItem();
  }

  async ngOnInit(): Promise<void> {
    // Store the initial value of noBaseEdit to preserve Input settings from parent component
    this.initialNoBaseEdit = this.noBaseEdit;

    if (this.loadDocumentTooSig()) {
      this.crudOptions.fragment = this.rowGroupMode() ? GET_DOCUMENTLINEITEMS_WITH_DOC_AND_PREDECESSOR : GET_DOCUMENTLINEITEMS_WITH_DOC;
    }
    else if (this.rowGroupMode())
      this.crudOptions.fragment = GET_DOCUMENTLINEITEMS_SINGLE_PREDECESSOR;

    this.itemsSig.subscribe(
      items => {

        items = items.filter(item => item.isStorable && isDocumentLineItemMissingStockBacking(item.quantity, item.lineItemQuantityStatus?.quantityBackedByStock, item.document?.documentDefinition?.stockAction ?? this.document()?.documentDefinition?.stockAction));
        items = items.map(item => ({ ...item, document: this.document() }));

        this.unstockedItemsSig.set(items)
      }
    );

    this.checkboxMode = true;

    this.onlyCheckboxSelection = true;

    this.noColFilter = true;
    this.noSearch = true;
    this.noSort = true;

    this.skipColsInFilter = 2;

    let dliDefaultDecimals = await this.serverSettingsService.getRoundDecimalPlacesLineItem();

    this.decimalInput = dliDefaultDecimals ?? this.document()?.decimalPlaces ?? 0.01;

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      let id = params.get("id");
      if (id && id !== this.id) {
        this.id = id;
      }
      // this.companyEditComponent.loadById(this.companyId);
      // this.cdRef.detectChanges();
    });

    super.ngOnInit();

    // this.loadFallBackFields();
  }

  override getHeader(outputHeader: HTMLElement, outputHeaderSpan: HTMLElement, outputHeaderDiv: HTMLElement, col: PrimeColumnDef): string {
    let fieldLowCase = col.field.toLowerCase();
    // if (fieldLowCase === 'nodocumentdiscount') {
    //   const headerDisplayValue = col?.customHeaderName ? col.customHeaderName : col?.header;
    //   return headerDisplayValue;
    // }
    if (fieldLowCase === "salestaxcode.identifier" || fieldLowCase.includes("account")) {
      outputHeader.classList.add('!text-right');
    }
    return super.getHeader(outputHeader, outputHeaderSpan, outputHeaderDiv, col);
  }

  private resolveDisplayDecimalPlaces(): number {
    return this.document()?.decimalPlaces
      ?? this.decimalInput
      ?? DEFAULT_DECIMAL_PRECISION;
  }

  protected override resolveRowDecimalPlaces(_rowData: DliEx): number {
    return this.resolveDisplayDecimalPlaces();
  }

  override getDisplayComponent(outputCell: HTMLElement, outputSpan: HTMLElement, col: PrimeColumnDef, rowData: DliEx, row: HTMLTableRowElement) {
    let rawValue = dot.get(rowData, col.field);
    let displayValue = this.getTypeStyledDisplayValue(col, rawValue, outputCell, outputSpan);
    const fieldLowCase = col.field.toLowerCase();
    const decimalPlaces = this.resolveRowDecimalPlaces(rowData);

    if (rowData.type == DocumentLineItemType.TEXT) {
      if (fieldLowCase !== 'description' && fieldLowCase !== 'addition' && fieldLowCase !== 'addition2' && fieldLowCase !== 'positionnumber' && fieldLowCase !== 'producttext')
        return;
    }

    if (fieldLowCase === 'discountpercentage' || fieldLowCase === 'discountpercentage2') {

      this.discountCellRefs.set(`${rowData.id}-${fieldLowCase}`, outputCell);

      displayValue = this.getTypeStyledDisplayValue(col, displayValue, outputCell, outputSpan);
      if (rowData.nonDiscountable) {
        displayValue = "0%";
        outputSpan.innerHTML = displayValue;
        (outputSpan as any).tooltip = this.i18n.translate(_('document-line-item.non-discountable'));
        outputCell.classList.add('line-through');
      }
      else if (typeof displayValue === 'number') {
        let changedData = xCalculateRoundPlaces(displayValue, decimalPlaces);
        displayValue = xDecimalPlacesAsString(changedData, decimalPlaces);

        if (displayValue?.length > 0)
          outputSpan.innerHTML = displayValue + '%';
      }

      // Subscribe to changes (only once per cell)
      rowData.detailToListChangeTrigger?.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        const cell = this.discountCellRefs.get(`${rowData.id}-${fieldLowCase}`);
        if (cell) {
          if (rowData.nonDiscountable) {
            cell.classList.add('line-through');
          } else {
            cell.classList.remove('line-through');
          }
        }
      });
    }
    if (fieldLowCase === "salestaxcode.identifier" || fieldLowCase.includes("account")) {
      outputSpan.innerHTML = displayValue;
      outputCell.classList.add('!text-right');
    } else if (
      fieldLowCase === "quantity"
      || fieldLowCase === "openquantity"
      || fieldLowCase === "requestedquantity"
      || fieldLowCase === "transferredquantity"
    ) {
      if (rawValue != null) {
        const quantityDecimalPlaces = this.resolveQuantityDecimalPlaces(rowData);
        if (fieldLowCase === 'quantity') {
          displayValue = formatQuantityWithContractFactor(
            rawValue,
            quantityDecimalPlaces,
            resolveContractFactorRaw(rowData),
          );
        } else {
          displayValue = xDecimalPlacesAsString(rawValue, quantityDecimalPlaces);
        }
        outputSpan.innerHTML = displayValue;

        if (fieldLowCase === 'quantity') {
          rowData.detailToListChangeTrigger?.pipe(
            takeUntilDestroyed(this.destroyRef),
          ).subscribe(() => {
            outputSpan.innerHTML = formatQuantityWithContractFactor(
              dot.get(rowData, col.field),
              quantityDecimalPlaces,
              resolveContractFactorRaw(rowData),
            );
          });
        }
      }
    }
    else {
      this.getCustomColumnDisplayComponent(outputSpan, outputCell, col, col.metaData, rowData);
      // a filled span makes the base render skip the cell
      this.showNestedTranslation(outputSpan, col, rowData);
      super.getDisplayComponent(outputCell, outputSpan, col, rowData, row);
    }
    //additional styling for product identifier
    if (fieldLowCase === 'productidentifier') {
      outputCell.classList.add('font-bold');
    }

    if (rowData.isOptional) {
      if (fieldLowCase === 'itemprice' || fieldLowCase === 'discountedlineprice' || fieldLowCase === 'discountedlinepricewithtax') {
        outputSpan.classList.add('text-slate-500');
      }
    }
  }

  /**
   * Nested columns such as `product.material.description` show the line item language when their parent
   * object was loaded with translations; an empty translated field keeps the base text.
   */
  private showNestedTranslation(outputSpan: HTMLElement, col: PrimeColumnDef, rowData: DliEx): void {
    const lastDot = col.field.lastIndexOf('.');
    if (lastDot < 0 || outputSpan.innerHTML) {
      return;
    }

    const language = this.dliService.lineItemLanguage(this.document());
    if (!language) {
      return;
    }

    const parent = dot.get(rowData, col.field.substring(0, lastDot)) as { translations?: Array<Record<string, unknown>> } | null;
    const translated = parent?.translations
      ?.find(each => each.languageCode === language)?.[col.field.substring(lastDot + 1)];
    if (typeof translated === 'string' && translated) {
      outputSpan.textContent = translated;
    }
  }

  async restockUnstockedItems() {
    // this.modalService.fromComponent(WidgetDliManualQuantityAndStoragePlaceChooserComponent, {
    //   locals: {
    //     documentLineItems: simpleDeepClone(this.unstockedItemsSig())
    //   },
    //   title: this.baseServices.i18nService.translate(_("common.storage-options")),
    //   closable: true,
    //   size: "lg",
    //   maximizable: true,
    // }).subscribe(async (x) => {
    //   if (x?.documentLineItems) {
    //     await this.endEditAndRefresh();
    //   }
    // });
    let result = await this.createWarehouseDialog(this.unstockedItemsSig());
    if (result.documentLineItems) {
      await this.onRowEditCancel(this.editFieldsArray[0]);
      await this.afterDocumentLineItemsMutated();
    }
  }

  override getCustomColumnArgs(col: ColumnDefinitionEx, _rowData?: DliEx): {} {
    if (col.field === 'statusTemplate') {
      return {
        document: this.document(),
        singleRestock: async () => await this.onSingleRestock()
      }
    }
    if (col.field === 'itemPrice') {
      return {
        decimalPlaces: this.resolveDisplayDecimalPlaces(),
      };
    }
  }

  async onSingleRestock() {
    await this.refresh();
    await this.afterDocumentLineItemsMutated();
  }

  override getSearchInputForQuery(): DocumentLineItemSearchInput {
    let si = super.getSearchInputForQuery() as DocumentLineItemSearchInput;
    if (this.tableAscendingSig() !== null && this.tableAscendingSig() !== undefined) {
      // second sort order by createdAt is necessary for addNewAtIndex because for a short time there are 2 items with same posNumber
      // only like this the new one stays after the old one on adding
      if (this.tableAscendingSig() === true)
        si.sort = [{ field: "positionNumber", order: SortOrder.ASC }, { field: "createdAt", order: SortOrder.ASC }];
      else
        si.sort = [{ field: "positionNumber", order: SortOrder.DESC }, { field: "createdAt", order: SortOrder.DESC }];
    }
    si.excludeTypes = [DocumentLineItemType.ADVANCE];
    si.documentId = this.id;
    return si;
  }

  async setTaxLineOnDli(dli: DocumentLineItemDisplayClass, ignoreProductDefaultTaxCode?: boolean) {
    let taxLine: TaxLine = await this.dliService.getTaxLineData(this.document(), dli);
    dli.taxPercentage = taxLine?.taxRate;
    dli.salesTaxCodeId = taxLine?.salesTaxCodeId;
    dli.account = taxLine?.account;
    dli.salesTaxCode = taxLine?.salesTaxCode;
    this.editModel.update(dli => {
      dli.salesTaxCode = taxLine?.salesTaxCode;
      dli.taxPercentage = taxLine?.taxRate;
      dli.account = taxLine?.account;
      return dli;
    });
  }

  getWidthOfTable() {
    if (this.table()?.el?.nativeElement?.offsetWidth > 0)
      return this.table()?.el?.nativeElement?.offsetWidth - 30;
    return null;
  }

  ngAfterViewInit() {
    this.pDataTable = this.table().el.nativeElement.querySelector('.p-datatable-wrapper');
    if (this.pDataTable) {
      this.pDataTable.addEventListener('scroll', this.onTableScroll.bind(this));
    }

    super.ngAfterViewInit();
    this.listWrapper.table.selection = [];
    this.listWrapper.table.selectionMode = null;

    this.setToolbarMenu();
  }

  override getContextMenuItems() {
    let items = super.getContextMenuItems();

    items.push({
      id: 'base-list-open',
      automationId: 'data-testid',
      label: this.i18n.translate(_("common.base-list-open")),
      tabindex: '2',
      items: [
        {
          label: this.baseServices.i18nService.translate(_("document-line-item.open-product")),
          // id: "open-product-in-new-tab",
          icon: "fa-solid fa-cube",
          automationId: 'data-testid',
          tabindex: '1',
          command: (e) => this.openInNewTab(EditLinks.PRODUCT + (this.contextClickItem$.value?.productId ?? this.contextClickItem$.value?.product?.id))
        }
      ]
    });

    this.warehouseContextMenuItem = {
      id: 'line-item-warehouse-stock',
      label: this.baseServices.i18nService.translate(_('product.showWarehouseStockButton')),
      icon: 'fa-solid fa-warehouse',
      automationId: 'data-testid',
      tabindex: '3',
      disabled: true,
      command: () => this.openWarehouseSidebarForLineItem(this.contextClickItem$.value),
    };
    items.push(this.warehouseContextMenuItem);

    return items;
  }

  override onContextMenuSelect(event: TableContextMenuSelectEvent) {
    super.onContextMenuSelect(event);
    if (this.warehouseContextMenuItem) {
      this.warehouseContextMenuItem.disabled = !this.contextClickItem$.value?.isStorable;
    }
  }
  protected async setColumnConfig(colConfig: ColumnConfigEx, table?: Table) {

    if (!colConfig.columns.some(column => column.field === 'statusTemplate')) {
      let customCol: ColumnDefinitionEx = {
        id: uuidv4(),
        dataType: 'custom',
        field: 'statusTemplate',
        skipEditInit: true,
        customHeaderName: this.i18n.translate(_('document-line-item.status')),
        header: this.i18n.translate(_('document-line-item.status')),
        width: 50,
        visible: true,
        deletable: false,
        sortable: false
      }

      colConfig.columns.unshift(customCol);
      try {
        await this.baseServices.columnService.saveItem(colConfig, true);
      }
      catch (e) {
        console.log("error when saving column config", e);
      }
    }

    const customColumnData = getCustomColumnLoadSpec(colConfig.columns);

    // this.priceListsToLoad = customColumnData.priceListIds;
    this.storagePlacesToLoad = customColumnData.storagePlaceIds;
    this.loadAllStoragePlaces = customColumnData.loadAllStoragePlaces;

    // exactly one owner - the conversion dropdown keeps single-instance state and two of them fight
    const unitPickerField = colConfig.columns.some(each => each.field == "unitShortForm")
      ? "unitShortForm"
      : "unitIdentifier";

    for (let cd of colConfig.columns) {

      if (cd.field == "productText") {
        if (this.document()?.documentDefinition?.documentDirection == DocumentDirection.IN)
          cd.customHeaderName = this.i18n.translate(_('document.productText-in'));
      }

      if (cd.field === 'quantity' || cd.field === 'openQuantity' || cd.field === 'requestedQuantity' || cd.field === 'transferredQuantity') {
        this.assignQuantityHeaderTemplate(cd);
      }

      if (cd.field == "revenue") {
        // this is set automatically - not manually 
        cd.canEdit = false;
      }

      if (cd.field == 'statusTemplate') {
        cd.skipEditInit = true;
      }

      // it has to be product identifier to get the dropdown for choosing the product
      if (cd.field == "productIdentifier") {
        cd.getEditComponent = () => DliProductDropDownComponent;
        cd.editComponentCreated = async (c: DliProductDropDownComponent, rowData: DocumentLineItem, colDef: ColumnDefinitionEx) => {

          c.noVariantProducts = true; //for now hardcoded until we have a chooser for variants
          c.showInfoPopup.set(true);

          const doc = this.document();
          const baseAddressId = doc?.addressId ?? doc?.address?.id ?? undefined;
          const addressIdForUnitConversion = doc.documentDefinition?.documentDirection == DocumentDirection.IN ? baseAddressId : undefined;
          c.addressId = addressIdForUnitConversion;
          c.documentId = doc?.id ?? undefined;
          c.excludeTradingSetProducts = isContractDocument(doc);

          if (this.newId === null || this.newId === undefined) {
            c.disabled.set(true);
          }
          else {
            c.autoOpenOnNew.set(true);
          }

          if (rowData.product)
            c.setModel(rowData.product);
          else if (rowData.productId) {
            await c.loadItemByIdAndSetSelected(rowData.productId);
            this.cdRef.detectChanges();
          }
          c.showWarehouseStockButton = true;
          subscribeUntilComponentDestroyed(c, c.value, async (x: Product) => {
            const currentProductId = rowData.productId ?? rowData.product?.id;
            if (!x?.id && !currentProductId)
              return;

            const conversionLinks = c.productUnitConversionLinks ?? [];
            if (conversionLinks.length > 0) {
              const link = conversionLinks[0];
              const product =
                link.product
                ?? c.lastSearchProducts?.find(p => p.id === link.productId)
                ?? (x?.id === link.productId ? x : null);
              if (product) {
                const resumeUnitConversionValueSync = this.suspendUnitConversionValueSync();
                try {
                  await this.onProductChange(product, rowData, false, true);
                  this.applyProductUnitConversionLinkFromScan(rowData, link, product, conversionLinks);
                  c.productUnitConversionLinks = [];
                } finally {
                  setTimeout(() => resumeUnitConversionValueSync(), 0);
                }
              }
            } else {
              if (x?.id && currentProductId === x.id)
                return;
              await this.onProductChange(x, rowData, false);
            }

            this.productPriceInfoComponent?.lineItem.set(rowData);
            await this.loadProductPriceInfosIfNeeded(rowData, this.document());
            if ((this.newId == rowData.id) && (rowData.productId || rowData.product?.id))
              this.productPriceInfoComponent?.preSelectByCustomerOrListPrice();
            this.discountCheckTrigger.next(rowData);
            // not necessary to set to productPriceInfoComponent since its connected to signal anyways
            if (rowData == null) {
              this.productPriceInfoComponent?.clear();
            }
          });
        }
      }

      if (cd.field == "positionNumber") {
        cd.sortable = true;
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: any, colDef: ColumnDefinitionEx) => {

          c.setValue(rowData.positionNumber);
          c.minFractionDigits.set(0);
          c.upDownButtons.set(false);
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.positionNumber = x;
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
              this.editModel.update(dli => { dli.positionNumber = x; return dli; });
            }

          })
          c.setDisabledState(true);
        }
      }

      if (cd.field == "positionText") {
        cd.sortable = true;
        cd.outputComponent = DocumentLineItemPositionTextComponent;
      }

      if (cd.field == "storagePlace.identifier") {
        cd.getEditComponent = () => StoragePlaceDropdownComponent;
        cd.editComponentCreated = (c: StoragePlaceDropdownComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {

          this.storagePlaceDropdownComponent = c;

          c.withQuantity = true;
          //c.setProductId(rowData.product?.Id);
          let docDef = this.document().documentDefinition;
          if ((docDef.stockAction == StockAction.OUT || docDef.stockAction == StockAction.IN) && (this.newId === null || this.newId === undefined)) {
            c.disabled.set(true);
          }
          if (this.newId) {

            // #region new storagePlace defaults - Determine new storage place by hierarchy

            const { storagePlaceId: rowStoragePlaceId, storagePlace: rowStoragePlace } = rowData;
            const { storagePlace: docStoragePlace, storagePlaceId: docStoragePlaceId, documentDefinition, address } = this.document() || {};

            const storagePlaceId =
              rowStoragePlaceId ??
              rowStoragePlace?.id ??
              docStoragePlace?.id ??
              docStoragePlaceId

            rowData.storagePlaceId = storagePlaceId

            this.selectedStoragePlaceIdSig.set(storagePlaceId);
            this.lineItemDataService.setDocumentLineItem(rowData); //triggers the stock refresh
          }

          if (rowData.storagePlaceId)
            c.loadItemByIdAndSetSelected(rowData.storagePlaceId);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {

            if (rowData.storagePlace) {
              if (c.selectedId() != rowData.storagePlace.id) {
                c.setModel(rowData.storagePlace);
                this.selectedStoragePlaceIdSig.set(rowData.storagePlace.id);
              }
            }
            else if (rowData.storagePlaceId) {
              if (c.selectedId() != rowData.storagePlaceId) {
                c.loadItemByIdAndSetSelected(rowData.storagePlaceId);
                this.selectedStoragePlaceIdSig.set(rowData.storagePlaceId);
              }
            }
            else {
              c.setModel(null);
              this.selectedStoragePlaceIdSig.set(null);
            }
          });

          subscribeUntilComponentDestroyed(c, c.value, (x: StoragePlace) => {
            if (rowData.storagePlace?.id !== x?.id) {
              rowData.storagePlaceId = x?.id;
              rowData.storagePlace = x;
              this.selectedStoragePlaceIdSig.set(x?.id);

              this.lineItemDataService.setDocumentLineItem(rowData);

              if (this.editModel()) {
                this.editModel.update((dli) => { dli.storagePlaceId = x?.id; dli.storagePlace = x; return dli; });
              }

              rowData.dataChangedTrigger.next();
              this.cdRef.markForCheck();
            }
          });

          c.destroyRef.onDestroy(() => {
            this.storagePlaceDropdownComponent = null;
          });
        }
      }

      if (cd.field == "itemPrice") {
        cd.outputComponent = ItemPriceColumnComponent;
        cd.getEditComponent = () => ProductPriceInfoComponent;
        cd.customChangesEmitterName = 'selectedPriceInfoChange';
        cd.editComponentCreated = async (c: ProductPriceInfoComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {

          if (!rowData)
            return;

          this.itemPriceInput.set(c);

          c.productPriceInfos = this.activeProductPriceInfos; // necessary to connect the signals

          c.matchingQuantity = true;
          c.doc.set(this.document());
          c.lineItem.set(rowData);

          // Must be set before loadProductPriceInfosIfNeeded — that method uses this reference, not the local `c`.
          this.productPriceInfoComponent = c;

          if (rowData.productId || rowData.product?.id)
            await this.loadProductPriceInfosIfNeeded(rowData, this.document());
          if (rowData.type === DocumentLineItemType.SBOMH && rowData.isCalculatedItemPrice == null) {
            const infos = this.activeProductPriceInfos() ?? [];
            const hasListOrCustomer = infos.some(
              (x) => x.sourceType === PriceSourceType.PRICELIST || x.sourceType === PriceSourceType.CUSTOMERPRICE,
            );
            rowData.isCalculatedItemPrice = !hasListOrCustomer;
          }
          c.disabled.set(this.itemPriceControlDisabled(rowData));
          // c.ngModel = rowData.itemPrice;

          // 0 is a saved price. Re-edit must not replace it with a list or customer price.
          if (this.shouldKeepSavedItemPrice(rowData))
            c.writeValue(rowData.itemPrice ?? null);
          else
            c.preSelectByCustomerOrListPrice();

          c.decimalPlacesInputSettings.set(rowData?.decimalPlaces ?? this.document()?.decimalPlaces);

          c.selectedPriceInfoChange?.subscribe(x => {

            if (!x) {
              x = {
                price: null,
                priceIncludesTax: true,
                sourceType: PriceSourceType.MANUALPRICE,
              };
            }

            const switchedToCustomerPrice =
              x.sourceType === PriceSourceType.CUSTOMERPRICE &&
              rowData.priceSource !== PriceSourceType.CUSTOMERPRICE;
            const isPurchase =
              this.document()?.documentDefinition?.documentDirection === DocumentDirection.IN;

            if (rowData.priceSource !== x.sourceType)
              rowData.priceSource = x.sourceType;

            // Sales: drop group/auto discounts when switching to customer price.
            // Purchase: CUSTOMERPRICE is the vendor price list — keep/apply Einkaufskonditionen instead.
            if (switchedToCustomerPrice && !isPurchase) {
              clearLineItemDiscountPercentages(rowData);
            } else if (switchedToCustomerPrice && isPurchase) {
              this.discountCheckTrigger.next(rowData);
            }

            // SBOMH: list/customer price fixes the header (split to SBOMI); must not stay in rollup mode (true).
            if (rowData.type === DocumentLineItemType.SBOMH) {
              if (x.sourceType === PriceSourceType.PRICELIST || x.sourceType === PriceSourceType.CUSTOMERPRICE) {
                rowData.isCalculatedItemPrice = false;
              }
              c.disabled.set(this.itemPriceControlDisabled(rowData));
            }

            let usedPrice = x.unitConversionPrice ?? x.price;

            if ((rowData?.itemPrice !== usedPrice)) {
              rowData.itemPrice = usedPrice;
              rowData.document = this.documentWithoutLineItems;
              rowData.priceIncludesTax = x.priceIncludesTax;
              CalculateDocumentLineItemPrices(rowData, this.document());
              rowData.dataChangedTrigger.next();
            } else if (switchedToCustomerPrice) {
              rowData.document = this.documentWithoutLineItems;
              CalculateDocumentLineItemPrices(rowData, this.document());
              rowData.dataChangedTrigger.next();
            }

            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);

              this.editModel.update(dli => {
                dli.itemPrice = usedPrice;
                dli.priceSource = x.sourceType;
                dli.priceListBulkQuantity = x.quantity;
                if (switchedToCustomerPrice && !isPurchase) {
                  dli.discountPercentage = null;
                  dli.discountPercentage2 = null;
                }
                if (
                  rowData.type === DocumentLineItemType.SBOMH &&
                  (x.sourceType === PriceSourceType.PRICELIST || x.sourceType === PriceSourceType.CUSTOMERPRICE)
                ) {
                  dli.isCalculatedItemPrice = false;
                }
                return dli;
              });
            }

            this.lineItemDataService.setProductPriceInfo(x);

            this.cdRef.markForCheck();
            this.triggerChangeDetection$.next(true);
          });
          c.isDestroyed$.pipe(takeUntilDestroyed(this.destroyRef), skip(1)).subscribe(x => {
            this.activeProductPriceInfoFilter = null;
            this.activeProductPriceInfos.set(null);
            this.productPriceInfoComponent = null;
          });
        }
      }

      if (cd.field == "discountedLinePrice") {
        cd.getEditComponent = () => DLILineSumInputComponent;
        cd.editComponentCreated = (c: DLILineSumInputComponent, rowData: any, colDef: ColumnDefinitionEx) => {

          c.setValue(rowData?.discountedLinePrice);
          c.dli = rowData;
          c.decimalPlaces = rowData.decimalPlaces ?? this.document()?.decimalPlaces;
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.discountedLinePrice = x;
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
            // this.cdRef.detectChanges();
          })
          c?.itemPriceChange?.subscribe(x => {
            rowData.itemPrice = x;
            // rowData.discountedLinePrice = x * rowData.quantity;
            this.productPriceInfoComponent.autoSelectPriceSource(x as number, rowData.quantity);

            this.productPriceInfoComponent.cdRef.detectChanges();
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
            CalculateDocumentLineItemPrices(rowData, this.document());
            this.cdRef.detectChanges();
            this.triggerChangeDetection$.next(true);
          })
        }
      }

      if (cd.field == "isStorable") {
        cd.getEditComponent = () => PhxCheckboxComponent;
        cd.editComponentCreated = (c: PhxCheckboxComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          this.isStorableInput = c;
          c.ngModel.set(rowData.isStorable);
          if (this.newId !== rowData.id) {
            c.disabled.set(true);
            c.innerTooltip.set(this.i18n.translate(_('document-line-item.is-storable-change-not-allowed')));
          }
          else {
            c.disabled.set(false);
            c.innerTooltip.set(null);
          }
          c.inlineChange.subscribe(x => {
            rowData.isStorable = x;
            this.editModel.update(dli => { dli.isStorable = x; return dli; });
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
            rowData.detailToListChangeTrigger?.next();
          });
        }
      }

      if (cd.field == "quantityLocked") {
        cd.getEditComponent = () => PhxCheckboxComponent;
        cd.editComponentCreated = (c: PhxCheckboxComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.ngModel.set(rowData.quantityLocked);
          c.inlineChange.subscribe(x => {
            rowData.quantityLocked = x;
            this.editModel.update(dli => { dli.quantityLocked = x; return dli; });
            this.storageQuantityNumberInput?.setDisabledState(!!x);
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
          });
          c.setDisabledState(false);
        }
      }

      if (cd.field == "quantity") {
        cd.getEditComponent = () => StorageQuantityNumberInputComponent;
        cd.editComponentCreated = (c: StorageQuantityNumberInputComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            this.selectedQuantitySig.set(rowData.quantity);
          });

          subscribeUntilComponentDestroyed(c, c.focus, () => {
            let activeUnitConversionsWithSourceUnit = this.activeUnitConversions()?.some(item => item.sourceUnit !== null);
            if (this.unitConversionDropDownComponent && activeUnitConversionsWithSourceUnit)
              this.unitConversionDropDownComponent.phxSelectComponent.show();
          });

          // c.minimumNumber = 0;
          // c.model = rowData.quantity || 1;
          // now safe for number 0 - important otherwise can break
          this.applyQuantityDecimalPlacesToInput(c, rowData);
          c.upDownButtons.set(false);
          c.model = this.selectedQuantitySig;
          c.storagePlaceIdSig = this.selectedStoragePlaceIdSig;
          c.productSig = this.selectedProductSig;
          this.storageQuantityNumberInput = c;
          this.updateUnitBasedQuantityNumberInput(rowData);
          let initializedWithoutNewQuantity: boolean = false;

          this.storageQuantityNumberInput?.setDisabledState(!!rowData.quantityLocked);

          subscribeUntilComponentDestroyed(c, c.valueChange, async x => {

            if (this.productPriceInfoComponent?.quantity && this.productPriceInfoComponent?.quantity() != x) {
              const preserveSavedPrice = !initializedWithoutNewQuantity && this.isPersistedDocumentLineItem(rowData);
              if (preserveSavedPrice)
                this.productPriceInfoComponent.quantity.set(x);
              else
                await this.updateProductPriceInfoQuantity(rowData, x);
            }
            if (initializedWithoutNewQuantity)
              await this.updateQuantityBasedOnAllowFractions(rowData, x);
            initializedWithoutNewQuantity = true;

            rowData.quantity = x;
            let quantityIsNumber = typeof rowData.quantity === 'number';

            if (quantityIsNumber)
              CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.unitConversionDropDownComponent)
              this.unitConversionDropDownComponent.documentLineItem.set(rowData);
            let selectedUnitConversion = { ...this.unitConversionDropDownComponent?.value(), quantity: x } as ConversionResult;
            if (this.unitConversionDropDownComponent && quantityIsNumber)
              this.unitConversionDropDownComponent.checkAndRecalculateIfQuantityChanged(selectedUnitConversion, true);

            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
              // this.editModel.set({ ...rowData }); //should not be need because of reference
            }
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();

          });
          c.destroy$.pipe(takeUntilDestroyed(this.destroyRef), skip(1)).subscribe(x => {
            this.storageQuantityNumberInput = null;
          });
        };
        // Bind the method to the component instance
        cd.editComponentCreated = cd.editComponentCreated.bind(this);
      }

      if (cd.field == "requestedQuantity") {
        cd.getEditComponent = () => StorageQuantityNumberInputComponent;
        cd.editComponentCreated = (c: StorageQuantityNumberInputComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {

          // Editable for storable and non-storable product lines; text lines have no demand quantity.
          const syncRequestedQuantityDisabled = () => {
            c.setDisabledState(rowData.type === DocumentLineItemType.TEXT);
          };

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            c.model.set(rowData.requestedQuantity ?? null);
            syncRequestedQuantityDisabled();
          });

          rowData.dataChangedTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            syncRequestedQuantityDisabled();
          });

          c.showStockStatus.set(false);
          c.upDownButtons.set(false);
          this.applyQuantityDecimalPlacesToInput(c, rowData);
          c.model.set(rowData.requestedQuantity ?? null);
          syncRequestedQuantityDisabled();
          if (rowData.unit?.quantityStep) {
            c.quantityStep.set(rowData.unit.quantityStep);
          }

          subscribeUntilComponentDestroyed(c, c.valueChange, async x => {
            rowData.requestedQuantity = x;

            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
              this.editModel.update(dli => {
                dli.requestedQuantity = x;
                return dli;
              });
            }
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();
          });
        };
        cd.editComponentCreated = cd.editComponentCreated.bind(this);
      }

      if (cd.field == "discountPercentage") {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          this.discountPercentageInput = c;
          c.setValue(rowData.discountPercentage);
          rowData.decimalPlaces = rowData.decimalPlaces ?? this.document()?.decimalPlaces;
          c.upDownButtons.set(false);
          //like this its directly connected to the signal
          c.disabled.set(rowData.nonDiscountable);
          c.minFractionDigits.set(2);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            c.disabled.set(rowData.nonDiscountable);
          });

          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.discountPercentage = x;
            CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {

              this.editModel.update(dli => {
                dli.discountPercentage = x;
                return dli;
              })
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
          })
          c.destroy$.pipe(takeUntilDestroyed(this.destroyRef), skip(1)).subscribe(x => {
            this.discountPercentageInput = null;
          });
        }
      }

      if (cd.field == "discountPercentage2") {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          this.discountPercentage2Input = c;
          c.setValue(rowData.discountPercentage2);
          rowData.decimalPlaces = rowData.decimalPlaces ?? this.document()?.decimalPlaces;
          c.upDownButtons.set(false);
          //like this its directly connected to the signal
          c.disabled.set(rowData.nonDiscountable);
          c.minFractionDigits.set(2);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            c.disabled.set(rowData.nonDiscountable);
          });

          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.discountPercentage2 = x;
            CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.editModel.update(dli => {
                dli.discountPercentage2 = x;
                return dli;
              })
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
          })
          c.destroy$.pipe(takeUntilDestroyed(this.destroyRef), skip(1)).subscribe(x => {
            this.discountPercentage2Input = null;
          });
        }
      }

      if (cd.field == "contractFactor") {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass, _colDef: ColumnDefinitionEx) => {
          if (!shouldShowContractLineItemFields(this.document(), rowData)) {
            return;
          }
          c.upDownButtons.set(false);
          c.setValue(rowData.contractDocumentLineItem?.contractFactor ?? null);
          c.minFractionDigits.set(0);
          c.maxFractionDigits.set(0);
          c.minValue.set(1);

          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((x) => {
            applyContractFactorToDli(rowData, x);
            CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.editModel.update((dli) => {
                applyContractFactorToDli(dli, x);
                return dli;
              });
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
            rowData.dataChangedTrigger?.next();
            this.cdRef.markForCheck();
            this.triggerChangeDetection$.next(true);
          });
        };
      }

      // free text here would create a Unit on save
      if ((cd.field == "unitIdentifier" || cd.field == "unitShortForm") && cd.field != unitPickerField) {
        cd.skipEditInit = true;
      }

      if (cd.field == unitPickerField) {
        cd.getEditComponent = () => DliUnitConversionDropDownComponent;
        cd.editComponentCreated = (c: DliUnitConversionDropDownComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.disabled.set(false);
          if (!rowData.document)
            rowData.document = this.document();
          c.documentLineItem.set(rowData);
          c.documentDirection.set(this.document()?.documentDefinition?.documentDirection);
          // direct signal binding to use it in detail component
          if (c.documentLineItem().id === this.editModel().id) {
            c.origQuantity.set(this.activeUnitConversionValue()?.quantity ?? rowData.quantity ?? 1);
            c.origUnitId.set(this.activeUnitConversionValue()?.unit?.id ?? null);
            c.items = this.activeUnitConversions;
            c.value = this.activeUnitConversionValue;
            this.cachedLastUnitConversion.set({ ...this.activeUnitConversionValue() });
          }
          this.unitConversionDropDownComponent = c;
          let initializedWithoutNewQuantity: boolean = false;

          subscribeUntilComponentDestroyed(c, c.value, async (x: ConversionResult) => {

            if (this.unitConversionValueSyncSuspended > 0) {
              return;
            }

            let originUnitId = c.origUnitId();

            let productId = this.editModel()?.product?.id ?? this.editModel()?.productId;

            if (!x && this.cachedLastUnitConversion()?.unit?.id && productId) {
              this.activeUnitConversionValue.set(this.cachedLastUnitConversion());
              c.value.set(this.cachedLastUnitConversion());
              this.baseServices.notificationService.warning(this.i18n.translate(_('document-line-item.product-cannot-be-saved-without-unit')));
              return;
            }

            this.cachedLastUnitConversion.set({ ...x });

            //TODO: improve as of now the formly component writes to unit directly thats why value is unit then
            if (c.documentLineItem()?.id !== this.editModel()?.id)
              return;

            if (x?.unit?.id === originUnitId && initializedWithoutNewQuantity) {
              if (x.quantity !== c.origQuantity()) {
                rowData.quantity = x?.quantity ?? null;
                this.editModel.update(dli => {
                  dli.quantity = x?.quantity ?? null;
                  return dli;
                });
                this.selectedQuantitySig.set(x?.quantity ?? null);
                if (x?.unit?.quantityStep)
                  this.storageQuantityNumberInput.setQuantityByQuantityStep(x?.unit?.quantityStep);
                else
                  this.storageQuantityNumberInput.model.set(x?.quantity ?? null);
              }
              return;
            }

            const unitShortForm = x?.unit
              ? await this.dliService.unitShortFormForLine(x.unit, this.dliService.lineItemLanguage(this.document()))
              : null;

            // rowData.unitId = x?.unit?.id ?? null;
            rowData.unitIdentifier = x?.unit?.identifier ?? null;
            rowData.unitShortForm = unitShortForm;
            rowData.unit = x?.unit as Unit ?? null;
            if (initializedWithoutNewQuantity)
              rowData.quantity = x?.quantity ?? null;
            rowData.quantityToBaseUnitFactor = x?.conversionRate ?? null;

            if (!rowData.baseUnitId && !rowData.baseUnit?.id)
              rowData.baseUnit = x?.unit as Unit;

            // delete rowData.openQuantity; //TODO: check how we handle this - is only recalculated on save
            this.editModel.update(dli => {
              // dli.unitId = x?.unit?.id ?? null;
              dli.unitIdentifier = x?.unit?.identifier ?? null;
              dli.unitShortForm = unitShortForm;
              dli.unit = x?.unit as Unit ?? null;
              if (initializedWithoutNewQuantity)
                dli.quantity = x?.quantity ?? null;
              dli.quantityToBaseUnitFactor = x?.conversionRate ?? null;
              if (!rowData.baseUnitId && !rowData.baseUnit?.id)
                dli.baseUnit = x?.unit as Unit;
              return dli;
            });
            this.lineItemDataService.setDocumentLineItem(this.editModel());
            if (initializedWithoutNewQuantity)
              this.selectedQuantitySig.set(x?.quantity ?? null);
            if (x?.unit?.quantityStep && this.storageQuantityNumberInput.quantityStep() === x?.unit?.quantityStep)
              this.storageQuantityNumberInput.setQuantityByQuantityStep(x?.unit?.quantityStep); //without this it doesnt work since in storageQuantityNumberInput nothing is changed
            this.updateUnitBasedQuantityNumberInput(rowData);

            // Opening a saved line emits the current unit once. That must not pull a new price.
            const preserveSavedPrice = !initializedWithoutNewQuantity && this.isPersistedDocumentLineItem(rowData);
            if (preserveSavedPrice) {
              if (x?.quantity != null)
                this.productPriceInfoComponent?.quantity.set(x.quantity);
            } else if (x?.unit?.id !== originUnitId) {
              this.refreshProductPriceInfosPerConversionRate(rowData);
              if (rowData.priceSource !== PriceSourceType.MANUALPRICE)
                this.productPriceInfoComponent.preSelectByCustomerOrListPrice();
            }
            if (!preserveSavedPrice && (x?.quantity !== this.productPriceInfoComponent?.quantity() || (x?.unit?.id !== originUnitId && x?.quantity === this.productPriceInfoComponent?.quantity())))
              await this.updateProductPriceInfoQuantity(rowData, x?.quantity ?? 1);

            c.documentLineItem.set({ ...rowData });
            initializedWithoutNewQuantity = true;
          });

          c.destroyRef.onDestroy(() => {
            this.unitConversionDropDownComponent = null;
            this.activeUnitConversions.set([]);
            this.activeUnitConversionValue.set(null);
            this.cachedLastUnitConversion.set(null);
          });
        }
      }

      if (cd.field == "taxPercentage") {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setValue(rowData.taxPercentage);
          c.upDownButtons.set(false);
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.taxPercentage = x;
            CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
          });
        }
      }

      if (cd.field == "productText") {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setValue(rowData.productText);
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.productText = x;
          });
        }
      }

      if (cd.field == "description") {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setValue(rowData.description);
          c.parentModel.set(rowData);
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.description = x;
          });
          subscribeUntilComponentDestroyed(c, c.parentModel, (x: DocumentLineItemDisplayClass) => {
            if (!x) return;
            rowData.description = x.description;
            rowData.productText = x.productText;
            this.editModel.set({ ...rowData });
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();
          });
        }
      }

      if (cd.field == "calculatoryCosts") {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setValue(rowData.calculatoryCosts);
          c.upDownButtons.set(false);
          c.ngModelChange$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            rowData.calculatoryCosts = x;
            CalculateDocumentLineItemPrices(rowData, this.document());
            if (this.editModel() && Object.keys(this.editModel()).length > 0) {
              this.lineItemDataService.setDocumentLineItem(rowData);
            }
          });
        }
      }


      if (cd.field == "salesTaxCode.identifier") {
        cd.getEditComponent = () => SalesTaxCodeDropDownComponent;
        cd.editComponentCreated = (c: SalesTaxCodeDropDownComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          this.salesTaxCodeDropDownComponent = c;
          c.setModel(rowData.salesTaxCode);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async x => {

            if (rowData.salesTaxCode) {
              if (c.selectedId() != rowData.salesTaxCode.id) {
                c.setModel(rowData.salesTaxCode);
                await this.setTaxLineOnDli(rowData);
              }
            }
            else if (rowData.salesTaxCodeId) {
              if (c.selectedId() != rowData.salesTaxCodeId) {
                await c.loadItemByIdAndSetSelected(rowData.salesTaxCodeId);
                await this.setTaxLineOnDli(rowData);
              }
            }
            else {
              c.setModel(null);
            }
          });

          subscribeUntilComponentDestroyed(c, c.value, async (x: SalesTaxCode) => {
            //with this if check the detail widget is not recognized since it changes the rowData directly
            // if (x?.id && rowData.salesTaxCode?.id !== x.id) {
            rowData.salesTaxCode = x;
            rowData.salesTaxCodeId = x.id;
            await this.setTaxLineOnDli(rowData);
            this.lineItemDataService.setDocumentLineItem(this.editModel());
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();
            // }
          });

          c.destroyRef.onDestroy(() => {
            this.salesTaxCodeDropDownComponent = null;
          });
        }
      }

      if (cd.field == "costCenter.identifier") {
        cd.getEditComponent = () => CostCenterDropdownComponent;
        cd.editComponentCreated = (c: CostCenterDropdownComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setModel(rowData.costCenter);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async x => {

            if (rowData.costCenter) {
              if (c.selectedId() != rowData.costCenter.id) {
                c.setModel(rowData.costCenter);
              }
            }
            else if (rowData.costCenterId) {
              if (c.selectedId() != rowData.costCenterId) {
                await c.loadItemByIdAndSetSelected(rowData.costCenterId);
              }
            }
            else {
              c.setModel(null);
            }
          });

          subscribeUntilComponentDestroyed(c, c.value, async (x: CostCenter) => {
            //with this if check the detail widget is not recognized since it changes the rowData directly
            // if (x?.id && rowData.salesTaxCode?.id !== x.id) {
            rowData.costCenter = x;
            rowData.costCenterId = x.id;
            this.lineItemDataService.setDocumentLineItem(this.editModel());
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();
            // }
          });
        }
      }

      if (cd.field == "costObject.identifier") {
        cd.getEditComponent = () => CostObjectDropdownComponent;
        cd.editComponentCreated = (c: CostObjectDropdownComponent, rowData: DocumentLineItemDisplayClass, colDef: ColumnDefinitionEx) => {
          c.setModel(rowData.costObject);

          rowData.detailToListChangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async x => {

            if (rowData.costObject) {
              if (c.selectedId() != rowData.costObject.id) {
                c.setModel(rowData.costObject);
              }
            }
            else if (rowData.costObjectId) {
              if (c.selectedId() != rowData.costObjectId) {
                await c.loadItemByIdAndSetSelected(rowData.costObjectId);
              }
            }
            else {
              c.setModel(null);
            }
          });

          subscribeUntilComponentDestroyed(c, c.value, async (x: CostObject) => {
            //with this if check the detail widget is not recognized since it changes the rowData directly
            // if (x?.id && rowData.salesTaxCode?.id !== x.id) {
            rowData.costObject = x;
            rowData.costObjectId = x.id;
            this.lineItemDataService.setDocumentLineItem(this.editModel());
            rowData.dataChangedTrigger.next();
            this.cdRef.markForCheck();
            // }
          });
        }
      }

      if (cd.field == 'statusTemplate') {
        cd.outputComponent = DocumentLineItemStatusColumnComponent;
        if (!cd.width)
          cd.width = 36;
      }

      if (cd.field == AllStoragePlacesIdentifier || this.storagePlacesToLoad.includes(cd.field)) {
        //TODO: prevent edit
        cd.getEditComponent = null;
        cd.editComponentCreated = (c, _, __) => {
          try {
            c.disabled.set(true);
          } catch (e) {
            console.error(e);
          }
        }
      }

      if (cd.field == "openQuantity") {
        cd.canEdit = true; //for now always allow
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: DocumentLineItemDisplayClass) => {
          c.upDownButtons.set(false);
          const initialOpen = this.resolveDisplayOpenQuantity(rowData);
          const editState: OpenQuantityEditState = {
            transferredQuantity: this.resolveTransferredQuantity(rowData),
            initialOpen,
            lastValidOpen: initialOpen,
            lastQuantity: rowData.quantity ?? null,
          };
          let hasFocus = false;
          let lastCanEdit: boolean = null;
          let isSyncing = false;

          // On a new line the product and the quantity are only chosen after this editor exists, so the
          // enabled state and the value have to follow the row instead of being resolved once.
          // Quantity edits from any other control land here through dataChangedTrigger and move the open
          // quantity by the same delta. The display value is written directly, because setValue() would run
          // through dataChanged() and re-emit dataChangedTrigger — the loop between both fields.
          const syncOpenQuantityEditor = () => {
            if (isSyncing) {
              return;
            }
            isSyncing = true;
            try {
              const canEdit = this.canEditOpenQuantity(rowData);
              c.disabled.set(!canEdit);
              this.applyQuantityDecimalPlacesToPhxInput(c, rowData);
              if (canEdit !== lastCanEdit) {
                lastCanEdit = canEdit;
                this.applyOpenQuantityEditTooltip(canEdit);
              }

              const quantity = rowData.quantity ?? null;
              if (!this.areOptionalQuantityValuesEqual(quantity, editState.lastQuantity, rowData)) {
                editState.lastQuantity = quantity;
                const openFromQuantity = this.calculateOpenQuantityFromQuantity(rowData, quantity, editState.transferredQuantity);
                this.writeOpenQuantityToLineItem(rowData, openFromQuantity);
                if (openFromQuantity != null) {
                  editState.lastValidOpen = openFromQuantity;
                }
              }

              if (!hasFocus) {
                c.displayValue.set(rowData.openQuantity ?? rowData.quantity ?? null);
              }
            } finally {
              isSyncing = false;
            }
          };

          syncOpenQuantityEditor();

          subscribeUntilComponentDestroyed(c, rowData.detailToListChangeTrigger, () => syncOpenQuantityEditor());
          subscribeUntilComponentDestroyed(c, rowData.dataChangedTrigger, () => syncOpenQuantityEditor());

          subscribeUntilComponentDestroyed(c, c.focus, () => {
            hasFocus = true;
          });

          // Typing in the open quantity moves the quantity live; validation runs on blur so no warning
          // is raised for intermediate values.
          subscribeUntilComponentDestroyed(c, c.ngModelChange$.pipe(skip(1)), (newOpen: number) => {
            if (isSyncing || newOpen == null || Number.isNaN(newOpen) || !this.canEditOpenQuantity(rowData)) {
              return;
            }
            this.applyOpenQuantityToLineItem(rowData, newOpen, editState.transferredQuantity);
            editState.lastQuantity = rowData.quantity ?? null;
          });

          subscribeUntilComponentDestroyed(c, c.focusLost, (newOpen: number) => {
            hasFocus = false;
            if (!this.canEditOpenQuantity(rowData)) {
              return;
            }
            // Leaving an untouched editor on a line without quantity must not turn the empty value into 0.
            if (newOpen == null && rowData.quantity == null) {
              return;
            }
            c.displayValue.set(this.commitOpenQuantityEdit(rowData, newOpen, editState));
          });
        };
        cd.editComponentCreated = cd.editComponentCreated.bind(this);
      }

    }

    super.setColumnConfig(colConfig);

    const sumHeaderFields = ['quantity', 'openQuantity', 'requestedQuantity', 'transferredQuantity'] as const;
    const needsSumHeaderTemplate = (colConfig.columns as ColumnDefinitionEx[]).some(column =>
      sumHeaderFields.includes(column.field as typeof sumHeaderFields[number]) && !column.headerTemplate
    );
    if (needsSumHeaderTemplate) {
      afterNextRender(() => {
        let templatesAssigned = false;
        for (const column of colConfig.columns as ColumnDefinitionEx[]) {
          if (sumHeaderFields.includes(column.field as typeof sumHeaderFields[number])) {
            templatesAssigned = this.assignQuantityHeaderTemplate(column) || templatesAssigned;
          }
        }
        if (templatesAssigned && this.table()) {
          this.table().columns = this.baseServices.columnService.colDefToPrimeNg(colConfig.columns as ColumnDefinitionEx[]);
          this.cdRef.detectChanges();
        }
      }, { injector: this.injector });
    }

    //csvExportColumns can be a big dataset, so use the correct search to look if already there is discountedLinePrice in it 
    let isGrossDocument = this.document()?.taxType === TaxType.GROSS;
    let discountedLinePriceField = isGrossDocument ? 'discountedLinePriceWithTax' : 'discountedLinePrice';
    let discountedLinePriceHeader = isGrossDocument ? this.i18n.translate(_('entities.DocumentLineItem.discountedLinePriceWithTax')) : this.i18n.translate(_('entities.DocumentLineItem.discountedLinePrice'));

    if (!this.csvExportColumns().some(x => x.field === discountedLinePriceField)) {
      this.csvExportColumns.set([...this.csvExportColumns(), { field: discountedLinePriceField, header: discountedLinePriceHeader } as ICsvExportColumn]);
    }
  }

  ngOnDestroy() {
    this.destroy$?.next();
    this.destroy$?.complete();
  }

  getSubItemWithParentId(parentId: string): DocumentLineItemDisplayClass[] {
    return this.itemsSig().filter(x => x.parentId == parentId);
  }

  override onDoubleClickRow(rowData: DliEx, rowIndex?: number): void {
    if (this.inEditingSig() || this.isLineItemEditBlocked(rowData)) {
      return;
    }
    rowData.document = this.documentWithoutLineItems;
    this.onRowEditInit(rowData, rowIndex);
  }

  /**
   * Document CO/RE or line-item CO/RE: no inline edit or delete.
   * Reorder / indent / outdent are allowed in the UI; the backend rejects if a completed item would be affected.
   */
  isLineItemEditBlocked(rowData?: Pick<DocumentLineItem, 'status'> | null): boolean {
    if (this.isDocumentLockedSig()) {
      return true;
    }
    const status = rowData?.status;
    return status === DocumentStatusType.CO || status === DocumentStatusType.RE;
  }

  /** True when any given line item is document-/status-locked (CO/RE). */
  private hasBlockedLineItems(items?: Array<Pick<DocumentLineItem, 'status'> | null>): boolean {
    return (items ?? []).some((item) => this.isLineItemEditBlocked(item));
  }

  //set all items to disabled when in edit mode - except the one we are editing - can also be used for disable/enable all
  setDisableStateOfItems(items: DliEx[], state: boolean, editItem?: DocumentLineItemDisplayClass) {
    // all others are disabled
    for (let i = 0; i < items.length; i++) {
      items[i].disabled = !state;
    }
    // the one we are editing is enabled
    if (editItem)
      editItem.disabled = state;
  }

  async onRowEditInit(rowData: DliEx, index: number, itemBeforeChange?: DliEx) {
    if (this.isLineItemEditBlocked(rowData)) {
      return;
    }

    this.ensureLineItemDisplaySignalsAndTriggers(rowData);
    rowData.currentlyInEditMode.set(true);

    super.onRowEditInit(rowData, index, itemBeforeChange);
    this.setDisableStateOfItems(this.itemsSig(), false, rowData);

    if (rowData.product) {
      applyContractDetailsDefaultsIfNeeded(rowData, rowData.product, this.document());
      await this.getAccessoryLinksAndSetSignals(rowData.product);
    }

    // After the inherited defaults, so opening and saving an unchanged line never asks.
    // A reopen after a failed save (itemBeforeChange given) keeps the snapshot from the real edit start.
    if (rowData.id && !(itemBeforeChange && this.contractTermsAtEditStart.has(rowData.id))) {
      this.contractTermsAtEditStart.set(rowData.id, snapshotContractTerms(rowData.contractDocumentLineItem));
    }

    this.addDLIPosNum = index;
    this.lineItemDataService.setDocumentLineItem(rowData);

    let initialQuantity = rowData.quantity;
    rowData.dataChangedTrigger.subscribe(x => {
      if (rowData.quantity != initialQuantity) {
        this.pendingForceDiscountReapply = true;
        this.discountCheckTrigger.next(rowData);
        initialQuantity = rowData.quantity;
      }
    })

    this.refreshToolbarButtons();
  }

  /** Only a line that already generated documents is in effect; until then its terms change freely. */
  private async confirmContractTermChanges(row: DliEx | null | undefined): Promise<boolean> {
    const contractLine = row?.contractDocumentLineItem;
    const before = row?.id ? this.contractTermsAtEditStart.get(row.id) : undefined;
    if ((contractLine?.billingCount ?? 0) <= 0 || !before || !hasContractTermChanges(before, contractLine)) {
      return true;
    }
    return confirmContractTermChangesDialog(this.modalService, this.i18n);
  }

  /**
   * Text lines have no article. Every other line needs a product before it can be saved.
   */
  canSaveLineItem(row?: { type?: DocumentLineItemType | null; productId?: string | null; product?: { id?: string | null } | null } | null): boolean {
    if (row?.type === DocumentLineItemType.TEXT) {
      return true;
    }
    return !!(row?.productId ?? row?.product?.id);
  }

  override inlineSaveDisabled(): boolean {
    const row = this.editModel();
    if (!row) {
      return false;
    }
    return !this.canSaveLineItem(row);
  }

  async onRowEditSave(rowData?: DliEx, index?: number, options?: SaveLineItemOptions): Promise<DliEx> {
    const rowToSave = rowData ?? this.editModel();
    if (!this.canSaveLineItem(rowToSave)) {
      this.baseServices.notificationService.warning(this.i18n.translate(_('document-line-item.save-requires-product')));
      return rowToSave;
    }
    const contractViolation = resolveContractFactorTermViolation(rowToSave?.contractDocumentLineItem);
    if (contractViolation) {
      this.baseServices.notificationService.error(this.i18n.translate(`document-line-item.contractWarning.${contractViolation}`));
      return rowToSave;
    }
    if (!await this.confirmContractTermChanges(rowToSave)) {
      this.baseServices.notificationService.warning(this.i18n.translate(_('common.save-cancel')));
      return rowToSave;
    }

    this.setLoading(true);
    this.isRowEditSaveRunning = true;
    const isStreamEnabled = this.crudOptions.enableStream === true;

    this.skipStreamRefetch.set(isStreamEnabled);

    if (!rowData)
      rowData = this.editModel();

    const selectedQuantity = this.selectedQuantitySig();
    if (typeof selectedQuantity === 'number') {
      rowData.quantity = selectedQuantity;
    }

    if (typeof rowData.quantity !== 'number') {
      rowData.quantity = 0;
      if (rowData.type !== DocumentLineItemType.TEXT)
        this.baseServices.notificationService.warning(this.i18n.translate(_('document-line-item.quantity-is-not-a-number')));
    }

    // UI handling, API accepts null and then recalculates but we dont want that on normal save. 
    // Since calculation already happens in UI - when we set null then really nothing is wanted. Otherwise option.recalculateDiscounts is set to true.
    // can be used
    if (rowData.discountPercentage == null)
      rowData.discountPercentage = 0;

    if (rowData.discountPercentage2 == null)
      rowData.discountPercentage2 = 0;

    if (index == null)
      index = this.editRowIndex;

    let force: boolean = false;
    const direction: DocumentDirection = this.document()?.documentDefinition?.documentDirection;
    let lotSize: number;

    if (direction === DocumentDirection.OUT)
      lotSize = rowData?.product?.lotSize;
    else if (direction === DocumentDirection.IN) {
      let vendorPriceListAddressLink: VendorPriceListAddressLink;
      let productId = rowData.product?.id ?? rowData.productId;
      if (productId)
        vendorPriceListAddressLink = await this.vendorPriceListAddressLinkService.getItemDataByProductAndAddress(productId, this.document()?.addressId)
      lotSize = vendorPriceListAddressLink?.lotSize;
    }
    if (!lotSize)
      force = true;
    else {
      force = await this.lotSizeInfo(lotSize, rowData);
    }

    let savedDLI: DocumentLineItem = null;
    let savedChildren: DocumentLineItem[] = [];
    let forceRefreshAtTheEnd = false;

    if (this.addDLIAtIndexModeSig()) {
      forceRefreshAtTheEnd = true; //solution for stream - until we find a better solution - without refresh getting issues of disappearing items and so on
      this.addDLIAtIndexModeSig.set(false);
    }

    if (force) {
      // this.setLoading(true);
      /**
       * always check for product type STORE since we only want to show stock errors for store products
       */
      let errorDLIs: DliEx[] = [];
      let stockErrorDLIs: DliEx[] = [];
      let otherErrorDLIs: DliEx[] = [];

      // if (rowData.unitId)
      //   delete rowData.unit;

      if (!rowData.decimalPlaces && rowData.type !== DocumentLineItemType.TEXT)
        rowData.decimalPlaces = this.decimalInput;

      // if (rowData.itemPrice === null || rowData.itemPrice === undefined)
      //   rowData.itemPrice = 0;

      const taxPercentage = rowData?.document?.taxType === TaxType.FREE ? 0 : rowData?.taxPercentage;
      if (rowData.type !== DocumentLineItemType.TEXT)
        rowData.taxPercentage = taxPercentage;
      // if (rowData?.product?.featuredAsset)
      //   rowData.product.featuredAsset = null;

      // This step was needed when the user on DLI creation chose a storage place from the row dropdown area.
      // More details here:  DEV-1020 Set storagePlace from dropdown doest save. DLI
      // Basically the storagePlaceId was not set when storagePlace was set.
      if (rowData.storagePlace?.id && rowData.type !== DocumentLineItemType.TEXT) {
        rowData.storagePlaceId = rowData.storagePlace.id;
      }

      if (rowData.discountPromise)
        await rowData.discountPromise;

      try {
        // SkipAutoAccessory needs to be true in Frontend since we set it explicitly in this method 
        // possibly based on choices made in Dialog in the UI 
        this.applyActiveUnitConversionsMetadata(rowData);
        let itemToSave: DocumentLineItem = { ...rowData, document: null, documentId: this.document()?.id }; //otherwise it will cause infinite loop
        const isNewLineItem = itemToSave?.id === this.newId;
        if (isNewLineItem)
          delete itemToSave.id;
        itemToSave = { ...itemToSave }; //this should not be needed, but we have some issues with protected properties. this is a quickfix for a customer in need

        const saveOptions: SaveLineItemOptions = {
          ...options,
          skipAutoAccessory: true,
          ...(this.pendingRecalculateTradingSetPrices
            ? { recalculateTradingSetPrices: true }
            : {}),
          ...(isNewLineItem ? { skipContractDefaultCalculation: true } : {}),
        } as SaveLineItemOptions;
        let res = await this.dliService.saveItemAlsReturnChildren(itemToSave, saveOptions);
        this.pendingRecalculateTradingSetPrices = false;
        savedDLI = res.item;
        savedChildren = res.children.map(x => x.item);

        if (savedChildren?.length > 0) {
          forceRefreshAtTheEnd = true; //solution for stream - until we find a better solution - without refresh getting issues of disappearing items and so on
        }

        if (savedDLI.isStorable && isDocumentLineItemMissingStockBacking(savedDLI.quantity, savedDLI.lineItemQuantityStatus?.quantityBackedByStock, this.document()?.documentDefinition?.stockAction))
          errorDLIs.push(savedDLI);

        //this condition was added to prevent stock from being altered twice, but since the above condition basically does the same (a-b>0 === b<a) i have commented it out
        // if (this.document()?.documentDefinition?.documentDirection == DocumentDirection.OUT && savedDLI.lineItemQuantityStatus.quantityBackedByStock < savedDLI.quantity)
        //   stockErrorDLIs.push(savedDLI);

      }
      catch (e) {

        console.error("ApolloError", e);
        let ae = e as any as ApolloError;
        let resolved = false;
        let isReservationInsufficientStock = false;
        if (ae.graphQLErrors) {

          let errorCode1 = ae.graphQLErrors[0]?.extensions?.code;
          const graphQlErrorMessage = ae.graphQLErrors[0]?.message ?? '';
          isReservationInsufficientStock =
            errorCode1 === ErrorCode.INSUFFICIENT_STOCK_ERROR &&
            (this.isReserveOutDocument() ||
              graphQlErrorMessage.includes('not-enough-available-to-reserve') ||
              graphQlErrorMessage.includes('outbound-would-cause-negative-reservation'));
          let dialogObject: DocumentLineItemDisplayClass;
          if (errorCode1 == ErrorCode.INSUFFICIENT_STOCK_ERROR && !isReservationInsufficientStock) {

            //inject document into DLI
            rowData.document = { ...this.document() }; //needed in chooser component
            rowData.documentId = rowData.document.id;

            //only if new element
            //remove exiting storage places because nothing was there.
            if (!rowData.createdAt) {
              rowData.storagePlace = null;
              rowData.storagePlaceId = null;
            }

            dialogObject = rowData;
          }
          else if (
            errorCode1 == (ErrorCode.MISSING_SERIAL_NUMBER_ERROR ?? "MISSING_SERIAL_NUMBER_ERROR")
            || errorCode1 == ErrorCode.MISSING_LOT_NUMBER_ERROR
          ) {
            dialogObject = { ...rowData, id: undefined, documentId: (rowData.document ?? this.document())?.id, /*should be optional:*/document: rowData.document ?? this.document() };
          }

          if (dialogObject) {
            let result = await this.createWarehouseDialog([{ ...dialogObject }]);
            if (!result) {
              errorDLIs.push(rowData);
            }
            else {
              savedDLI = result.documentLineItems?.at(0);
              await this.afterDocumentLineItemsMutated();
              resolved = true;
              if (this.newId)
                delete rowData.id;
            }
          }
        }
        if (!resolved) {
          const reservationSaveFailed = isReservationInsufficientStock;

          if (reservationSaveFailed) {
            const editRowId = rowData?.id ?? this.newId;
            if (editRowId)
              this.table().editingRowKeys = { [editRowId]: true };
          } else {
            if (!rowData?.id)
              rowData.id = this.newId;
            delete this.newId;
            // necessary ! otherwise not opening row again on error - somehow it overlaps (even with timeout not working)
            this.table().editingRowKeys = {};

            let index = this.itemsSig().findIndex(x => x.id === rowData.id);
            await this.onRowEditInit(rowData, index, this.clonedItems[rowData.id as string]);
          }

          this.setLoading(false);
          this.isRowEditSaveRunning = false;
          const errorMessage = ae.graphQLErrors?.[0]?.message ?? e.message;
          if (errorMessage.startsWith("error."))
            this.baseServices.notificationService.error(this.i18n.translate(errorMessage));
          else if (errorMessage.includes(' '))
            return;
          else
            this.baseServices.notificationService.error(this.i18n.translate(_("error." + (e.networkError?.error?.errors[0].extensions?.code))));
          return;
        }
      }

      // Update Apollo cache when adding new item - runs for both direct save and dialog save (after error)
      if (!isStreamEnabled)
        this.addNewItemToDocumentApolloCache([savedDLI, ...savedChildren]);

      // this.dliService.adjustDLIsOfDocument(rowData?.documentId, rowData?.id)
      this.lineItemDataService.setDocumentLineItem(null);
      if (!rowData?.id)
        rowData.id = this.newId;

      let accessoryItems: DocumentLineItem[] | DocumentLineItemInput[] = [];

      // just if new added item
      if (this.newId) {
        //quickfix to not open edit mode again
        this.table().editingRowKeys = {};
        try {
          accessoryItems = await this.saveAccessoriesForParentLine(
            savedDLI,
            this.accessoryConfigAppliedSig(),
          );
        } catch (e) {
          console.error(`Error saving accessory items for parent line item id: ${savedDLI?.id}`, e);
        }
      }

      for (let i = 0; i < accessoryItems.length; i++) {
        const savedAccessoryDli = accessoryItems[i] as DocumentLineItem;

        if (savedAccessoryDli !== null && savedAccessoryDli !== undefined) {
          if (!savedAccessoryDli.storagePlaceId) {
            const documentInput: DocumentInput = {
              id: this.document().id,
              documentDirection: this.document().documentDirection,
            };
            errorDLIs.push({
              ...savedAccessoryDli,
              document: documentInput
            } as DliEx);
          }
          else if (savedAccessoryDli.isStorable && isDocumentLineItemMissingStockBacking(savedAccessoryDli.quantity, savedAccessoryDli.lineItemQuantityStatus?.quantityBackedByStock, this.document()?.documentDefinition?.stockAction))
            errorDLIs.push(savedAccessoryDli as DliEx);
          else {
            if (!isStreamEnabled) {
              let updatedItems = this.itemsSig();
              updatedItems = updatedItems.filter(x => x.id !== savedAccessoryDli.id);
              updatedItems.splice(index + i + 1, 0, savedAccessoryDli as DliEx);
              this.setItems(updatedItems);
            }
            forceRefreshAtTheEnd = true;
          }
        }
      }

      if (errorDLIs && errorDLIs?.length > 0) {
        stockErrorDLIs = errorDLIs?.filter(x => x.isStorable) ?? [];
        otherErrorDLIs = errorDLIs?.filter(x => !x.isStorable) ?? [];
      }

      //TODO: check which message or handling is best for otherErrorDLIs
      if (otherErrorDLIs && otherErrorDLIs?.length > 0)
        console.error("errorDLIs", otherErrorDLIs.map(x => this.i18n.translate(_("common.documentLineItem-positionNumber")) + " " + x.positionNumber));

      if (stockErrorDLIs?.length > 0 && [StockAction.IN, StockAction.OUT].includes(this.document()?.documentDefinition?.stockAction)) {
        let result = await this.createWarehouseDialog(stockErrorDLIs);

        if (result?.documentLineItems && result.documentLineItems.length > 0) {
          savedDLI = result.documentLineItems[0];
        }

        //TODO check if thats needed here
        // just add new items not already in unstockedItems
        this.unstockedItemsSig.update(x => { return [...x, ...stockErrorDLIs.filter(y => !x.includes(y))] });
      }

      this.rememberPendingStreamSavedItems([savedDLI, ...savedChildren]);

      // Keep saved row visible immediately in UI.
      // In stream mode this is a fallback in case no stream delta arrives for the save mutation.
      const previousRowId = rowData?.id ?? this.newId;
      this.itemsSig.update(items => {
        const next = [...items] as DliEx[];
        const bySavedId = savedDLI?.id ? next.findIndex((i) => i?.id === savedDLI.id) : -1;
        const byPreviousId = previousRowId ? next.findIndex((i) => i?.id === previousRowId) : -1;
        const replaceIndex = bySavedId >= 0 ? bySavedId : byPreviousId >= 0 ? byPreviousId : index;

        if (replaceIndex >= 0 && replaceIndex < next.length) {
          next[replaceIndex] = savedDLI as DliEx;
        } else if (savedDLI) {
          next.push(savedDLI as DliEx);
        }

        for (const ch of savedChildren) {
          if (!ch?.id) {
            continue;
          }
          const childIdx = next.findIndex((i) => i?.id === ch.id);
          if (childIdx >= 0) {
            const merged = { ...next[childIdx], ...ch } as DliEx;
            this.ensureLineItemDisplaySignalsAndTriggers(merged);
            next[childIdx] = merged;
          }
        }
        return next;
      });

      // SBOMH unit price / totals are recalculated on the server when an SBOMI is saved, but the save mutation only
      // returns the SBOMI. Without reloading the parent row, the list still shows the old header price until a full refresh.
      if (savedDLI?.type === DocumentLineItemType.SBOMI && savedDLI.parentId) {
        const parentInPage = this.itemsSig().some((i) => i?.id === savedDLI.parentId);
        if (parentInPage) {
          try {
            const parentFresh = await this.dliService.getItemData(savedDLI.parentId);
            if (parentFresh?.id) {
              this.itemsSig.update((items) => {
                const next = [...items] as DliEx[];
                const pIdx = next.findIndex((i) => i?.id === parentFresh.id);
                if (pIdx >= 0) {
                  next[pIdx] = { ...next[pIdx], ...parentFresh } as DliEx;
                }
                return next;
              });
              if (!isStreamEnabled) {
                this.addNewItemToDocumentApolloCache([parentFresh]);
              }
            }
          } catch (e) {
            console.error(
              `Failed to refresh SBOMH line after SBOMI save (parentId: ${savedDLI.parentId})`,
              e,
            );
          }
        }
        // Also refresh all children; the server may redistribute amounts across the whole set.
        await this.refreshTradingSetGroup(savedDLI.parentId);
      }

      // When saving an SBOMH price, the server may redistribute SBOMI amounts. Refresh the whole group so the list
      // always shows a consistent header/child split after switching between header/child edits.
      if (savedDLI?.type === DocumentLineItemType.SBOMH) {
        await this.refreshTradingSetGroup(savedDLI.id);
      }

      this.resetAccessorySignals();
      this.table().cancelRowEdit(savedDLI);
      this.setSelectedItems([]);
      this.lastSelectedItemSig.set(null);

      // since we now return children we don't need to refresh here
      // await this.refresh(true);

      //clonedItems property needs to stay after refresh because refresh also calls onRowEditCancel which needs clonedItems on edit and not addNew
      delete this.clonedItems[rowData.id as string];

      this.resetDLIEditSignals();

      // Do not set table.totalRecords here - preserve it so pagination stays on current page (endEdit/setItems use -1 to keep totalRecords)
      this.endEdit(rowData, savedDLI);
      this.addDLIPosNum = null;
      this.warehouseNumbers = false;
      this.setDisableStateOfItems(this.itemsSig(), true, rowData);

      this.afterDocumentLineItemsMutated().then(() => {
        this.itemsChanged.emit(this.itemsSig());
      });

      this.cdRef.detectChanges();
      //this is QUICKFIX because sometimes there was error in repetetive new - save - new ...
      //TODO find better solution
      setTimeout(() => {
        this.setLoading(false)
      }, 300);

      if (forceRefreshAtTheEnd) {
        await this.refresh(true);
      }
      this.reRenderTable();

      this.skipStreamRefetch.set(false);

      this.isRowEditSaveRunning = false;
      return savedDLI as DliEx;
    }

    // you cannot use onRowEditCancel or similar here because we want the edit to stay open if force false

    //this is QUICKFIX because sometimes there was error in repetetive new - save - new ... 
    //TODO find better solution
    setTimeout(() => {
      this.setLoading(false)
    }, 300
    )
    this.skipStreamRefetch.set(false);
    this.isRowEditSaveRunning = false;
    return rowData as DliEx;
  }

  // maybe refactor to be one method together with deleteSelectedItems
  async deleteDLIs(itemsToDelete?: DocumentLineItemDisplayClass[], showNotification?: boolean) {
    if (!itemsToDelete?.length) {
      return;
    }
    itemsToDelete = itemsToDelete.filter(
      (item) => item && !item.disabled && !this.isLineItemEditBlocked(item),
    );
    if (!itemsToDelete.length) {
      return;
    }
    this.setSelectedItems(itemsToDelete);
    try {
      // Use this (not super) so the CO/RE guard in deleteSelectedItems always runs.
      await this.deleteSelectedItems();
      //TODO check response which items were deleted and which not and then remove those from cach
    }
    catch (e) {
      console.log("error when deleting items", e);
    }
  }

  override async deleteSelectedItems(): Promise<void> {
    if (this.hasBlockedLineItems(this.selectedItems())) {
      return;
    }
    await super.deleteSelectedItems();
  }

  override async onDeletionFinish(items: DocumentLineItem[]): Promise<void> {
    let cachedDoc = this.getDocumentApolloCache() as unknown as Document;

    if (cachedDoc) {
      try {
        this.apollo.client.cache.modify({
          id: "Document:" + cachedDoc.id,
          fields: {
            lineItems(existingRefs = [], { readField }) {
              return existingRefs.filter((ref: any) => {
                return !items.some((item: DocumentLineItem) => {
                  return readField("id", ref) === item.id;
                });
              });
            },
            itemsCount(existingCount = 0, { readField }) {
              return existingCount - items.length;
            }
          },
        });
      }
      catch (e) {
        console.log("error when setting GraphQL Cache", e);
      }
      this.lineItemDataService.setDocumentLineItem(null); //reset the toolbar
      await this.afterDocumentLineItemsMutated();
    }
    else
      await this.afterDocumentLineItemsMutated();

  }

  /** Notifies document-scoped listeners that line items changed in this document. */
  private notifyDocumentLineItemsChanged(): void {
    this.docService.notifyDocumentLineItemsChanged(this.document()?.id);
  }

  private assignQuantityHeaderTemplate(column: ColumnDefinitionEx): boolean {
    if (column.field === 'quantity') {
      const template = this.quantityColumnHeader();
      if (template) {
        column.headerTemplate = template;
        return true;
      }
    }
    if (column.field === 'openQuantity') {
      const template = this.openQuantityColumnHeader();
      if (template) {
        column.headerTemplate = template;
        return true;
      }
    }
    if (column.field === 'requestedQuantity') {
      const template = this.requestedQuantityColumnHeader();
      if (template) {
        column.headerTemplate = template;
        return true;
      }
    }
    if (column.field === 'transferredQuantity') {
      const template = this.transferredQuantityColumnHeader();
      if (template) {
        column.headerTemplate = template;
        return true;
      }
    }
    return false;
  }

  private async afterDocumentLineItemsMutated(): Promise<void> {
    this.notifyDocumentLineItemsChanged();
    const documentId = this.document()?.id;
    if (!documentId) {
      return;
    }
    await this.docService.refetchOnlyDocumentLineItemCausedChanges(documentId);
  }

  /**
   * Reads the Document entity from Apollo cache if present.
   * Uses a minimal fragment (id only) so we get a hit when Document was cached by getDocument()
   * or by getDocumentLineItems (WITH_DOC) where each item has document { ...DocumentWithoutItems }.
   * Returns null when no Document entity exists (e.g. when only getDocumentLineItems was used without WITH_DOC).
   */
  getDocumentApolloCache(): { id: string } | null {
    const docId = this.document()?.id;
    if (!docId)
      return null;
    const docFrag = this.apollo.client.readFragment<{ id: string }>({
      id: "Document:" + docId,
      fragment: gql`
        fragment DocumentCacheCheck on Document {
          id
        }
      `
    });
    return docFrag ?? null;
  }

  override async copyItem(): Promise<DliEx | null | undefined> {
    this.setLoading(true);
    try {
      const ls = this.lastSelectedItemSig();
      if (!ls) {
        this.baseServices.notificationService.info(_('common.no-selection'));
        return;
      }
      if (!ls.id) {
        this.baseServices.notificationService.info(_('common.element-has-no-id'));
        return;
      }

      const options = await resolveCopyOptions(
        this.crudService,
        this.baseServices.modalService,
        this.baseServices.i18nService,
        this.baseServices.settingsService,
      );
      if (options === null) {
        return null;
      }

      const copyResult = await this.dliService.copyLineItemResult(ls.id);
      if (!copyResult?.item) {
        return null;
      }

      const copiedChildren = (copyResult.children ?? [])
        .map((child) => child?.item)
        .filter((item): item is DocumentLineItem => !!item);
      this.addNewItemToDocumentApolloCache([copyResult.item, ...copiedChildren]);
      await this.afterDocumentLineItemsMutated();

      const editingRow = this.editModel();
      if (this.inEditingSig() && editingRow) {
        this.endEdit(editingRow, editingRow);
      }

      await this.refresh(true);

      this.baseServices.notificationService.success(_('common.copied'));
      this.setSelectedItems();
      this.itemsChanged.emit(this.itemsSig());
      return copyResult.item as DliEx;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Writes the newly saved line items into Apollo cache so the list and document stay in sync without refetch.
   * Updates both caches when present:
   * - getDocumentLineItems (ROOT_QUERY): so the table shows the new items.
   * - Document:docId (when in cache, e.g. from document edit): so document.lineItems / itemsCount stay in sync.
   */
  private addNewItemToDocumentApolloCache(savedDLIs: DocumentLineItem[]): void {
    if (!savedDLIs?.length)
      return;
    const cache = this.apollo.client.cache;
    const newItems = savedDLIs.map((dli) => {
      return { ...dli }
    });
    newItems.forEach(item => this.stripLineItemDisplaySignalsAndTriggers(item));

    try {

      const docFrag = this.getDocumentApolloCache();
      if (docFrag) {
        let newlyAddedCount = 0;
        cache.modify({
          id: "Document:" + docFrag.id,
          fields: {
            lineItems(existingRefs = [], { readField }) {
              const refs = existingRefs as Reference[];
              const existingIds = new Set(
                refs.map((ref) => readField('id', ref) as string)
              );
              const result: (Reference | DocumentLineItem)[] = refs.map((ref) => {
                const id = readField('id', ref) as string;
                const newItem = newItems.find((n) => n.id === id);
                return newItem ?? ref;
              });
              for (const newItem of newItems) {
                if (!existingIds.has(newItem.id)) {
                  result.push(newItem);
                  newlyAddedCount++;
                }
              }
              return result;
            },
            itemsCount(existingCount = 0) {
              return existingCount + newlyAddedCount;
            }
          }
        });
      }
    }
    catch (e) {
      console.log("error when setting GraphQL Cache", e);
    }
  }

  // FOR FUTURE REFERENCE / USE - but then the normal save is not in sync anymore
  /**
   * Replaces the current page's getDocumentLineItems cache entry with items from itemsSig() (order + data).
   * Single modify: only the cache entry for the current page is updated.
   * @important - this is not necessary in normal dli save method or when refresh is called !! just when we want to not refresh !
   */
  private updateApolloCache() {
    let dlis = [...this.itemsSig()];
    dlis.forEach(item => this.stripLineItemDisplaySignalsAndTriggers(item));
    const cache = this.apollo.client.cache;
    const docId = this.document()?.id;
    const currentSkip = this.currentSearchInput?.skip ?? 0;
    const currentTake = this.currentSearchInput?.take ?? 25;

    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        getDocumentLineItems(
          existing: { items?: unknown[]; totalItems?: number;[k: string]: unknown } | undefined,
          { storeFieldName }
        ) {
          if (!existing || !docId)
            return existing ?? undefined;
          const key = String(storeFieldName ?? '');
          if (!key.includes(docId) || !key.includes(String(currentSkip)) || !key.includes(String(currentTake)))
            return existing;
          return { items: dlis };
        }
      }
    });
  }

  /**
   * Inline edit owns the selection. Other row checkboxes stay disabled so a click
   * cannot end the open editor. The edited row stays checked: unchecking it ends
   * row edit in `onCheckboxClick`, then `onRowUnselect` cancels and `setItems`
   * runs while inline editors are still being destroyed.
   */
  override getRowDataDisabledState(rowData: DliEx, checkbox: TableCheckbox | TableHeaderCheckbox): boolean {
    if (!this.inEditingSig()) {
      return false;
    }
    if (this.isRowInInlineEdit(rowData)) {
      checkbox.checked = true;
    }
    return true;
  }

  /** Header checkbox clears disabled row checkboxes too. */
  override getHeaderCheckboxDisabledState(_rows: DliEx[]): boolean {
    return this.inEditingSig();
  }

  override onCheckboxClick(event: Event, rowData: DliEx): void {
    if (this.inEditingSig()) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    super.onCheckboxClick(event, rowData);
  }

  override onRowUnselect(event: TableRowUnSelectEvent<DliEx>): void {
    const rowData = event?.data;
    if (Array.isArray(rowData) || !this.isRowInInlineEdit(rowData)) {
      super.onRowUnselect(event);
      return;
    }
    const selected = this.selectedItems() ?? [];
    if (!selected.some((item) => item?.id === rowData.id)) {
      this.setSelectedItems([...selected, rowData]);
    }
  }

  private isRowInInlineEdit(item: { id?: string } | null | undefined): boolean {
    if (!this.inEditingSig() || !item?.id) {
      return false;
    }
    const editId = this.editModel()?.id ?? this.inEditRowId ?? this.newId;
    return item.id === editId;
  }

  async onRowEditCancel(rowData?: DocumentLineItemDisplayClass, index?: number) {
    //TODO check - this is not always existing
    rowData?.currentlyInEditMode?.set(false);
    this.lineItemDataService.reset();
    this.lineItemDataService.setDocumentLineItem(null);
    super.onRowEditCancel(rowData, index); //sets editModel to null
    this.resetDLIEditSignals();
    this.resetAccessorySignals();
    this.refreshToolbarButtons();
    afterNextRender(() => {
      this.setDisableStateOfItems(this.itemsSig(), true, rowData);
    }, { injector: this.injector });
  }

  override deleteAtIndexAndFillHole(items: any[], index: number) {
    if (index >= 0 && index < items.length) {
      items.splice(index, 1); // Remove element at index

      // Adjust indexes of the remaining elements
      for (let i = index; i < items.length; i++) {
        items[i].index = i; // Assuming you have an "index" property in your objects
      }

      // for what was this necessary - it broke our posNumber!
      // items = resetItemsPositionNumberAndText(items, false);
    }
    this.setItems(items);
  }

  splitAtDotAsArray(text: string): string[] | string {
    if (text.includes('.')) {
      // Split the text into an array using the dot as the separator
      return text.split('.');
    }
    else return text
  }

  setTableWidth(): void {
    let additionalWidth: number;
    // additionalWidth += this.checkCol?.offsetWidth;
    // additionalWidth += this.actionsCol?.offsetWidth;
    // additionalWidth += this.extendCol?.offsetWidth;
    super.setTableWidth(additionalWidth);
  }

  /**
   * Mirrors backend {@link DocumentLineItemProductHydrationService.addProductToDocumentLineItem} type mapping.
   */
  private resolveLineItemTypeFromProduct(product: Product): DocumentLineItemType {
    if (product.productType === ProductType.TRADINGSET) {
      return DocumentLineItemType.SBOMH;
    }
    if (product.productType === ProductType.SHIPPING) {
      return DocumentLineItemType.SHIPPING;
    }
    if (product.productType === ProductType.SERVICE) {
      return DocumentLineItemType.SERVICE;
    }
    return DocumentLineItemType.PRODUCT;
  }

  private applyDefaultLineItemTypeIfMissing(item: DocumentLineItemDisplayClass): void {
    if (!item.type) {
      item.type = DocumentLineItemType.PRODUCT;
    }
  }

  private applyLineItemTypeFromProduct(
    rowDataDli: DocumentLineItemDisplayClass,
    product: Product,
  ): void {
    if (rowDataDli.type === DocumentLineItemType.SBOMI || rowDataDli.type === DocumentLineItemType.TEXT) {
      return;
    }
    rowDataDli.type = this.resolveLineItemTypeFromProduct(product);
  }

  /**
   * mainly fixes the issue where shortcut ctrl+enter didnt add new item because of refresh
   */
  addNewSubscription: OutputRefSubscription;

  /**
   * will save current item and then add new item if needed
   * @param index 
   * @param defaultValues 
   * @returns 
   */
  async addNewItem(index?: number, defaultValues?: Partial<DocumentLineItemDisplayClass>) {

    if (this.IsLoading) {
      //when refresh is done we try again

      if (!this.addNewSubscription) {
        this.addNewSubscription = this.onRefresh.subscribe(async (res) => {
          if (res === false) {
            this.addNewSubscription.unsubscribe();
            this.addNewSubscription = null;
            await this.addNewItem(index, defaultValues);
          }
        });
      }
      return;
    }

    if (defaultValues?.disabled)
      return;

    if (index == null) {
      const anchor = this.selectedItems()?.[0] ?? this.lastSelectedItemSig();
      if (anchor?.id) {
        const anchorIndex = this.itemsSig()?.findIndex((item) => item.id === anchor.id);
        if (anchorIndex >= 0) {
          index = anchorIndex;
        }
      }
    }

    if (this.inEditingSig()) {
      await this.onRowEditSave();
      if (this.inEditingSig()) {
        return;
      }
    }

    if (!defaultValues)
      defaultValues = {} as DocumentLineItemDisplayClass;

    this.lastSelectedItemSig.set(null);
    this.setSelectedItems([]);
    this.newId = uuidv4();

    this.warehouseNumbers = true;

    this.preAddNewItem(defaultValues as DocumentLineItemDisplayClass);

    const priceIncludesTax = this.document()?.taxType === TaxType.GROSS || this.document()?.taxType === TaxType.FREE;

    let newItem = {
      id: this.newId,
    } as DocumentLineItemDisplayClass;
    newItem = { ...newItem, ...defaultValues };
    if (newItem.product) {
      this.applyLineItemTypeFromProduct(newItem, newItem.product);
    }
    this.applyDefaultLineItemTypeIfMissing(newItem);
    if (newItem.type !== DocumentLineItemType.TEXT)
      newItem.priceIncludesTax = priceIncludesTax;
    this.ensureLineItemDisplaySignalsAndTriggers(newItem);

    if (!newItem.storagePlace && newItem.type !== DocumentLineItemType.TEXT) {
      newItem.storagePlace = this.document()?.storagePlace; //this still can be null, will handled at the bottom async
    }

    if (newItem.storagePlace?.id)
      this.selectedStoragePlaceIdSig.set(newItem.storagePlace.id);

    if (!newItem.decimalPlaces && newItem.type !== DocumentLineItemType.TEXT)
      newItem.decimalPlaces = this.decimalInput;

    if (this.document()?.costBase?.type === CostType.CENTER && !newItem.costCenter && newItem.type !== DocumentLineItemType.TEXT)
      newItem.costCenter = this.document()?.costBase;
    if (this.document()?.costBase?.type === CostType.OBJECT && !newItem.costObject && newItem.type !== DocumentLineItemType.TEXT)
      newItem.costObject = this.document()?.costBase;

    if (!newItem.quantity) {
      this.selectedQuantitySig.set(null);
    }

    if (index === null || index === undefined || index < 0) {
      await this.addNewItemAtEnd(newItem);
    } else {
      await this.addNewItemAtIndex(newItem, index);
    }

    //expand group of no predecessor
    if (this.rowGroupMode() && !this.table().expandedRowKeys["undefined"])
      this.table().expandedRowKeys["undefined"] = true;

    this.cdRef.markForCheck();
    this.triggerChangeDetection$.next(true);

    // get default from server
    if (!newItem.storagePlace?.id && newItem.type !== DocumentLineItemType.TEXT) {
      let storagePlace = await this.storagePlaceService.getDefaultStoragePlaceData();

      if (!this.editModel()) {
        console.error("editModel is null, this should never happen");
        return;
      }

      this.editModel.update(dli => {
        dli.storagePlace = storagePlace;
        return dli;
      });

      this.editModel().detailToListChangeTrigger?.next();

      this.selectedStoragePlaceIdSig.set(storagePlace.id);
    }
    //do this before saving - since on cancel will be decremented then - when saving will be newly calculated anyways
    this.table().totalRecords = this.table().totalRecords + 1;

  }

  private async preAddNewItem(defaults: DocumentLineItem) {
    if (defaults?.type === DocumentLineItemType.TEXT)
      this.focusedColumnName.set('description');
    else
      this.focusedColumnName.set('identifier');
  }

  /**
   * PrimeNG iterates `table.value`. Rebind it to itemsSig after in-place insert so the new row
   * is visible before edit mode starts (enrichment may have detached the two arrays).
   */
  private async startRowEditForNewItem(newItem: DocumentLineItemDisplayClass, index: number): Promise<void> {
    this.bindTableValueToItemsSig();
    await this.onRowEditInit(newItem, index);
  }

  private async addNewItemAtEnd(newItem: DocumentLineItemDisplayClass) {
    if (this.tableAscendingSig()) {
      await this.addNewItemInAscendingOrder(newItem);
    } else {
      await this.addNewItemInDescendingOrder(newItem);
    }
  }

  private async addNewItemInAscendingOrder(newItem: DocumentLineItemDisplayClass) {

    const numberOfItemsOnLastPage = this.table().totalRecords % this.table().rows;
    const firstItemOnLastPageIndex = this.table().totalRecords - numberOfItemsOnLastPage + 1;
    this.currentSearchInput.skip = firstItemOnLastPageIndex - 1;
    this.newItemIndex = this.table().totalRecords - this.currentSearchInput.skip;

    // check if already on lastPage 
    if ((this.table().first + 1) >= firstItemOnLastPageIndex && numberOfItemsOnLastPage < this.table().rows) {
      this.itemsSig.update(items => {
        items.push(newItem);
        return items;
      });
      await this.startRowEditForNewItem(newItem, this.newItemIndex);
    }
    // if not on last page move to last page and refresh
    else {
      let refreshStarted = false;
      const addSub = this.Loading$.subscribe(async (isLoading) => {
        if (isLoading) refreshStarted = true;
        if (!isLoading && refreshStarted) {
          this.itemsSig.update(items => {
            items.push(newItem);
            return items;
          });
          await this.startRowEditForNewItem(newItem, this.newItemIndex);
          this.table().first = firstItemOnLastPageIndex - 1; // this updates the page
          addSub.unsubscribe();
        }
      });
      await this.refresh();
    }
  }

  private async addNewItemInDescendingOrder(newItem: DocumentLineItemDisplayClass) {
    this.itemsSig.update(items => {
      items.unshift(newItem);
      return items;
    });
    this.newItemIndex = 0;
    await this.startRowEditForNewItem(this.itemsSig()[this.newItemIndex], this.newItemIndex);
  }

  private async addNewItemAtIndex(newItem: DocumentLineItemDisplayClass, index: number) {

    this.addDLIAtIndexModeSig.set(true);

    const indexOnActivePage = index;
    const items = this.itemsSig();
    let relatedPosNumber = items[indexOnActivePage]?.positionNumber ?? 0;

    if (this.tableAscendingSig())
      this.newItemIndex = indexOnActivePage + 1;
    else
      this.newItemIndex = indexOnActivePage;

    // Check for parentId of the previous item
    const previousItemIndex = indexOnActivePage;
    const nextItemIndex = this.tableAscendingSig() ? indexOnActivePage + 1 : indexOnActivePage - 1;
    const previousItem = items[previousItemIndex];
    const nextItem = items[nextItemIndex];

    // SBOMI rows are locked to SBOMI type, except explicit text positions inside a trading set.
    if (newItem.type !== DocumentLineItemType.TEXT) {
      if ((previousItem?.type == DocumentLineItemType.SBOMI) || (nextItem?.type == DocumentLineItemType.SBOMI) ||
        (previousItem?.type == DocumentLineItemType.SBOMH && this.tableAscendingSig()) ||
        (nextItem?.type == DocumentLineItemType.SBOMH && !this.tableAscendingSig())) {
        newItem.type = DocumentLineItemType.SBOMI;
        newItem.isStorable = false;
        newItem.nonSalesRelevant = true;
        newItem.isFrozenQuantity = true;
      }
      // for now we dont recognize other types since we dont filter products and so it can lead to mismatch of shipping is saved as type normal product and so on.
      else if (previousItem && !newItem.type && previousItem.type === DocumentLineItemType.TEXT)
        newItem.type = previousItem.type;
    }

    let previousParentId = previousItem?.parentId;
    let nextParentId = nextItem?.parentId;
    let parentId = null;
    if (nextParentId === previousItem?.id)
      parentId = previousItem?.id;
    else if (previousParentId)
      parentId = previousParentId;
    else if (nextParentId)
      parentId = nextParentId;

    if (parentId && !newItem.parentId)
      newItem.parentId = parentId;

    newItem.positionNumber = relatedPosNumber + 1;

    // When adding after the last row on the page (ascending), the new item would land on the next page – jump there like addNewItemAtEnd
    const globalInsertIndex = this.table().first + (this.tableAscendingSig() ? indexOnActivePage + 1 : indexOnActivePage);
    const targetPageFirst = this.table().rows * Math.floor(globalInsertIndex / this.table().rows);
    const isInsertOnDifferentPage = this.tableAscendingSig() && targetPageFirst !== this.table().first;

    if (isInsertOnDifferentPage) {
      this.currentSearchInput.skip = targetPageFirst;
      this.newItemIndex = globalInsertIndex - targetPageFirst;

      let refreshStarted = false;
      const addSub = this.Loading$.subscribe(async (isLoading) => {
        if (isLoading) refreshStarted = true;
        if (!isLoading && refreshStarted) {
          const itemsAfterRefresh = simpleDeepClone(this.itemsSig());
          itemsAfterRefresh.splice(this.newItemIndex, 0, newItem);
          this.setItems(itemsAfterRefresh);
          this.table().first = targetPageFirst - 1;
          await this.startRowEditForNewItem(itemsAfterRefresh[this.newItemIndex], this.newItemIndex);
          addSub.unsubscribe();
        }
      });
      await this.refresh();
    } else {
      this.itemsSig.update(x => {
        x.splice(this.newItemIndex, 0, newItem);
        return x;
      });

      await this.startRowEditForNewItem(this.itemsSig()[this.newItemIndex], this.newItemIndex);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // Calculate the new overlay width based on the window size and any adjustments
    this.tableWidth = this.getWidthOfTable(); //adjustment for fitting the screen better
  }

  onTableScroll(event: any) {
    const container: HTMLDivElement = document.querySelector('.detail-fields-wrapper');
    if (container)
      this.renderer.setStyle(container, "margin-left", this.pDataTable.scrollLeft + 'px');
  }

  // Setting Detail Width and Overlay-Like Behavior with reactive margin
  onToggleDetailFields(rowData: DocumentLineItem) {

    this.toggleRowExpansion(rowData);
    this.tableWidth = this.getWidthOfTable(); //adjustment for fitting the screen better
    this.cdRef.detectChanges();
    this.triggerChangeDetection$.next(true);
  }

  resetDLIEditSignals() {
    this.selectedQuantitySig.set(this.isRequestedQuantityColumnVisible() ? null : 1);
    this.selectedProductSig.set(null);
    this.selectedStoragePlaceIdSig.set(null);
    this.pendingRecalculateTradingSetPrices = false;
  }

  async onProductChange(
    e: Product,
    rowDataDli: DocumentLineItemDisplayClass,
    triggerDiscountCheck = true,
    skipUnitConversionDropdownSync = false,
  ) {

    this.loadingSig.set(true);

    try {
      const direction: DocumentDirection = this.document()?.documentDefinition?.documentDirection;

      if (!e) {
        rowDataDli.product = null;
        rowDataDli.productIdentifier = null;
        rowDataDli.productId = null;
        rowDataDli.description = null;
        rowDataDli.itemPrice = null;
        rowDataDli.discountedLinePrice = null;
        this.applyQuantityDefaultAfterProductSelection(rowDataDli, false);
        rowDataDli.quantityToBaseUnitFactor = null;
        rowDataDli.baseUnitId = null;
        rowDataDli.baseUnit = null;
        rowDataDli.unitIdentifier = null;
        rowDataDli.unitId = null;
        rowDataDli.unit = null;
        rowDataDli.addition = null;
        rowDataDli.addition2 = null;
        rowDataDli.nonDiscountable = null;
        rowDataDli.nonCashDiscountable = null;
        rowDataDli.nonProvisionable = null;
        rowDataDli.costCenter = null;
        rowDataDli.costObject = null;
        rowDataDli.taxPercentage = null;
        rowDataDli.salesTaxCodeId = null;
        rowDataDli.salesTaxCode = null;
        rowDataDli.isStorable = null;
        rowDataDli.detailToListChangeTrigger?.next();
        //rowDataDli.type = null; //we dont want to remove the type
        rowDataDli.itemSalesTaxCategoryId = null;
        rowDataDli.account = null;
        if (this.discountPercentageInput)
          this.discountPercentageInput.disabled.set(false);
        if (this.discountPercentage2Input)
          this.discountPercentage2Input.disabled.set(false);
        this.updateUnitBasedQuantityNumberInput(rowDataDli);
        this.editModel.set(rowDataDli);
        rowDataDli.dataChangedTrigger.next();
        this.loadingSig.set(false);
        return; //should we remove more fields in DLI?
      }
      else {
        rowDataDli.itemPrice = null;
        rowDataDli.taxPercentage = null;
        rowDataDli.salesTaxCodeId = null;
        rowDataDli.salesTaxCode = null;
        rowDataDli.itemSalesTaxCategoryId = null;
        rowDataDli.account = null;
      }

      // null when the base entity already holds the wanted text - then no translations are fetched
      const lineItemLanguage = this.dliService.lineItemLanguage(this.document());

      //reload the product to have all resolved fields (contract details, cost objects, …)
      try {
        const missingProductData = await this.productService.getItemMissingFromDropdownData(e.id, lineItemLanguage != null);
        if (missingProductData) {
          e = { ...e, ...missingProductData };
        }
      } catch (error: unknown) {
        console.error(`Failed to load extra product fields for product ${e.id}`, error);
      }

      if (isContractDocument(this.document()) && isTradingSetDocumentLineItem(null, e)) {
        this.baseServices.notificationService.error(
          this.i18n.translate(_('error.trading-set.not-allowed-on-contract-document')),
        );
        this.loadingSig.set(false);
        return;
      }
      //TODO: find out if this is best solution - or defaults need to be applied again after product change - this can overwrite defaultValues

      const productFieldsInLineItem: ProductFieldsInLineItem = filterObjectByClass<Product, ProductFieldsInLineItem>(e, ProductFieldsInLineItem);

      // overlay the translation onto the snapshot fields, not onto e - e stays master data (rowDataDli.product = e)
      this.dliService.applyProductTranslation(productFieldsInLineItem, e, lineItemLanguage);

      for (let key in productFieldsInLineItem) {
        const purchaseIgnoresProductFlag = direction === DocumentDirection.IN
          && (key === 'nonDiscountable' || key === 'nonCashDiscountable');
        if (key !== 'customFields' && !purchaseIgnoresProductFlag) {
          rowDataDli[key] = null;
          if (productFieldsInLineItem[key] != null) {
            rowDataDli[key] = productFieldsInLineItem[key];
          }
        }
        if (key === 'customFields') {
          //TODO improve - since the customFields are not present in the DLI - so will not be overwritten
          for (let customFieldKey in productFieldsInLineItem.customFields) {
            if (customFieldKey !== '__typename' && productFieldsInLineItem.customFields?.[customFieldKey] != undefined && rowDataDli.customFields?.[customFieldKey]) {
              rowDataDli.customFields[customFieldKey] = null
              rowDataDli.customFields[customFieldKey] = productFieldsInLineItem.customFields[customFieldKey];
            }
          }

        }
      }

      rowDataDli.baseUnitId = rowDataDli.unit?.id ?? rowDataDli.unitId;
      rowDataDli.baseUnit = rowDataDli.unit;
      rowDataDli.quantityToBaseUnitFactor = 1;

      delete rowDataDli.referenceId; //quick hack / fix, @Daniel needs to fix

      rowDataDli.product = e;
      this.applyLineItemTypeFromProduct(rowDataDli, e);

      if (e.productType === ProductType.TRADINGSET) {
        // Unit price for the set must come from document price infos (list / customer), not the product master itemPrice
        // copied in the field loop — that value would skip preSelect and hide the real list price.
        rowDataDli.itemPrice = null;
        rowDataDli.priceSource = null;
        delete rowDataDli.isCalculatedItemPrice;
      }
      rowDataDli.productId = e.id;
      rowDataDli.productIdentifier = e.identifier;
      rowDataDli.contractDocumentLineItem = {} as DocumentLineItem['contractDocumentLineItem'];
      applyContractDetailsDefaultsIfNeeded(rowDataDli, e, this.document());

      rowDataDli.unitIdentifier = e.unit?.identifier;
      rowDataDli.unitShortForm = await this.dliService.unitShortFormForLine(e.unit, lineItemLanguage);
      if (direction === DocumentDirection.OUT) {
        rowDataDli.productText = this.dliService.productTextOf(e, DocumentDirection.OUT, lineItemLanguage);
        rowDataDli.itemSalesTaxCategoryId = e.itemSalesTaxCategoryOutId;
      }
      else if (direction === DocumentDirection.IN) {
        rowDataDli.productText = this.dliService.productTextOf(e, DocumentDirection.IN, lineItemLanguage);
        rowDataDli.itemSalesTaxCategoryId = e.itemSalesTaxCategoryInId;
        const addressId = this.document()?.addressId ?? this.document()?.address?.id;
        const vendorLink = addressId && e.id
          ? await this.vendorPriceListAddressLinkService.getItemDataByProductAndAddress(e.id, addressId)
          : null;
        // Purchase lines take these flags from the supplier condition, not from the product master.
        rowDataDli.nonDiscountable = !!vendorLink?.nonDiscountable;
        rowDataDli.nonCashDiscountable = !!vendorLink?.nonCashDiscountable;
        if (rowDataDli.nonDiscountable) {
          rowDataDli.discountPercentage = null;
          rowDataDli.discountPercentage2 = null;
        }
      }

      if (e.productType === ProductType.STORE || e.productType === ProductType.SIMPLEPRODUCTION || rowDataDli.type === DocumentLineItemType.SBOMI)
        rowDataDli.isStorable = true;
      else
        rowDataDli.isStorable = false;

      if (this.isStorableInput?.formControl)
        this.isStorableInput.formControl.setValue(rowDataDli.isStorable);

      rowDataDli.detailToListChangeTrigger?.next();

      this.applyQuantityDefaultAfterProductSelection(rowDataDli, true);

      await this.setTaxLineOnDli(rowDataDli);
      if (this.salesTaxCodeDropDownComponent)
        this.salesTaxCodeDropDownComponent.setModel(rowDataDli.salesTaxCode);

      this.selectedProductSig.set(rowDataDli.product);
      if (this.editModel() && Object.keys(this.editModel()).length > 0) {
        this.lineItemDataService.setDocumentLineItem(rowDataDli);
      }

      if (this.discountPercentageInput && this.editModel()) {
        this.discountPercentageInput.setValue(rowDataDli.discountPercentage);
        this.discountPercentageInput.disabled.set(rowDataDli.nonDiscountable);
      }
      if (this.discountPercentage2Input && this.editModel()) {
        this.discountPercentage2Input.setValue(rowDataDli.discountPercentage2);
        this.discountPercentage2Input.disabled.set(rowDataDli.nonDiscountable);
      }

      if (!skipUnitConversionDropdownSync) {
        this.updateUnitBasedQuantityNumberInput(rowDataDli);

        if (this.unitConversionDropDownComponent) {
          this.unitConversionDropDownComponent.documentLineItem?.update(() => ({ ...rowDataDli }));
        }
      }

      await this.getAccessoryLinksAndSetSignals(e);

      if (e.productType === ProductType.TRADINGSET) {
        const doc = this.document();
        if (doc) {
          this.activeProductPriceInfoFilter = null;
          await this.loadProductPriceInfosIfNeeded(rowDataDli, doc);
          const infosAfterLoad = this.activeProductPriceInfos() ?? [];
          const hasListOrCustomerPrice = infosAfterLoad.some(
            (x) => x.sourceType === PriceSourceType.PRICELIST || x.sourceType === PriceSourceType.CUSTOMERPRICE,
          );
          rowDataDli.isCalculatedItemPrice = !hasListOrCustomerPrice;
          const priceDisabled = this.itemPriceControlDisabled(rowDataDli);
          if (this.productPriceInfoComponent) {
            this.productPriceInfoComponent.lineItem.set(rowDataDli);
            this.productPriceInfoComponent.disabled.set(priceDisabled);
            if (rowDataDli.itemPrice == null) {
              this.productPriceInfoComponent.preSelectByCustomerOrListPrice();
            } else {
              this.productPriceInfoComponent.writeValue(rowDataDli.itemPrice);
              this.productPriceInfoComponent.autoSelectPriceSource(rowDataDli.itemPrice, rowDataDli.quantity ?? 1);
            }
          } else if (rowDataDli.itemPrice == null) {
            const chosen = pickPreferredProductPriceInfo(infosAfterLoad);
            if (chosen) {
              const usedPrice = chosen.unitConversionPrice ?? chosen.price;
              rowDataDli.itemPrice = usedPrice;
              rowDataDli.document = this.documentWithoutLineItems;
              rowDataDli.priceIncludesTax = chosen.priceIncludesTax;
              const switchedToCustomerPrice =
                chosen.sourceType === PriceSourceType.CUSTOMERPRICE &&
                rowDataDli.priceSource !== PriceSourceType.CUSTOMERPRICE;
              const isPurchase = direction === DocumentDirection.IN;
              rowDataDli.priceSource = chosen.sourceType;
              // Purchase vendor prices are tagged CUSTOMERPRICE — do not clear Einkaufskonditionen.
              if (switchedToCustomerPrice && !isPurchase) {
                clearLineItemDiscountPercentages(rowDataDli);
              }
              CalculateDocumentLineItemPrices(rowDataDli, this.document());
              rowDataDli.dataChangedTrigger.next();
              if (this.editModel() && Object.keys(this.editModel()).length > 0) {
                this.lineItemDataService.setDocumentLineItem(rowDataDli);
                this.editModel.update((dli) => {
                  dli.itemPrice = usedPrice;
                  dli.priceSource = chosen.sourceType;
                  dli.priceListBulkQuantity = chosen.quantity;
                  return dli;
                });
              }
              this.lineItemDataService.setProductPriceInfo(chosen);
            }
          }
        }
      } else if (this.productPriceInfoComponent) {
        this.productPriceInfoComponent.disabled.set(this.itemPriceControlDisabled(rowDataDli));
      }

      rowDataDli.dataChangedTrigger.next();
      if (triggerDiscountCheck) {
        this.discountCheckTrigger.next(rowDataDli);
      }

      this.triggerChangeDetection$.next(true);
      this.cdRef.detectChanges();
    } finally {
      this.loadingSig.set(false);
    }
  }

  private suspendUnitConversionValueSync(): () => void {
    this.unitConversionValueSyncSuspended++;
    return () => {
      this.unitConversionValueSyncSuspended = Math.max(0, this.unitConversionValueSyncSuspended - 1);
    };
  }

  /**
   * After a scanId match via {@link DliProductDropDownComponent}, applies the first conversion link's product unit.
   */
  private applyProductUnitConversionLinkFromScan(
    rowData: DocumentLineItemDisplayClass,
    link: ProductUnitConversionLink,
    product: Product,
    conversionLinks: ProductUnitConversionLink[],
  ): void {
    const targetUnit = link.unitConversion?.targetUnit;
    if (!targetUnit?.id) {
      return;
    }

    const rate = link.unitConversion?.rate ?? 1;
    const baseUnit = product.unit;

    if (!rowData.document) {
      rowData.document = this.document();
    }

    rowData.unit = targetUnit;
    rowData.unitIdentifier = targetUnit.identifier;
    rowData.unitId = targetUnit.id;
    rowData.baseUnit = baseUnit ?? null;
    rowData.baseUnitId = baseUnit?.id ?? null;
    rowData.quantityToBaseUnitFactor = rate;

    this.updateUnitBasedQuantityNumberInput(rowData);

    if (this.editModel() && Object.keys(this.editModel()).length > 0) {
      this.editModel.update(dli => {
        dli.unit = targetUnit;
        dli.unitIdentifier = targetUnit.identifier;
        dli.unitId = targetUnit.id;
        dli.baseUnit = baseUnit ?? null;
        dli.baseUnitId = baseUnit?.id ?? null;
        dli.quantityToBaseUnitFactor = rate;
        return dli;
      });
      this.lineItemDataService.setDocumentLineItem(rowData);
    }

    if (this.unitConversionDropDownComponent) {
      const uc = this.unitConversionDropDownComponent;
      uc.productUnitConversionLinks.set(conversionLinks);
      uc.origProductId.set(product.id);
      uc.origAddressId.set(rowData.document?.addressId ?? null);
      uc.origUnitId.set(targetUnit.id);
      uc.origQuantity.set(rowData.quantity ?? 1);
      uc.documentLineItem.set({ ...rowData });
      uc.calculateAndSetConversions(uc.documentLineItem());
      const selectedConversion = uc.value() as ConversionResult;
      this.activeUnitConversions.set(uc.items() as ConversionResult[]);
      this.activeUnitConversionValue.set(selectedConversion);
      if (selectedConversion) {
        this.cachedLastUnitConversion.set({ ...selectedConversion });
      }
    }

    this.refreshProductPriceInfosPerConversionRate(rowData);
    if (rowData.priceSource !== PriceSourceType.MANUALPRICE && this.productPriceInfoComponent) {
      this.productPriceInfoComponent.preSelectByCustomerOrListPrice();
    }
    rowData.dataChangedTrigger.next();
  }

  /**
   * Applies metadata.unitConversions from the active conversion dropdown (single source of truth on save).
   */
  private applyActiveUnitConversionsMetadata(rowData: DliEx): void {
    const conversions = this.activeUnitConversions();
    if (!conversions?.length || rowData.id !== this.editModel()?.id) {
      return;
    }

    const activeUnitId = rowData.unit?.id ?? rowData.unitId;
    const unitConversions = buildUnitConversionsMetadataFromItems(
      conversions.map(c => ({
        quantity: c.unit?.id === activeUnitId
          ? (c.displayQuantity ?? c.quantity)
          : c.quantity,
        unit: c.unit,
      })),
      {
        roundUnitId: activeUnitId,
        decimalPlaces: rowData.unit?.decimalPlaces ?? rowData.decimalPlaces ?? this.document()?.decimalPlaces,
      },
    );

    rowData.metadata = {
      ...(rowData.metadata ?? {}),
      unitConversions,
    };
  }

  private resolveQuantityDecimalPlaces(rowData: DocumentLineItemDisplayClass): number {
    const unitDecimalPlaces = rowData.unit?.decimalPlaces;
    if (unitDecimalPlaces != null && unitDecimalPlaces !== 0) {
      return unitDecimalPlaces;
    }

    const hasUnit = !!(rowData.unit?.id ?? rowData.unitId);
    if (!hasUnit) {
      return (
        this.decimalInput ??
        this.document()?.decimalPlaces ??
        DEFAULT_DECIMAL_PRECISION
      );
    }

    return (
      rowData.decimalPlaces ??
      this.decimalInput ??
      this.document()?.decimalPlaces ??
      DEFAULT_DECIMAL_PRECISION
    );
  }

  private applyQuantityDecimalPlacesToInput(
    input: StorageQuantityNumberInputComponent,
    rowData: DocumentLineItemDisplayClass,
  ): void {
    const decimalPlaces = this.resolveQuantityDecimalPlaces(rowData);
    const fractionDigits = xDecimalPlacesAsFractionDigits(decimalPlaces);
    input.decimalPlaces.set(decimalPlaces);
    input.minFractionDigits.set(fractionDigits);
    input.maxFractionDigits.set(fractionDigits);
  }

  private applyQuantityDecimalPlacesToPhxInput(
    input: PhxInputNumberComponent,
    rowData: DocumentLineItemDisplayClass,
  ): void {
    const fractionDigits = xDecimalPlacesAsFractionDigits(this.resolveQuantityDecimalPlaces(rowData));
    input.minFractionDigits.set(fractionDigits);
    input.maxFractionDigits.set(fractionDigits);
    input.useGrouping.set(false);
  }

  /**
   * Open quantity edits shift the line quantity by the same delta.
   * Saved lines need a numeric quantity as delta base; an unsaved line has no transferred quantity yet,
   * so its open quantity equals its quantity and is editable as soon as a product or quantity is set.
   */
  private canEditOpenQuantity(rowData: DocumentLineItemDisplayClass): boolean {
    if (!rowData || rowData.type === DocumentLineItemType.TEXT) {
      return false;
    }
    if (this.isPersistedDocumentLineItem(rowData)) {
      return typeof rowData.quantity === 'number';
    }
    return !!(rowData.productId ?? rowData.product?.id) || typeof rowData.quantity === 'number';
  }

  /** Shows the delta hint on the open quantity editor while it is editable. */
  private applyOpenQuantityEditTooltip(canEdit: boolean): void {
    afterNextRender(() => {
      const inputGroup = document
        .querySelector('[data-testid="table-column-openQuantity"]')
        ?.closest('.phx-input-number-group');
      if (!inputGroup) {
        return;
      }
      if (canEdit) {
        inputGroup.setAttribute('title', this.i18n.translate(_('document-line-item.open-quantity-edit-tooltip')));
      } else {
        inputGroup.removeAttribute('title');
      }
    }, { injector: this.injector });
  }

  private resolveDisplayOpenQuantity(rowData: DocumentLineItemDisplayClass): number {
    if (rowData.openQuantity != null) {
      return rowData.openQuantity;
    }
    return rowData.quantity ?? 0;
  }

  private areQuantityValuesEqual(
    a: number,
    b: number,
    rowData: DocumentLineItemDisplayClass,
  ): boolean {
    const decimalPlaces = this.resolveQuantityDecimalPlaces(rowData);
    return xCalculateRoundPlaces(a, decimalPlaces) === xCalculateRoundPlaces(b, decimalPlaces);
  }

  private areOptionalQuantityValuesEqual(
    a: number | null,
    b: number | null,
    rowData: DocumentLineItemDisplayClass,
  ): boolean {
    if (a == null || b == null) {
      return a == null && b == null;
    }
    return this.areQuantityValuesEqual(a, b, rowData);
  }

  /** Quantity already transferred to follow-up documents — the part the open quantity cannot touch. */
  private resolveTransferredQuantity(rowData: DocumentLineItemDisplayClass): number {
    if (rowData?.transferredQuantity != null) {
      return xCalculateRoundPlaces(rowData.transferredQuantity, this.resolveQuantityDecimalPlaces(rowData));
    }
    if (rowData?.quantity == null || rowData?.openQuantity == null) {
      return 0;
    }
    return xCalculateRoundPlaces(rowData.quantity - rowData.openQuantity, this.resolveQuantityDecimalPlaces(rowData));
  }

  private calculateOpenQuantityFromQuantity(
    rowData: DocumentLineItemDisplayClass,
    quantity: number | null,
    transferredQuantity: number,
  ): number | null {
    if (quantity == null) {
      return null;
    }
    return xCalculateRoundPlaces(quantity - transferredQuantity, this.resolveQuantityDecimalPlaces(rowData));
  }

  /** Follows a quantity change; the quantity itself is already up to date, so nothing is pushed back. */
  private writeOpenQuantityToLineItem(rowData: DocumentLineItemDisplayClass, newOpen: number | null): void {
    if (rowData.openQuantity === newOpen) {
      return;
    }
    rowData.openQuantity = newOpen;
    if (this.editModel() && Object.keys(this.editModel()).length > 0) {
      this.editModel.update(dli => {
        dli.openQuantity = newOpen;
        return dli;
      });
    }
  }

  /** Applies an open quantity entry and moves the quantity by the same delta. */
  private applyOpenQuantityToLineItem(
    rowData: DocumentLineItemDisplayClass,
    newOpen: number,
    transferredQuantity: number,
  ): void {
    const newQty = xCalculateRoundPlaces(newOpen + transferredQuantity, this.resolveQuantityDecimalPlaces(rowData));
    if (rowData.openQuantity === newOpen && rowData.quantity === newQty) {
      return;
    }

    rowData.quantity = newQty;
    rowData.openQuantity = newOpen;
    this.selectedQuantitySig.set(newQty);

    if (this.storageQuantityNumberInput) {
      if (this.storageQuantityNumberInput.quantityStep()) {
        this.storageQuantityNumberInput.setQuantityByQuantityStep(this.storageQuantityNumberInput.quantityStep());
      } else {
        this.storageQuantityNumberInput.model.set(newQty);
      }
    }

    CalculateDocumentLineItemPrices(rowData, this.document());

    if (this.unitConversionDropDownComponent) {
      this.unitConversionDropDownComponent.documentLineItem.set(rowData);
      const selectedUnitConversion = {
        ...this.unitConversionDropDownComponent.value(),
        quantity: newQty,
      } as ConversionResult;
      this.unitConversionDropDownComponent.checkAndRecalculateIfQuantityChanged(selectedUnitConversion, true);
    }

    if (this.editModel() && Object.keys(this.editModel()).length > 0) {
      this.lineItemDataService.setDocumentLineItem(rowData);
      this.editModel.update(dli => {
        dli.quantity = newQty;
        dli.openQuantity = newOpen;
        return dli;
      });
    }

    rowData.dataChangedTrigger.next();
    this.cdRef.markForCheck();
  }

  /**
   * Validates the entered open quantity on blur and restores the last valid one when it is rejected.
   * @returns the open quantity the editor has to show
   */
  private commitOpenQuantityEdit(
    rowData: DocumentLineItemDisplayClass,
    newOpen: number,
    editState: OpenQuantityEditState,
  ): number {
    const { transferredQuantity, initialOpen, lastValidOpen } = editState;

    const rejectEntry = (message: string, messageParams?: Record<string, unknown>): number => {
      if (message) {
        this.baseServices.notificationService.warning(this.i18n.translate(message, messageParams));
      }
      this.applyOpenQuantityToLineItem(rowData, lastValidOpen, transferredQuantity);
      editState.lastQuantity = rowData.quantity ?? null;
      return lastValidOpen;
    };

    if (newOpen == null || typeof newOpen !== 'number' || Number.isNaN(newOpen)) {
      return rejectEntry(null);
    }

    // Unsaved lines have no follow-up documents, so their sign is still free to choose.
    const isSignChangeBlocked = this.isPersistedDocumentLineItem(rowData)
      && ((initialOpen >= 0 && newOpen < 0) || (initialOpen < 0 && newOpen > 0));
    if (isSignChangeBlocked) {
      return rejectEntry(_('document-line-item.open-quantity-sign-not-allowed'));
    }

    const newQty = xCalculateRoundPlaces(newOpen + transferredQuantity, this.resolveQuantityDecimalPlaces(rowData));
    const isNegativeLine = lastValidOpen + transferredQuantity < 0;
    const belowTransferred = isNegativeLine ? newOpen > 0 : newOpen < 0;

    if (belowTransferred && transferredQuantity !== 0) {
      return rejectEntry(_('document-line-item.open-quantity-below-transferred'), {
        quantity: newQty,
        transferredQuantity,
      });
    }

    editState.lastValidOpen = newOpen;
    this.applyOpenQuantityToLineItem(rowData, newOpen, transferredQuantity);
    editState.lastQuantity = rowData.quantity ?? null;

    return newOpen;
  }

  /**
   * @description Updates the quantity number input based on the unit (decimalPlaces and quantityStep)
   */
  updateUnitBasedQuantityNumberInput(rowData: DocumentLineItemDisplayClass) {

    if (this.storageQuantityNumberInput) {
      const unit = rowData.unit;
      if (unit?.quantityStep) {
        this.storageQuantityNumberInput.quantityStep.set(unit.quantityStep);
      } else {
        this.storageQuantityNumberInput.quantityStep.set(undefined);
      }

      this.applyQuantityDecimalPlacesToInput(this.storageQuantityNumberInput, rowData);
    }
  }

  private isPersistedDocumentLineItem(item: DocumentLineItem | null | undefined): boolean {
    return !!item?.id && item.id !== this.newId;
  }

  /** Saved lines keep their unit price, including 0. New lines without a price still take the list price. */
  private shouldKeepSavedItemPrice(item: DocumentLineItem | null | undefined): boolean {
    return this.isPersistedDocumentLineItem(item) || item?.itemPrice != null;
  }

  private getAccessoryBasePositionNumber(parentLineItem: DocumentLineItem): number {
    const relatedItems = this.itemsSig().filter(
      (item) => item.id === parentLineItem.id || item.parentId === parentLineItem.id,
    );
    return relatedItems.reduce(
      (max, item) => Math.max(max, item.positionNumber ?? 0),
      parentLineItem.positionNumber ?? 0,
    );
  }

  private async saveAccessoriesForParentLine(
    parentLineItem: DocumentLineItem,
    accessoryConfigs: AccessoryConfig[],
  ): Promise<DocumentLineItem[]> {
    if (!accessoryConfigs?.length || !parentLineItem?.id) {
      return [];
    }

    const existingAccessories = await this.getExistingAccessoryDLIs(parentLineItem as DocumentLineItemDisplayClass);
    const existingConfigIds = new Set(existingAccessories.map((item) => item.accessoryConfigId));
    const configsToAdd = accessoryConfigs.filter(
      (config) => config?.id && !existingConfigIds.has(config.id),
    );
    if (!configsToAdd.length) {
      return [];
    }

    let accessoryItems = simpleDeepClone(
      await this.dliService.getLineItemsFromAccessoryConfig(configsToAdd, parentLineItem, this.document()),
    );

    for (let i = 0; i < accessoryItems.length; i++) {
      if (!accessoryItems[i]?.product) {
        const product: Product = await this.productService.getItemData(accessoryItems[i].productId);
        accessoryItems[i].product = {
          id: product.id,
          identifier: product.identifier,
          description: product.description,
          productType: product.productType,
        } as ProductInput;
      }

      const discounts = await this.dliService.getApplicableDiscountsForDocumentLineItem({
        ...accessoryItems[i],
        document: this.document(),
      });
      if (isLineItemDiscountPercentagesUnset(accessoryItems[i])) {
        accessoryItems[i].discountPercentage = discounts.discountPercentage1 ?? 0;
        accessoryItems[i].discountPercentage2 = discounts.discountPercentage2 ?? 0;
      }
    }

    const accessoryBasePosition = this.getAccessoryBasePositionNumber(parentLineItem);
    const accessoryItemsToSave = accessoryItems.map((item, accessoryIndex) => {
      const storagePlaceId = item.id
        ? item.storagePlaceId
        : this.document()?.storagePlace?.id || this.document()?.storagePlaceId;
      const productType = item.product?.productType;
      const isStorableFromProduct =
        productType === ProductType.STORE || productType === ProductType.SIMPLEPRODUCTION;
      return {
        ...item,
        type: DocumentLineItemType.AI,
        isStorable: item.isStorable ?? isStorableFromProduct,
        storagePlaceId,
        documentId: this.document()?.id,
        frozenPositionText: false,
        positionNumber: accessoryBasePosition + accessoryIndex + 1,
      };
    });

    return this.dliService.saveItems(accessoryItemsToSave, {
      skipAutoAccessory: true,
      saveWithFailedQuantity: true,
      recalculateAccessoryPrice: true,
    });
  }

  private async addAccessoriesToPersistedParentLine(
    parentLineItem: DocumentLineItemDisplayClass,
    accessoryConfigs: AccessoryConfig[],
  ): Promise<void> {
    if (!accessoryConfigs?.length) {
      return;
    }

    try {
      let parent = parentLineItem;
      let endedEdit = false;

      if (this.inEditingSig() && this.editModel()?.id === parent.id) {
        const savedParentResult = await this.dliService.saveItemEx(
          this.editModel() as DocumentLineItemInput,
          { skipAutoAccessory: true },
        );
        if (savedParentResult?.item) {
          parent = savedParentResult.item as DocumentLineItemDisplayClass;
        }
        await this.endLineItemEditRow(parent);
        endedEdit = true;
      }

      const savedAccessories = await this.saveAccessoriesForParentLine(parent, accessoryConfigs);
      if (!savedAccessories.length) {
        if (endedEdit) {
          await this.refreshAfterAccessoriesSaved([]);
        }
        this.baseServices.notificationService.info(
          this.i18n.translate(_('document-line-item.add-accessory-none-new')),
        );
        return;
      }

      await this.refreshAfterAccessoriesSaved(savedAccessories);
      this.baseServices.notificationService.success(_('document-line-item.add-accessory-success'));
    } catch (error) {
      console.error(`Failed to add accessories for parent line item id: ${parentLineItem.id}`, error);
    }
  }

  private async endLineItemEditRow(rowToClose: DocumentLineItemDisplayClass): Promise<void> {
    if (rowToClose?.id) {
      delete this.clonedItems[rowToClose.id as string];
    }
    rowToClose?.currentlyInEditMode?.set(false);
    await this.onRowEditCancel(rowToClose);
  }

  /** After accessories are persisted: update cache and reload the list. */
  private async refreshAfterAccessoriesSaved(savedDLIs: DocumentLineItem[]): Promise<void> {
    if (savedDLIs.length) {
      this.addNewItemToDocumentApolloCache(savedDLIs);
    }

    this.table().editingRowKeys = {};
    this.inEditingSig.set(false);
    this.editModel.set(null);

    await this.afterDocumentLineItemsMutated();
    await this.refresh(true);
    this.itemsChanged.emit(this.itemsSig());
    this.refreshToolbarButtons();
    this.cdRef.markForCheck();
  }

  async getExistingAccessoryDLIs(dli: DocumentLineItemDisplayClass) {
    const searchInput: SearchInput = {
      searchFilter: {
        rules: [
          {
            field: 'parentId',
            operator: Operator.equal,
            value: dli.id
          },
          {
            field: 'accessoryConfigId',
            operator: Operator.isnotnull
          },
        ]
      }
    }
    return (await lastValueFrom(this.dliService.getItems(searchInput).single$)).getDocumentLineItems.items as DocumentLineItem[];
  }

  private async refreshTradingSetGroup(parentId: string): Promise<void> {
    if (!parentId) {
      return;
    }

    try {
      const [parentFresh, childrenFresh] = await Promise.all([
        this.dliService.getItemData(parentId),
        (async () => {
          const searchInput: SearchInput = {
            take: 500,
            searchFilter: {
              rules: [
                {
                  field: 'parentId',
                  operator: Operator.equal,
                  value: parentId,
                },
              ],
            },
          };
          return (await lastValueFrom(this.dliService.getItems(searchInput).single$)).getDocumentLineItems
            .items as DocumentLineItem[];
        })(),
      ]);

      const freshById = new Map<string, DocumentLineItem>();
      if (parentFresh?.id) {
        freshById.set(parentFresh.id, parentFresh);
      }
      for (const ch of childrenFresh ?? []) {
        if (ch?.id) {
          freshById.set(ch.id, ch);
        }
      }

      if (freshById.size === 0) {
        return;
      }

      this.itemsSig.update((items) => {
        const next = [...items] as DliEx[];
        for (let i = 0; i < next.length; i++) {
          const fresh = freshById.get(next[i]?.id);
          if (!fresh) {
            continue;
          }
          const merged = { ...next[i], ...fresh } as DliEx;
          this.ensureLineItemDisplaySignalsAndTriggers(merged);
          next[i] = merged;
        }
        return next;
      });

      if (!this.crudOptions.enableStream) {
        this.addNewItemToDocumentApolloCache([parentFresh, ...(childrenFresh ?? [])].filter(Boolean));
      }
    } catch (e) {
      console.error(`Failed to refresh trading-set group (parentId: ${parentId})`, e);
    }
  }

  async getAccessoryLinksAndSetSignals(product: Product) {

    this.resetAccessorySignals();
    // get accessory config product links
    let accessoryConfigProductLinks = await this.accessoryConfigProductLinkService.getAccessoryLinksForProduct(product.id);
    this.setAccessorySignals(accessoryConfigProductLinks);
    let ask = this.accessoryConfigAskSig();
    if (ask.length > 0 && this.newId) {
      this.showAccessoryList();
    }
    else
      this.accessoryConfigAppliedSig.set(this.accessoryConfigAutoSig());
  }

  setAccessorySignals(accessoryConfigProductLinks: AccessoryConfigProductLink[]) {
    for (let link of accessoryConfigProductLinks) {
      if (link.accessoryConfig?.direction === AccessoryAppliedOnDirection.BOTH ||
        link.accessoryConfig?.direction as string === this.document()?.documentDefinition?.documentDirection as string) { // cast as string to compare different enums with each other
        switch (link.accessoryConfig.insertMode) {
          // manual, and ask
          case AccessoryInsertMode.AUTO:
            this.accessoryConfigAutoSig.set([...this.accessoryConfigAutoSig(), link.accessoryConfig]);
            break;
          case AccessoryInsertMode.ASK:
            this.accessoryConfigAskSig.set([...this.accessoryConfigAskSig(), link.accessoryConfig]);
            break;
          case AccessoryInsertMode.MANUAL:
            this.accessoryConfigManualSig.set([...this.accessoryConfigManualSig(), link.accessoryConfig]);
            break;
          default:
            break;
        }
      }
    }
  }

  setAppliedAccessorySignal(accessoryConfigProductLinks: AccessoryConfigProductLink[], force: boolean = false) {
    // empty applied accessories, because here it gets applied again together with all else
    this.accessoryConfigAppliedSig.set([]);
    if (accessoryConfigProductLinks?.length > 0)
      for (let link of accessoryConfigProductLinks) {
        if (force || (link.accessoryConfig?.direction === AccessoryAppliedOnDirection.BOTH ||
          link.accessoryConfig?.direction as string === this.document()?.documentDefinition?.documentDirection as string)) { // cast as string to compare different enums with each other
          this.accessoryConfigAppliedSig.set([...this.accessoryConfigAppliedSig(), link.accessoryConfig]);
        }
      }
  }

  resetAccessorySignals() {
    this.accessoryConfigAutoSig.set([]);
    this.accessoryConfigAskSig.set([]);
    this.accessoryConfigManualSig.set([]);
    this.accessoryConfigAppliedSig.set([]);
  }

  /**
   * 
   * @param field 
   * @param rowData 
   * @param data 
   */
  public dataChanged(field: string, rowData: DocumentLineItemDisplayClass, data: Partial<DocumentLineItemDisplayClass>): void {
    super.dataChanged(field, rowData, data);
    this.editModel.set(rowData);
    if (isContractRelatedDataChange(field) && shouldShowContractLineItemFields(this.document(), rowData)) {
      rowData.document = this.documentWithoutLineItems;
      CalculateDocumentLineItemPrices(rowData, this.document());
      if (this.editModel() && Object.keys(this.editModel()).length > 0) {
        this.lineItemDataService.setDocumentLineItem(rowData);
      }
    }
    rowData.dataChangedTrigger.next();
  }

  async rowReorder(rowReorder: TableRowReorderEvent) {

    //calculate drag and drop index per page - since per event they dont have pagination
    const dragIndex = rowReorder.dragIndex - this.table().first;
    const dropIndex = rowReorder.dropIndex - this.table().first;
    const draggedItem = this.selectedReorderItemSig();

    // Skip validation if dropping at the same position
    if (rowReorder.dragIndex === dropIndex) {
      return;
    }

    let selectedItems: DocumentLineItemDisplayClass[] = []
    selectedItems.push(draggedItem);
    let direction = dropIndex - dragIndex;

    await this.changeItemPosition(selectedItems, direction, { immediate: true });

    this.selectedReorderItemSig.set(null);
  }

  async recalculatePositionTextOfDocumentLineItems() {

    this.setLoading();
    let res = await this.dliService.recalculatePositionTextOfDocumentLineItems(this.document().id);

    let toUpdate = [];

    //we got all new position text, update existing items
    for (let item of res) {
      let bp = this.itemsSig().find(x => x.id === item.id);
      if (bp) {
        bp.positionText = item.positionText;
        bp.positionNumber = item.positionNumber;
        toUpdate.push(bp);
      }
    }

    this.itemsSig.update(x => { return toUpdate });

    this.setLoading(false);
  }

  async changeItemPosition(
    selectedItems: DocumentLineItemDisplayClass[],
    delta: number,
    options?: { immediate?: boolean },
  ): Promise<void> {
    if (!selectedItems?.length) {
      return;
    }

    const debounceThisCall =
      !options?.immediate &&
      selectedItems.length === 1 &&
      (delta === 1 || delta === -1);

    if (!debounceThisCall) {
      await this.flushPendingPositionReorder();
      await this.executePositionReorderImmediate(selectedItems, delta);
      return;
    }

    const documentId = this.document()?.id;
    if (!documentId) {
      return;
    }

    const item = selectedItems[0];

    if (this.pendingPositionReorder && this.pendingPositionReorder.itemId !== item.id) {
      await this.flushPendingPositionReorder();
    }

    if (
      this.pendingPositionReorder &&
      this.pendingPositionReorder.documentId !== documentId
    ) {
      await this.flushPendingPositionReorder();
    }

    const positionNumberBeforeStep = item.positionNumber;
    const applied = this.applyOptimisticLineItemPositionStep(item, delta);
    if (!applied) {
      return;
    }

    if (!this.pendingPositionReorder) {
      this.pendingPositionReorder = {
        documentId,
        itemId: item.id,
        positionNumberAtBatchStart: positionNumberBeforeStep,
        accumulatedDelta: 0,
      };
    }

    this.pendingPositionReorder.accumulatedDelta += delta;
    this.schedulePositionReorderFlush();
  }

  private schedulePositionReorderFlush(): void {
    if (this.positionReorderFlushTimer != null) {
      clearTimeout(this.positionReorderFlushTimer);
    }
    this.positionReorderFlushTimer = setTimeout(() => {
      this.positionReorderFlushTimer = null;
      void this.flushPendingPositionReorder();
    }, this.positionReorderDebounceMs);
  }

  /**
   * Applies one ↑/↓ step in the current table order by swapping `positionNumber` / `positionText` with the neighbour row.
   * @returns false if the neighbour does not exist (already at top/bottom).
   */
  private applyOptimisticLineItemPositionStep(
    item: DocumentLineItemDisplayClass,
    delta: number,
  ): boolean {
    if (delta !== 1 && delta !== -1) {
      return false;
    }

    const ascending = this.tableAscendingSig();
    const sortFn = (a: DliEx, b: DliEx) =>
      ascending ? a.positionNumber - b.positionNumber : b.positionNumber - a.positionNumber;

    const items = this.itemsSig();
    const ordered = [...items].sort(sortFn);
    const idx = ordered.findIndex((x) => x.id === item.id);
    if (idx < 0) {
      return false;
    }
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= ordered.length) {
      return false;
    }

    const rowA = ordered[idx];
    const rowB = ordered[swapIdx];
    const tempNum = rowA.positionNumber;
    rowA.positionNumber = rowB.positionNumber;
    rowB.positionNumber = tempNum;
    const tempText = rowA.positionText;
    rowA.positionText = rowB.positionText;
    rowB.positionText = tempText;

    this.itemsSig.set([...items].sort(sortFn));
    this.table().value = this.itemsSig();
    return true;
  }

  private async flushPendingPositionReorder(): Promise<void> {
    if (this.positionReorderFlushTimer != null) {
      clearTimeout(this.positionReorderFlushTimer);
      this.positionReorderFlushTimer = null;
    }

    const pending = this.pendingPositionReorder;
    if (!pending) {
      return;
    }
    this.pendingPositionReorder = null;

    if (pending.accumulatedDelta === 0) {
      return;
    }

    if (pending.documentId !== this.document()?.id) {
      this.setLoading(true);
      try {
        await this.refresh(true);
        this.reRenderTable();
      } finally {
        this.setLoading(false);
      }
      return;
    }

    this.setLoading(true);
    try {
      let res = await this.dliService.reorderDocumentLineItems(
        pending.documentId,
        [pending.positionNumberAtBatchStart],
        pending.accumulatedDelta,
      );
      res = simpleDeepClone(res);

      this.itemsSig.update((x) => {
        for (const item of res) {
          const index = x.findIndex((y) => y.id === item.id);
          if (index !== -1) {
            x[index].positionNumber = item.positionNumber;
            x[index].positionText = item.positionText;
          }
        }
        if (this.tableAscendingSig()) {
          return x.sort((a, b) => a.positionNumber - b.positionNumber);
        }
        return x.sort((a, b) => b.positionNumber - a.positionNumber);
      });
      this.updateApolloCache();
      this.notifyDocumentLineItemsChanged();
    } catch (error: unknown) {
      console.error(error);
      this.notifyLineItemMutationError(error);
    } finally {
      await this.refresh(true);
      this.reRenderTable();
      this.setLoading(false);
    }
  }

  private async executePositionReorderImmediate(
    selectedItems: DocumentLineItemDisplayClass[],
    delta: number,
  ): Promise<void> {
    this.setLoading(true);
    try {
      let res = await this.dliService.reorderDocumentLineItems(
        this.document().id,
        selectedItems.map((x) => x.positionNumber),
        delta,
      );
      res = simpleDeepClone(res);

      this.itemsSig.update((x) => {
        for (const item of res) {
          const index = x.findIndex((y) => y.id === item.id);
          if (index !== -1) {
            x[index].positionNumber = item.positionNumber;
            x[index].positionText = item.positionText;
          }
        }
        if (this.tableAscendingSig()) {
          return x.sort((a, b) => a.positionNumber - b.positionNumber);
        }
        return x.sort((a, b) => b.positionNumber - a.positionNumber);
      });
      this.updateApolloCache();
      this.notifyDocumentLineItemsChanged();
    } catch (error: unknown) {
      console.error(error);
      this.notifyLineItemMutationError(error);
    } finally {
      await this.refresh(true);
      this.reRenderTable();
      this.setLoading(false);
    }
  }

  private reRenderTable() {
    const totalRecords = this.table().totalRecords; //otherwise incorrect value
    const first = this.table().first;

    this.table().value = this.itemsSig();
    this.triggerChangeDetection$.next(true);
    this.table().totalRecords = totalRecords;  //without this no re-rendering of table without refresh
    this.table().first = first;
    // quickfix for disappearing items - find better solution
    this.refresh();
  }

  async recalcPositionTextAndRefresh() {
    this.setLoading(true);
    await this.dliService.recalculatePositionTextOfDocumentLineItems(this.document().id);
    this.setLoading(false);
    await this.refresh();
  }

  override onRowSelect(event: TableRowSelectEvent): void {

    // THIS creates duplicates in the selection
    // let selectedItems = [...this.selectedItems(), event.data];
    // let finalSelection: DocumentLineItemDisplayClass[] = [...selectedItems];
    // this.setSelectedItems(finalSelection);

    this.handleClickInRowSelect(event);

    this.addToSelectedItems([event.data]);
    this.lastSelectedItemSig.set(event.data);

    this.handleEditingInRowSelect(event);
  }

  /**
   * get the level starting by 0 of the dli -> 1,2,3,4 would be level 0, 1.1,2.1,2.2,3.1 would be level 1 and so on
   * @param dli Line item row used for position text / parent chain.
   * @returns Dot depth from `positionText` (0 when not nested).
   */
  getLevelOfDLI(dli: DocumentLineItemDisplayClass): number {
    if (!dli)
      return 0;
    else if (!dli.positionText && dli.parentId)
      return 1;
    else if (!dli.parentId || !dli.positionText)
      return 0;
    else {
      let dotCount = dli.positionText.split('.').length - 1;
      return dotCount;
    }
  }

  /**
   * get all following items in direction in UI list - in the direction
   * @param dliId Current line item id.
   * @param direction only use 1 or -1 to specify the direction
   * @returns Following rows in list order, or undefined when direction is invalid.
   */
  getFollowingItemsInDirection(dliId: string, direction: number): DocumentLineItemDisplayClass[] | undefined {
    if (direction !== 1 && direction !== -1)
      return undefined;

    const items = this.itemsSig();
    const selectedItemIndex = items.findIndex(x => x.id === dliId);

    if (selectedItemIndex === -1)
      return undefined;

    // all items after the selected item in direction
    let followingItems: DocumentLineItemDisplayClass[] = [];
    if (direction === 1)
      followingItems = items.slice(selectedItemIndex + 1);
    else if (direction === -1)
      followingItems = items.slice(0, selectedItemIndex);

    let filteredFollowingItems = followingItems.filter(item => item != null);
    if (filteredFollowingItems.length != followingItems.length) {
      console.error('followingItems contain null/undefined values - should not happen');
      let nullItemsInItemsSig = items.filter(item => item == null);
      if (nullItemsInItemsSig.length > 0)
        console.error('itemsSig contains null/undefined values - should not happen');
    }

    for (let item of filteredFollowingItems) {
      item.level = this.getLevelOfDLI(item);
    }
    return filteredFollowingItems;

  }

  refreshToolbarButtons(): void {
    const selectedItems: DocumentLineItemDisplayClass[] = this.selectedItems() ?? [];
    // Toolbar trash in base-list-wrapper binds [disabled] to disabledDelete.
    this.disabledDelete =
      selectedItems.length === 0 || this.hasBlockedLineItems(selectedItems);

    // region up / down movement
    if (selectedItems.length !== 1) {
      this.canMoveUpSig.set(false);
      this.canMoveDownSig.set(false);
      this.canIndentSignal.set(false);
      this.canOutdentSignal.set(false);
      return;
    }

    const selectedItem: DocumentLineItemDisplayClass = selectedItems[0];
    selectedItem.level = this.getLevelOfDLI(selectedItem);

    if (!this.itemsSig().some(x => x.id === selectedItem.id))
      return;

    const nextItems = this.getFollowingItemsInDirection(selectedItem.id, 1);
    const previousItems = this.getFollowingItemsInDirection(selectedItem.id, -1);

    let nextItemLevel = nextItems ? nextItems[0]?.level : undefined;
    let previousItemLevel = previousItems ? previousItems[previousItems.length - 1]?.level : undefined;

    let previousItem = previousItems ? previousItems[previousItems.length - 1] : undefined;

    //handling of TEXT items and others with no posText
    if (previousItemLevel > selectedItem.level && selectedItem.level > 0)
      selectedItem.level = previousItemLevel;
    else if (previousItem && selectedItem.parentId === previousItem?.id)
      selectedItem.level = previousItemLevel + 1;

    if (previousItems?.length > 0)
      for (let previousItem of previousItems) {
        if (previousItem?.parentId === selectedItem?.id) {
          previousItemLevel = selectedItem.level;
          previousItem.level = previousItemLevel;
        }
        else if (previousItem.parentId === selectedItem.parentId) {
          previousItemLevel = selectedItem.level;
          previousItem.level = previousItemLevel;
        }
      }
    if (nextItems?.length > 0)
      for (let nextItem of nextItems) {
        if (nextItem?.parentId === selectedItem?.id) {
          nextItemLevel = selectedItem.level + 1;
          nextItem.level = nextItemLevel;
        }
        else if (nextItem.parentId === selectedItem.parentId) {
          nextItemLevel = selectedItem.level;
          nextItem.level = nextItemLevel;
        }
      }

    const nextItemOfSameLevel = nextItems?.find(x => x.level === selectedItem.level);
    const nextItemOfHigherLevel = nextItems?.find(x => x.level < selectedItem.level);
    const previousItemOfSameLevel = previousItems?.find(x => x.level === selectedItem.level);
    const previousItemOfHigherLevel = previousItems?.find(x => x.level < selectedItem.level);

    const canMoveDown =
      nextItemLevel !== undefined &&
      nextItemLevel !== null &&
      nextItemOfSameLevel !== undefined &&
      (nextItemOfHigherLevel ? nextItemOfHigherLevel?.positionNumber > nextItemOfSameLevel?.positionNumber : true) &&
      nextItemLevel >= selectedItem.level;

    const canMoveUp =
      previousItemLevel !== undefined &&
      previousItemLevel !== null &&
      previousItemOfSameLevel !== undefined &&
      (previousItemOfHigherLevel ? previousItemOfHigherLevel?.positionNumber < previousItemOfSameLevel?.positionNumber : true) &&
      previousItemLevel >= selectedItem.level;

    this.canMoveUpSig.set(canMoveUp);
    this.canMoveDownSig.set(canMoveDown);
    // endregion

    //#region indent / outdent

    let canIndent = false;
    let canOutdent = false;

    //for now only when 1 is selected
    if (selectedItems?.length == 1) {
      let selectedItem = selectedItems[0];

      if (selectedItem.positionNumber == 1) { //first item is selected
        canIndent = false;
        canOutdent = false;
      }
      //never first item
      else {
        canIndent = true; //hard to find out in frontend, for now we let it crash / not work in backend
        //our item has a parent, can indent but outdent
        if (selectedItem.parentId) {
          canOutdent = true;
        }
      }
    }

    this.canIndentSignal.set(canIndent);
    this.canOutdentSignal.set(canOutdent);
    //#endregion
  }

  /** Sales Order and other RESERVE + OUT documents: insufficient stock is a reservation limit, not physical picking. */
  private isReserveOutDocument(): boolean {
    const def = this.document()?.documentDefinition;
    return def?.stockAction === StockAction.RESERVE && def?.documentDirection === DocumentDirection.OUT;
  }

  private hasOpenRequestedQuantityGap(item: DocumentLineItem): boolean {
    return item?.requestedQuantity != null
      && item?.quantity != null
      && item.requestedQuantity > item.quantity
      && item.type !== DocumentLineItemType.TEXT;
  }

  async fulfillOpenRequestedQuantityForSelectedItems() {
    if (this.IsLoading) return;

    const items = this.selectedItems().filter(item => this.hasOpenRequestedQuantityGap(item));
    if (items.length === 0) return;

    this.setLoading(true);
    let succeeded = 0;
    let failedCount = 0;

    try {
      for (const item of items) {
        try {
          await this.dliService.saveItemEx({
            id: item.id,
            documentId: item.documentId ?? this.document().id,
            productId: item.productId,
            quantity: item.requestedQuantity,
            requestedQuantity: item.requestedQuantity,
          }, { skipAutoAccessory: true });
          succeeded++;
        } catch (e) {
          failedCount++;
          console.error(`Failed to fulfill requested quantity for line item id: ${item.id}`, e);
        }
      }

      if (succeeded > 0) {
        await this.afterDocumentLineItemsMutated();
        await this.refresh(true);
      }

      if (failedCount === 0) {
        this.baseServices.notificationService.success(
          this.i18n.translate(_('document-line-item.fulfill-requested-quantity-success'), { count: succeeded }),
        );
      } else if (succeeded === 0) {
        this.baseServices.notificationService.error(
          this.i18n.translate(_('document-line-item.fulfill-requested-quantity-all-failed'), { count: failedCount }),
        );
      } else {
        this.baseServices.notificationService.warning(
          this.i18n.translate(_('document-line-item.fulfill-requested-quantity-partial'), { succeeded, failed: failedCount }),
        );
      }
    } finally {
      this.setLoading(false);
    }
  }

  openStockHistoryPopup() {
    const selected = this.lastSelectedItemSig();
    const dliId = selected?.id ?? this.itemsSig()[0]?.id;
    if (!dliId) {
      return;
    }
    this.stockHistoryDliId.set(dliId);
    this.stockHistoryShowValue.set(false);
    this.stockHistoryDrawerOpen.set(true);
    this.cdRef.markForCheck();
    this.cdRef.detectChanges();
  }

  async goToAdjacentStockHistoryDli(delta: -1 | 1): Promise<void> {
    if (this.inEditingSig() || this.stockHistoryNavigating() || this.IsLoading) {
      return;
    }

    const pageIndex = this.stockHistoryDliIndex();
    if (pageIndex < 0) {
      return;
    }

    const tableFirst = this.table()?.first ?? this.currentSearchInput?.skip ?? 0;
    const globalIndex = tableFirst + pageIndex;
    const newGlobalIndex = globalIndex + delta;
    const totalRecords = this.table()?.totalRecords ?? 0;

    if (newGlobalIndex < 0 || newGlobalIndex >= totalRecords) {
      return;
    }

    const rowsPerPage = this.table()?.rows ?? this.currentSearchInput?.take ?? 25;
    const targetSkip = rowsPerPage * Math.floor(newGlobalIndex / rowsPerPage);
    const indexOnPage = newGlobalIndex - targetSkip;
    const currentSkip = this.currentSearchInput?.skip ?? tableFirst;
    const requestId = ++this.stockHistoryNavRequestId;

    if (targetSkip === currentSkip) {
      const items = this.itemsSig();
      const neighbor = items[indexOnPage];
      if (!neighbor?.id) {
        console.warn(
          `Stock history adjacent selection missing at global index ${newGlobalIndex}, page index ${indexOnPage}`
        );
        return;
      }
      this.applyStockHistorySelection(neighbor);
      return;
    }

    this.stockHistoryNavigating.set(true);
    try {
      this.currentSearchInput.skip = targetSkip;
      await this.refresh(true);

      if (requestId !== this.stockHistoryNavRequestId) {
        return;
      }

      this.table().first = targetSkip;
      if (this.lastLazyLoadEvent) {
        this.lastLazyLoadEvent = {
          ...this.lastLazyLoadEvent,
          first: targetSkip,
          rows: rowsPerPage,
        };
      }

      const items = this.itemsSig();
      const neighbor = items[indexOnPage];
      if (!neighbor?.id) {
        console.warn(
          `Stock history cross-page selection missing at global index ${newGlobalIndex}, page index ${indexOnPage}, skip ${targetSkip}`
        );
        return;
      }
      this.applyStockHistorySelection(neighbor);
    } finally {
      if (requestId === this.stockHistoryNavRequestId) {
        this.stockHistoryNavigating.set(false);
        this.cdRef.markForCheck();
      }
    }
  }

  private applyStockHistorySelection(neighbor: DliEx): void {
    if (!neighbor?.id) {
      return;
    }
    this.stockHistoryDliId.set(neighbor.id);
    this.setSelectedItems([neighbor]);
    this.cdRef.markForCheck();
    this.cdRef.detectChanges();
  }

  openLineItemLInksPopup() {
    let first: DocumentLineItem = this.lastSelectedItemSig();
    this.firstSelectedId = first.id;
    this.cdRef.markForCheck();

    const ref = this.baseServices.modalService.fromComponent(WidgetDocumentLineItemLinksComponent, {
      size: 'lg',
      verticalAlign: 'top',
      closable: true,
      maximizable: true,
      title: this.baseServices.i18nService.translate(_("document-line-item.line-item-links")),
      setLocalsSig: {
        dliId: this.firstSelectedId
      }
    });
    let sub = ref.subscribe(x => {
      sub.unsubscribe();
    });

    //for onPush Strategy
    this.cdRef.detectChanges();
  }

  openSelectedItemInNewTab() {
    const lastSelected = this.lastSelectedItemSig();

    if (lastSelected && lastSelected.productId) {
      this.openInNewTab(EditLinks.PRODUCT + lastSelected.productId);
    }
  }

  openWarehouseSidebarForLineItem(dli: DocumentLineItem | null | undefined) {
    const productId = dli?.productId ?? dli?.product?.id;
    if (!dli?.isStorable || !productId) {
      return;
    }
    this.warehouseSidebarProductId.set(productId);
    // Defer until context menu overlay is gone; PrimeNG drawer otherwise measures a near-zero width.
    setTimeout(() => {
      this.productStorageVisible.set(true);
      this.cdRef.detectChanges();
    });
  }

  openSelectedItemWarehouseSidebar() {
    this.openWarehouseSidebarForLineItem(this.lastSelectedItemSig());
  }

  openPriceHistoryDrawer(): void {
    const selected = this.lastSelectedItemSig();
    const productId = selected?.productId ?? selected?.product?.id;
    if (!productId) {
      return;
    }

    const doc = this.document();
    this.priceHistoryProductId.set(productId);
    this.priceHistoryCustomerId.set(doc?.addressId ?? doc?.address?.id ?? undefined);
    this.priceHistoryDocumentDirection.set(
      doc?.documentDefinition?.documentDirection ?? DocumentDirection.OUT,
    );
    this.priceHistoryDocumentType.set(doc?.documentDefinition?.identifier ?? undefined);
    this.priceHistoryLineItemId.set(selected?.id ?? undefined);

    setTimeout(() => {
      this.priceHistoryDrawerOpen.set(true);
      this.cdRef.detectChanges();
    });
  }

  canApplyPriceFromHistory(): boolean {
    if (this.isDocumentLockedSig() || this.IsLoading || !this.inEditingSig()) {
      return false;
    }
    const lineItemId = this.priceHistoryLineItemId();
    if (!lineItemId || this.editModel()?.id !== lineItemId) {
      return false;
    }
    const rowData = this.itemsSig().find((item) => item.id === lineItemId);
    if (!rowData) {
      return false;
    }
    return !this.itemPriceControlDisabled(rowData);
  }

  async onPriceHistoryUnitPriceApply(payload: LineItemPriceHistoryApplyPayload): Promise<void> {
    if (!this.canApplyPriceFromHistory() || payload.unitPrice == null) {
      return;
    }

    const rowData = this.editModel();
    if (!rowData) {
      return;
    }

    this.applyManualUnitPriceToLineItem(rowData, payload);
    this.priceHistoryDrawerOpen.set(false);
    this.cdRef.markForCheck();
    this.triggerChangeDetection$.next(true);
  }

  private applyManualUnitPriceToLineItem(rowData: DliEx, payload: LineItemPriceHistoryApplyPayload): void {
    const { unitPrice, discountPercentage, discountPercentage2 } = payload;
    const doc = this.document();
    const priceIncludesTax = doc?.taxType === TaxType.GROSS || doc?.taxType === TaxType.FREE;

    rowData.itemPrice = unitPrice;
    rowData.priceSource = PriceSourceType.MANUALPRICE;
    rowData.priceIncludesTax = priceIncludesTax;
    rowData.document = this.documentWithoutLineItems;

    if (!rowData.nonDiscountable) {
      rowData.discountPercentage = discountPercentage ?? null;
      rowData.discountPercentage2 = discountPercentage2 ?? null;
    }

    CalculateDocumentLineItemPrices(rowData, doc);

    if (this.editModel()?.id === rowData.id) {
      this.editModel.update((dli) => {
        dli.itemPrice = unitPrice;
        dli.priceSource = PriceSourceType.MANUALPRICE;
        dli.priceIncludesTax = priceIncludesTax;
        if (!dli.nonDiscountable) {
          dli.discountPercentage = discountPercentage ?? null;
          dli.discountPercentage2 = discountPercentage2 ?? null;
        }
        return dli;
      });
    }

    if (this.discountPercentageInput && this.editModel() && !rowData.nonDiscountable) {
      this.discountPercentageInput.setValue(rowData.discountPercentage);
    }
    if (this.discountPercentage2Input && this.editModel() && !rowData.nonDiscountable) {
      this.discountPercentage2Input.setValue(rowData.discountPercentage2);
    }

    this.lineItemDataService.setDocumentLineItem(rowData);
    rowData.dataChangedTrigger?.next();
    this.itemsChanged.emit(this.itemsSig());
  }

  async doManualStockTransaction() {

    let selectedItem = this.editModel();

    // delete id if new so modal recognizes it as new
    if (this.newId === selectedItem.id)
      delete selectedItem.id;

    selectedItem.documentId = this.document().id;
    selectedItem.document = this.documentWithoutLineItems;
    let result = await this.createWarehouseDialog([selectedItem]);
    if (result.documentLineItems) {
      await this.onRowEditCancel(this.editFieldsArray[0]);
      await this.afterDocumentLineItemsMutated();
    }
  }

  /** When true, nested refresh must not clear loading (outer action owns the spinner). */
  private suppressRefreshLoadingManagement = false;

  override setLoading(isLoading: boolean = true) {
    if (!isLoading && this.suppressRefreshLoadingManagement) {
      return;
    }
    super.setLoading(isLoading);
  }

  override async refresh(force: boolean = false) {

    //for change detection, see comment tagged with #currentlyLoadedDocumentId
    this.currentlyLoadedDocumentId = this.id;

    if (this.document()?.status != DocumentStatusType.CO && this.document()?.status != DocumentStatusType.RE)
      this.inlineEditMode = true;

    return await super.refresh(force);
  }

  /**
   * Ensures display-related signals and triggers exist on a line item.
   * Call when an item may have been created or loaded without them (e.g. before onRowEditInit).
   * Replaces any that are frozen (e.g. from GraphQL cache) so we never subscribe to read-only Subjects.
   */
  private ensureLineItemDisplaySignalsAndTriggers(item: DocumentLineItemDisplayClass): void {
    if (!item.currentlyInEditMode || Object.isFrozen(item.currentlyInEditMode))
      item.currentlyInEditMode = signal<boolean>(false);
    if (!item.dataChangedTrigger || Object.isFrozen(item.dataChangedTrigger))
      item.dataChangedTrigger = new Subject<void>();
    if (!item.detailToListChangeTrigger || Object.isFrozen(item.detailToListChangeTrigger))
      item.detailToListChangeTrigger = new Subject<void>();
    if (!item.childrenHidden || Object.isFrozen(item.childrenHidden))
      item.childrenHidden = signal<boolean>(false);
    if (!item.hidden || Object.isFrozen(item.hidden))
      item.hidden = signal<boolean>(false);
    if (!item.childrenHiddenChanged || Object.isFrozen(item.childrenHiddenChanged))
      item.childrenHiddenChanged = new Subject<boolean>();
  }

  private stripLineItemDisplaySignalsAndTriggers(item: DocumentLineItemDisplayClass): void {
    delete item.currentlyInEditMode;
    delete item.dataChangedTrigger;
    delete item.detailToListChangeTrigger;
    delete item.childrenHidden;
    delete item.hidden;
    delete item.childrenHiddenChanged;
  }

  setItems(inputItems: DliEx[], totalCount?: number, skipClone: boolean = false): void {
    let items = inputItems?.filter((item) => item != null) ?? [];
    if (items?.length != inputItems?.length)
      console.error('setItems inputItems contains null/undefined values - should not happen');

    if (!skipClone)
      items = simpleDeepClone(items);

    if (this.crudOptions.enableStream === true)
      items = this.mergePendingStreamSavedItems(items);

    // the next two blocks are only needed if stream is enabled - With streaming updates during inline edit, these two blocks merge the incoming list with UI truth
    //TODO: find a better solution than this

    if (this.crudOptions.enableStream === true && this.inEditingSig()) {
      const existingIds = new Set(items.map(item => item?.id).filter(id => !!id));
      const currentlyVisibleItems = this.itemsSig() ?? [];
      const currentlyVisiblePersistedItems = currentlyVisibleItems
        .filter(item => !!item?.id && item.id !== this.newId);
      for (const visibleItem of currentlyVisiblePersistedItems) {
        if (!existingIds.has(visibleItem.id)) {
          const previousIndex = currentlyVisibleItems.findIndex(item => item?.id === visibleItem.id);
          const boundedIndex = Math.max(0, Math.min(previousIndex, items.length));
          items.splice(boundedIndex, 0, { ...visibleItem });
          existingIds.add(visibleItem.id);
        }
      }
    }

    // Stream updates can arrive while inline editing and overwrite/remove the local edit row
    // (especially for unsaved new rows). Preserve the current edit model in the visible list.
    if (this.crudOptions.enableStream === true && this.inEditingSig() && this.editModel() && !this.isRowEditSaveRunning) {
      const currentEditItem = this.editModel() as DliEx;
      const currentEditId = currentEditItem?.id ?? this.inEditRowId ?? this.newId;
      const existingEditIndex = currentEditId ? items.findIndex((item) => item?.id === currentEditId) : -1;
      if (existingEditIndex >= 0) {
        items[existingEditIndex] = currentEditItem;
      } else {
        const previousItems = this.itemsSig() ?? [];
        const previousEditIndex = currentEditId ? previousItems.findIndex((item) => item?.id === currentEditId) : -1;
        const preferredIndex = previousEditIndex >= 0 ? previousEditIndex : this.newItemIndex ?? this.editRowIndex ?? items.length;
        const boundedIndex = Math.max(0, Math.min(preferredIndex, items.length));
        items.splice(boundedIndex, 0, currentEditItem);
      }
    }

    for (let item of items) {
      this.ensureLineItemDisplaySignalsAndTriggers(item);

      const docForPricing = this.document() ?? item.document;
      if (docForPricing && lineItemContractFactorEffective(item) > 1) {
        CalculateDocumentLineItemPrices(item, docForPricing);
      }

      if (item.getDocumentLineItemPredecessorRelationLinks?.length > 0)
        item.predecessorKey = item.getDocumentLineItemPredecessorRelationLinks.map(x => x.sourceDocumentId).join();

      //TODO check if we need cancel subscription earlier
      item.childrenHiddenChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {

        let allChildren = this.itemsSig().filter(x => x.parentId == item.id);
        for (let child of allChildren)
          this.hideShowDLIWithChildrenRecursively(child.id, x);
      });
      if (item.parentId) {
        let parent = items.find(x => x.id === item.parentId);
        if (parent) {
          parent.hasChildren = true;
        }
      }
    }

    if (this.rowGroupMode()) {
      this.table().expandedRowKeys = { "undefined": true };
      for (let key of items.map(x => x.predecessorKey).filter((v, i, a) => v && a.indexOf(v) === i))
        this.table().expandedRowKeys[key] = true;

      let sortedItems: DliEx[] = [];
      for (let item of items) {
        if (sortedItems.map(x => x.id).includes(item.id))
          continue;
        sortedItems.push(...items.filter(x => x.predecessorKey === item.predecessorKey));
      }
      items = sortedItems;
    }

    //TODO improve to call it only when the items are changed
    this.itemsChanged.emit(items);

    // Always skip clone in base: this override already cloned when `skipClone` was false.
    // If we forward skipClone=false here, base deep-clones again and replaces every row
    // (including the live editModel), which destroys inline editors (e.g. ProductDropDown).
    super.setItems(items, totalCount, true);

    if (this.storagePlacesToLoad?.length > 0 || this.loadAllStoragePlaces) {
      this.runCustomColumnEnrichment(async () => {
        await this.dliService.enrichItems(this.itemsSig(), this.crudOptions);
      });
    }

    if (this.selectedItems().length > 0) {
      this.refreshToolbarButtons();
    }

    if (!this.crudOptions.enableStream)
      this.updateApolloCache();
    // console.log('itemsSig', this.itemsSig());
  }

  private rememberPendingStreamSavedItems(items: (DocumentLineItem | DliEx)[]): void {
    if (!this.crudOptions.enableStream)
      return;

    const now = Date.now();
    for (const item of items ?? []) {
      if (!item?.id)
        continue;
      this.pendingStreamSavedItemsById.set(item.id, { item: { ...item } as DliEx, addedAt: now });
    }
  }

  private mergePendingStreamSavedItems(streamItems: DliEx[]): DliEx[] {
    if (!this.pendingStreamSavedItemsById.size)
      return streamItems;

    const pendingTtlMs = 15000;
    const now = Date.now();
    const mergedItems = [...streamItems];
    const existingIds = new Set(mergedItems.map(item => item?.id).filter(id => !!id));

    for (const [id, pending] of this.pendingStreamSavedItemsById.entries()) {
      if (!id || !pending?.item) {
        this.pendingStreamSavedItemsById.delete(id);
        continue;
      }

      if (existingIds.has(id)) {
        this.pendingStreamSavedItemsById.delete(id);
        continue;
      }

      if (now - pending.addedAt > pendingTtlMs) {
        this.pendingStreamSavedItemsById.delete(id);
        continue;
      }

      mergedItems.push({ ...pending.item });
    }

    return mergedItems;
  }

  /**
   * hides the DLI and all Children. Used for collapsing the DLI tree
   * @param id 
   */
  hideShowDLIWithChildrenRecursively(id: string, hide: boolean) {
    let dli = this.itemsSig().find(x => x.id === id);
    if (dli) {
      dli.childrenHidden.set(hide);
      dli.hidden.set(hide);
    }
    let anyChildsOfDLI = this.itemsSig().filter(x => x.parentId == id);
    for (let child of anyChildsOfDLI) {
      this.hideShowDLIWithChildrenRecursively(child.id, hide);
    }
  }

  /**
   * @param lineItems will be cloned into Dialog
   * @returns Result of the modal interaction.
   */
  async createWarehouseDialog(lineItems: DocumentLineItemDisplayClass[]): Promise<ManualOverrideResult | undefined> {
    return await lastValueFrom(
      this.modalService.fromComponent(WidgetDliManualQuantityAndStoragePlaceChooserComponent, {
        locals: {
          documentLineItems: simpleDeepClone(lineItems).map(x => {
            x.document = x.document ?? this.document();
            return x;
          })
        },
        title: this.baseServices.i18nService.translate(_("common.storage-options")),
        closable: true,
        size: "lg",
        maximizable: true,
      }))
  }

  showAccessoryList() {
    const item = this.lastSelectedItemSig() ?? this.editModel();
    const persistAccessories = this.isPersistedDocumentLineItem(item);

    const ref = this.baseServices.modalService.fromComponent(AccessoryConfigProductLinkListWidgetComponent, {
      size: 'lg',
      verticalAlign: 'top',
      closable: true,
      maximizable: true,
      title: this.baseServices.i18nService.translate(_("line-item.accessory-config-product-links")),
      locals: {
        product: item.product,
        direction: this.document()?.documentDirection,
        lineItem: item,
        persistAccessoriesOnSubmit: persistAccessories,
        checkboxMode: true,
        markAutomatic: true,
      } as Partial<AccessoryConfigProductLinkListWidgetComponent>,
    });

    ref.pipe(
      catchError(err => { throw 'error in accessoryConfigProductLinkListWidget subscription: ' + err })
    ).subscribe(async (selectedLinks: AccessoryConfigProductLink[]) => {
      if (!selectedLinks?.length) {
        return;
      }

      const accessoryConfigs = selectedLinks
        .map((link) => link.accessoryConfig)
        .filter((config): config is AccessoryConfig => !!config?.id);

      if (persistAccessories) {
        this.suppressRefreshLoadingManagement = true;
        this.setLoading(true);
        try {
          await this.addAccessoriesToPersistedParentLine(item as DocumentLineItemDisplayClass, accessoryConfigs);
        } finally {
          this.suppressRefreshLoadingManagement = false;
          this.setLoading(false);
        }
      } else {
        this.setAppliedAccessorySignal(selectedLinks, true);
      }
    });
  }

  lotSizeInfo(lotSize: number, rowData: DocumentLineItemDisplayClass): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const restValue = rowData?.quantity % lotSize;
      if (restValue !== 0) {
        let message = this.i18n.translate(_("document.lot-size-info-message"), {
          lotSize,
          restValue
        });
        let ref = this.modalService.showConfirmationDialog({
          title: this.i18n.translate(_("document.lot-size-info")),
          message: message,
          btnYesText: this.i18n.translate(_("common.confirm")),
          btnNoText: this.i18n.translate(_("common.no")),
          size: "xs"
        });

        ref.subscribe({
          next: (result) => {
            if (result) {
              // resize
              resolve(true);
            } else {
              // don't resize
              resolve(false);
            }
          },
          error: (error) => {
            console.error(error);
            reject(error);
          }
        });
      } else {
        resolve(true); // If no warning is needed, resolve with true
      }
    });
  }

  async indentSelectedItems() {
    if (this.selectedItems().length != 1) {
      return;
    }

    this.setLoading(true);
    try {
      await this.dliService.indentDocumentLineItem(this.selectedItems()[0].id);
      this.notifyDocumentLineItemsChanged();
    }
    catch (e) {
      console.error(e);
      this.notifyLineItemMutationError(e);
    }
    finally {
      this.setLoading(false);
    }
    this.refresh(); //for now refresh all
  }

  async outdentSelectedItems() {
    if (this.selectedItems().length != 1) {
      return;
    }

    this.setLoading(true);
    try {
      await this.dliService.outdentDocumentLineItem(this.selectedItems()[0].id);
      this.notifyDocumentLineItemsChanged();
    }
    catch (e) {
      console.error(e);
      this.notifyLineItemMutationError(e);
    }
    finally {
      this.setLoading(false);
    }
    this.refresh(); //for now refresh all
  }

  private notifyLineItemMutationError(error: unknown): void {
    const apolloError = error as ApolloError;
    const message = apolloError?.graphQLErrors?.[0]?.message;
    if (message) {
      this.baseServices.notificationService.error(message);
    }
  }

  /**
   * @description Temporary switches ASC and DESC but doesnt save - just for overview on long lists 
   * 
   */
  async switchDirection() {
    this.tableAscendingSig.set(!this.tableAscendingSig());
    // minus should be plus and plus should be minus
    this.upDownDirectionalNumberSig.set(this.upDownDirectionalNumberSig() === -1 ? 1 : -1);
    await this.refresh();
    this.cdRef.detectChanges();
  }

  override isCellVisibleOnEdit(rowData: DocumentLineItemDisplayClass, field: string): boolean {
    if (rowData.type == DocumentLineItemType.TEXT) {
      if (field == "description" || field == "addition" || field == "addition2" || field == "status" || field == "positionNumber" || field == "productText")
        return true;
      else
        return false;
    }
    return super.isCellVisibleOnEdit(rowData, field);
  }


  //#region custom columns (storagePlace)
  getCustomColumnDisplayComponent(outputSpan: HTMLElement, outputCell: HTMLElement, col: PrimeColumnDef, colMetaData: { priceList: string, currency: CurrencyCode, storagePlace: string, allStoragePlaces: boolean }, rowData: DliEx) {
    if (!colMetaData?.storagePlace && !colMetaData?.allStoragePlaces) {
      return;
    }
    const quantity = getCustomColumnValue(rowData, col.field);
    if (quantity === undefined) {
      return;
    }
    const styledQuantity = this.getTypeStyledDisplayValue({ type: 'number' } as PrimeColumnDef, quantity, outputCell, outputSpan);
    outputSpan.innerHTML = `${styledQuantity.toString()} ${rowData.product?.unit?.identifier ?? ""}`;
  }

  async addCustomColumn() {
    let storagePlaceBlacklist: string[] = [];
    if (this.loadAllStoragePlaces)
      storagePlaceBlacklist.push(AllStoragePlacesIdentifier);
    storagePlaceBlacklist.push(...this.storagePlacesToLoad ?? []);

    let result = await lastValueFrom(this.baseServices.modalService.fromComponent(ProductListCustomColumnDialogComponent, {
      size: 'sm',
      title: this.baseServices.i18nService.translate("common.columns-add"), //this.baseServices.i18nService.translate('product.add'),
      closable: true,
      locals: {
        showPriceList: false,
        storagePlaceIdBlacklist: storagePlaceBlacklist,
      }
    }));

    if (!result || (!result.priceList && !result.storagePlace && !result.allStoragePlaces))
      return;

    var config = this.currentColumnConfig.value;

    let currentUser = this.authService.getCurrentUser();
    if (!userHasSuperAdminRole(currentUser) && currentUser.id !== this.currentColumnConfig.value.ownerId) {
      let res = await lastValueFrom(this.baseServices.modalService.showConfirmationDialog({
        title: this.baseServices.i18nService.translate("common.missing-right"),
        message: this.baseServices.i18nService.translate("column-chooser.missing-edit-rights-create-new"),
        btnYesText: this.baseServices.i18nService.translate("common.yes"),
        btnNoText: this.baseServices.i18nService.translate("common.no"),
      }));
      if (!res)
        return;

      let newConf: ColumnConfig = {
        id: null,
        name: this.baseServices.i18nService.translate("column-chooser.config-name-copy", { name: this.currentColumnConfig.value.name ?? "Standard" }) ?? "",
        baseView: this.currentColumnConfig.value.baseView,
        variant: this.currentColumnConfig.value?.variant,
        originType: ConfigOriginType.USER,
        columns: simpleDeepClone(this.currentColumnConfig.value.columns),
        disabled: false,
      }

      let savedConfig = await lastValueFrom(this.baseServices.modalService.fromComponent(ColumnChooserRenameDialogComponent, {
        title: this.baseServices.i18nService.translate(_("column-chooser.config-name-new")),
        size: "sm",
        closable: true,
        maximizable: false,
        locals: {
          detailModel: newConf,
        }
      }));
      if (!savedConfig)
        return;

      await this.baseServices.columnService.setUsersColumnConfig(savedConfig.id);

      config = savedConfig;
    }

    if (result.allStoragePlaces) {
      config.columns.push({
        field: AllStoragePlacesIdentifier,
        metaData: { allStoragePlaces: result.allStoragePlaces },
        dataType: 'string',
        sortable: false,
        filterable: false,
        customHeaderName: `${this.baseServices.i18nService.translate(_("columns.storagePlace"))} '${this.baseServices.i18nService.translate(_("warehouse.all-storage-places"))}'`,
      } as ColumnDefinitionEx);
    }
    else if (result.storagePlace)
      config.columns.push({
        field: result.storagePlace.id,
        metaData: { storagePlace: result.storagePlace.id },
        dataType: 'string',
        sortable: false,
        filterable: false,
        customHeaderName: `${this.baseServices.i18nService.translate(_("columns.storagePlace"))} '${result.storagePlace.identifier}'`,
      } as ColumnDefinitionEx);

    let saved = await this.baseServices.columnService.saveItem(config);

    this.setColumnConfig(saved);
    this.refresh();
  }

  override getPopupMenuItems(): MenuItem[] {
    let items: MenuItem[] = super.getPopupMenuItems();

    items.push({
      label: this.baseServices.i18nService.translate('common.columns-add'),
      icon: 'pi pi-plus',
      command: () => this.addCustomColumn()
    });

    return items;
  }
  //#endregion

  //#region product price info 

  /**
   * Description: Only returns filter of the product price info
   * @returns Promise<ProductPriceInfoInput>
   */
  async getProductPriceFilter(dli: DocumentLineItem, doc: Document): Promise<ProductPriceInfoInput | null> {

    const productId = dli?.product?.id ?? dli?.productId;
    if (!productId) {
      return null;
    }

    let addressId: string = null;
    let direction: DocumentDirection = null;

    if (doc.documentDefinition?.documentDirection) {
      direction = doc.documentDefinition.documentDirection;
    }

    addressId = doc.addressId ?? doc.address?.id ?? null;

    return {
      productId,
      addressId,
      direction,
      onlyActive: true,
      onlyOfPriceListIds: doc.priceListId != null ? [doc.priceListId] : [],
      dateOfCheck: graphQlDateOnly(doc.date),
    } as ProductPriceInfoInput;
  }

  /**
   * SBOMH (trading-set header): disable unit price only when the header rolls up from SBOMI — i.e. there is no
   * document list or customer-specific list row for the set product in the loaded price infos, or the server
   * marked the line as calculated ({@link DocumentLineItem.isCalculatedItemPrice} true).
   */
  private sbomhHeaderItemPriceControlDisabled(rowData: DocumentLineItemDisplayClass): boolean {
    if (rowData?.type !== DocumentLineItemType.SBOMH) {
      return false;
    }
    if (rowData.isCalculatedItemPrice === false) {
      return false;
    }
    if (rowData.isCalculatedItemPrice === true) {
      return true;
    }
    const infos = this.activeProductPriceInfos() ?? [];
    const hasListOrCustomer = infos.some(
      (x) => x.sourceType === PriceSourceType.PRICELIST || x.sourceType === PriceSourceType.CUSTOMERPRICE,
    );
    return !hasListOrCustomer;
  }

  /**
   * SBOMH (trading-set header): when the header is set to manual unit price (isCalculatedItemPrice false) and it has
   * child DLIs (SBOMI), the children must not allow unit price editing in edit mode.
   */
  private sbomhChildItemPriceControlDisabled(rowData: DocumentLineItemDisplayClass): boolean {
    if (!this.inEditingSig()) {
      return false;
    }
    const parentId = rowData?.parentId;
    if (!parentId) {
      return false;
    }
    const parent = (this.itemsSig() ?? []).find((x) => x?.id === parentId);
    if (!parent || parent.type !== DocumentLineItemType.SBOMH) {
      return false;
    }
    if (parent.isCalculatedItemPrice !== false) {
      return false;
    }
    const hasChildren = (this.itemsSig() ?? []).some((x) => x?.parentId === parent.id);
    return hasChildren;
  }

  private itemPriceControlDisabled(rowData: DocumentLineItemDisplayClass): boolean {
    return (
      this.sbomhHeaderItemPriceControlDisabled(rowData) ||
      this.sbomhChildItemPriceControlDisabled(rowData)
    );
  }

  private async shouldApplyAutomaticDiscounts(rowData: DliEx): Promise<boolean> {
    // Purchase (IN) documents let CUSTOMERPRICE lines through: their vendor price is tagged
    // CUSTOMERPRICE but the backend resolves the supplier's stored Einkaufskonditionen discount.
    const isPurchase = this.document()?.documentDefinition?.documentDirection === DocumentDirection.IN;
    let priceSource = rowData.priceSource ?? this.editModel()?.priceSource;

    if (priceSource === PriceSourceType.CUSTOMERPRICE && !isPurchase) {
      return false;
    }
    if (priceSource) {
      return true;
    }

    const doc = this.document();
    if (!doc || (!rowData.productId && !rowData.product?.id)) {
      return false;
    }

    await this.loadProductPriceInfosIfNeeded(rowData, doc);
    if (this.productPriceInfoComponent) {
      if (rowData.itemPrice == null) {
        this.productPriceInfoComponent.preSelectByCustomerOrListPrice();
      } else {
        this.productPriceInfoComponent.autoSelectPriceSource(rowData.itemPrice, rowData.quantity ?? 1);
      }
    }

    priceSource = rowData.priceSource ?? this.editModel()?.priceSource;
    if (priceSource === PriceSourceType.CUSTOMERPRICE && !isPurchase) {
      return false;
    }
    if (priceSource) {
      return true;
    }

    return true;
  }

  async loadProductPriceInfosIfNeeded(lineItem: DocumentLineItem, doc: Document): Promise<void> {
    const freshFilter = await this.getProductPriceFilter(lineItem, doc);
    const activeFilter = this.activeProductPriceInfoFilter;

    if (deepEqual(activeFilter, freshFilter))
      return;

    if (this.productPriceInfoComponent) {
      this.productPriceInfoComponent.lineItem.set(lineItem);
    }
    this.activeProductPriceInfoFilter = freshFilter;
    await this.loadProductPriceInfos(lineItem, doc);
  }

  /**
   * Converts a price to the document's tax type (net/gross). Use for both normal and bulk price lists.
   * @param price - The source price
   * @param docTaxType - Document's tax type
   * @param sourcePriceIncludesTax - Whether the source price includes tax (e.g. from remote API)
   * @param taxPercentage - Line item tax percentage
   * @param decimalPlaces - Decimal places for rounding
   */
  private convertPriceToDocumentTaxType(
    price: number,
    docTaxType: TaxType,
    sourcePriceIncludesTax: boolean,
    taxPercentage: number,
    decimalPlaces: number
  ): number {
    if (docTaxType !== TaxType.GROSS && sourcePriceIncludesTax)
      return netPriceOf(price, taxPercentage, decimalPlaces);

    if (docTaxType === TaxType.GROSS && !sourcePriceIncludesTax)
      return grossPriceOf(price, taxPercentage, decimalPlaces);
    return price;
  }

  /**
   * Updates product price info component with the given quantity and refreshes price infos if needed.
   */
  async updateProductPriceInfoQuantity(rowData: DocumentLineItemDisplayClass, quantity: number): Promise<void> {
    if (rowData.productId || rowData.product?.id) {
      if (this.productPriceInfoComponent)
        this.productPriceInfoComponent.quantity.set(quantity);
      await this.loadProductPriceInfosIfNeeded(rowData, this.document());
    }
    if (this.productPriceInfoComponent) {
      this.productPriceInfoComponent.autoSelectPriceSource(rowData.itemPrice, quantity);
    }
  }

  async updateQuantityBasedOnAllowFractions(rowData: DocumentLineItem, quantity: number) {
    let allowFractions = this.activeUnitConversions().find(x => x.unit?.id === rowData.unit?.id)?.allowFractions;
    if (allowFractions === false && allowFractions !== undefined) {
      quantity = Math.ceil(quantity);
      rowData.quantity = quantity;
      this.selectedQuantitySig.set(quantity);
      this.editModel.update(x => {
        x.quantity = quantity;
        return x;
      });
      await this.updateProductPriceInfoQuantity(rowData, quantity);
    }
  }

  async loadProductPriceInfos(lineItem: DocumentLineItem, doc: Document): Promise<void> {

    if (!doc) {
      return;
    }

    if (!lineItem) {
      return;
    }

    if (!this.activeProductPriceInfoFilter) {
      this.activeProductPriceInfos.set([]);
      return; //nothing to load
    }

    if (!this.activeProductPriceInfoFilter.productId)
      return;

    let newPriceInfos: ProductPriceInfoExt[] = [];

    let remotePriceInfos = simpleDeepClone(await this.productPriceInfoService.getProductPriceInfoData(this.activeProductPriceInfoFilter)) as ProductPriceInfoExt[];

    for (let i = 0; i < remotePriceInfos.length; i++) {
      const item = remotePriceInfos[i];
      (item as ProductPriceInfoExt & { originPriceIncludesTax?: boolean }).originPriceIncludesTax = item.priceIncludesTax;
      let taxPercentage = lineItem.taxPercentage;
      // just remove tax if priceList has tax - then taxPercentage is known by product defaultSalesTaxCodeIds 
      if (item.priceIncludesTax && (lineItem.taxPercentage === 0 || lineItem.taxPercentage === null || lineItem.taxPercentage === undefined)) {
        let salesTaxCodeIdToCheck = doc.documentDefinition?.documentDirection === DocumentDirection.IN ? lineItem.product?.defaultSalesTaxCodeInId : lineItem.product?.defaultSalesTaxCodeOutId;
        let salesTaxCodeDetail: SalesTaxCodeDetail | null = salesTaxCodeIdToCheck ? await this.salesTaxCodeDetailService.getItemDataBySalesTaxCodeId(salesTaxCodeIdToCheck) : null;
        if (salesTaxCodeDetail) {
          taxPercentage = salesTaxCodeDetail.value;
        }
      }
      const newPrice = this.convertPriceToDocumentTaxType(item.price, doc.taxType, item.priceIncludesTax, taxPercentage, lineItem.decimalPlaces);
      item.originPrice = item.price;
      item.price = newPrice;
      item.unitConversionPrice = xMultiply(newPrice, lineItem.quantityToBaseUnitFactor ?? 1);
      item.priceIncludesTax = doc.taxType === TaxType.GROSS || doc.taxType === TaxType.FREE;
      newPriceInfos = [...newPriceInfos, item];
    }
    let validBulkPriceInfosSet: Set<ProductPriceInfoExt> = new Set<ProductPriceInfoExt>();
    let validBulkPriceInfos: ProductPriceInfoExt[] = [];

    if (newPriceInfos.length > 0 && lineItem.quantity && lineItem && doc) {
      for (let item of newPriceInfos) {
        // can bulkPriceListEntries be null?
        if (item.bulkPriceListEntries?.length > 0)
          validBulkPriceInfos = this.getBulkPrices(newPriceInfos, lineItem.quantity, lineItem, doc);
      }
      if (validBulkPriceInfosSet.size > 0)
        validBulkPriceInfos = Array.from(validBulkPriceInfosSet);
    }

    newPriceInfos = [...newPriceInfos, ...validBulkPriceInfos];

    // CUSTOMERPRICE → ACCESSORYCONFIG → PRICELIST (see PRODUCT_PRICE_SOURCE_PRIORITY_ORDER)
    newPriceInfos.sort(
      (a, b) =>
        (a.priority ?? productPriceSourcePriority(a.sourceType)) -
        (b.priority ?? productPriceSourcePriority(b.sourceType)),
    );

    this.activeProductPriceInfos.set(newPriceInfos);

    if (this.activeProductPriceInfos().length == 0) {
      let documentDate = doc.date
        ? this.localeDatePipe.transform(doc.date, 'shortDate', this.localeDatePipe.getActiveLocale()) ?? ''
        : '';
      if (documentDate.length > 0)
        documentDate = `'${documentDate}'`;
      let priceListName = doc.priceList?.identifier ?? '';
      if (doc.priceList?.description)
        priceListName += ` - ${doc.priceList?.description}`;
      if (priceListName.length > 0)
        priceListName = `'${priceListName}'`;
      this.baseServices.notificationService.warning(_('document-line-item.no-price-found-in-price-list'), {
        priceListName,
        documentDate,
      });
    }

  }

  // we get all bulk prices because when user gets quantity up or down its otherwise not working
  getBulkPrices(productPriceInfos: ProductPriceInfoExt[], quantity: number, lineItem: DocumentLineItem, doc: Document): ProductPriceInfoExt[] {
    const validBulkPriceInfosSet = new Set<ProductPriceInfoExt>();

    for (const item of productPriceInfos) {
      if (item.bulkPriceListEntries?.length === 0) continue;
      const sourcePriceIncludesTax = (item as ProductPriceInfoExt & { originPriceIncludesTax?: boolean }).originPriceIncludesTax ?? item.priceIncludesTax;
      const docPriceIncludesTax = doc.taxType === TaxType.GROSS || doc.taxType === TaxType.FREE;

      for (const bulkPrice of item.bulkPriceListEntries) {
        const newPrice = this.convertPriceToDocumentTaxType(bulkPrice.price, doc.taxType, sourcePriceIncludesTax, lineItem.taxPercentage, lineItem.decimalPlaces);
        validBulkPriceInfosSet.add({
          originPrice: bulkPrice.price,
          price: newPrice,
          unitConversionPrice: xMultiply(newPrice, lineItem.quantityToBaseUnitFactor ?? 1),
          priceIncludesTax: docPriceIncludesTax,
          quantity: bulkPrice.minQuantity,
          nonCashDiscountable: item.nonCashDiscountable,
          nonDiscountable: item.nonDiscountable,
          nonProvisionable: item.nonProvisionable,
          currency: doc.currency,
          sourceType: item.sourceType,
          sourceIdentifier: item.sourceIdentifier,
          priority: item.priority,
          color: item.color,
        });
      }
    }

    return validBulkPriceInfosSet.size > 0 ? Array.from(validBulkPriceInfosSet) : [];
  }

  refreshProductPriceInfosPerConversionRate(lineItem: DocumentLineItem): void {
    this.activeProductPriceInfos.set(this.activeProductPriceInfos().map(x => {
      x.unitConversionPrice = xMultiply(x.price, lineItem.quantityToBaseUnitFactor ?? 1);
      return x;
    }));
  }

  getFieldFromCurrentColumnConfig(field: string): string {
    return this.currentColumnConfig?.value?.columns?.find(x => x.field === field)?.field;
  }

  /** Whether requestedQuantity is visible in the active column configuration. */
  isRequestedQuantityColumnVisible(): boolean {
    const col = this.currentColumnConfig?.value?.columns?.find(c => c.field === 'requestedQuantity');
    return col?.visible === true;
  }

  /**
   * Sets quantity defaults after product selection/clear — not on empty new rows before a product is chosen.
   * Non-storable products always get quantity 1. Storable products with requestedQuantity column visible keep quantity empty.
   */
  private applyQuantityDefaultAfterProductSelection(
    rowDataDli: DocumentLineItemDisplayClass,
    productSelected: boolean,
  ): void {
    if (!productSelected) {
      if (this.isRequestedQuantityColumnVisible()) {
        rowDataDli.quantity = null;
        this.selectedQuantitySig.set(null);
      } else {
        rowDataDli.quantity = 1;
        this.selectedQuantitySig.set(1);
      }
      return;
    }

    if (!rowDataDli.isStorable) {
      rowDataDli.quantity = 1;
      this.selectedQuantitySig.set(1);
      return;
    }

    if (this.isRequestedQuantityColumnVisible()) {
      if (rowDataDli.quantity == null) {
        this.selectedQuantitySig.set(null);
      } else {
        this.selectedQuantitySig.set(rowDataDli.quantity);
      }
      return;
    }

    if (rowDataDli.quantity == null) {
      rowDataDli.quantity = 1;
      this.selectedQuantitySig.set(1);
    } else {
      this.selectedQuantitySig.set(rowDataDli.quantity);
    }
  }

  toggleGroupMode() {
    let rowGroupMode: "subheader" /* | "rowspan"  */ | undefined = this.rowGroupMode() === "subheader" ? undefined : "subheader";

    this.crudOptions.fragment = rowGroupMode ?
      (this.loadDocumentTooSig() ? GET_DOCUMENTLINEITEMS_WITH_DOC_AND_PREDECESSOR : GET_DOCUMENTLINEITEMS_SINGLE_PREDECESSOR) :
      (this.loadDocumentTooSig() ? GET_DOCUMENTLINEITEMS_WITH_DOC : GET_DOCUMENTLINEITEMS);

    if (!rowGroupMode)
      this.baseServices.settingsService.clearSetting(S_DLI_ROW_GROUP_MODE);
    else
      this.baseServices.settingsService.setSetting(S_DLI_ROW_GROUP_MODE, rowGroupMode);

    this.refresh().then(() => this.rowGroupMode.set(rowGroupMode));
  }

  //#endregion

  /**
   * Toggles SBOMH calculated vs fixed header mode (not saved here).
   * Dialog sets {@link pendingRecalculateTradingSetPrices} for the next {@link onRowEditSave}.
   */
  async changeSBOMHStatus(rowData: DocumentLineItemDisplayClass) {
    if (rowData.type !== DocumentLineItemType.SBOMH) {
      return;
    }

    const isNew = rowData.id === this.newId;
    const switchingToManual = rowData.isCalculatedItemPrice === true;
    const switchingToCalculated = rowData.isCalculatedItemPrice !== true;
    let recalculateOnNextSave = false;

    if (!isNew) {
      if (switchingToManual) {
        recalculateOnNextSave = await lastValueFrom(this.modalService.showConfirmationDialog({
          title: this.i18n.translate(_('document-line-item.recalculate-from-sbomh-price')),
          message: this.i18n.translate(_('document-line-item.recalculate-from-sbomh-price-confirm')),
          btnYesText: this.i18n.translate(_('common.yes')),
          btnNoText: this.i18n.translate(_('common.no')),
          size: 'xs',
        }));
      } else if (switchingToCalculated) {
        recalculateOnNextSave = await lastValueFrom(this.modalService.showConfirmationDialog({
          title: this.i18n.translate(_('document-line-item.recalculate-from-sbomi-prices')),
          message: this.i18n.translate(_('document-line-item.recalculate-from-sbomi-prices-confirm')),
          btnYesText: this.i18n.translate(_('common.yes')),
          btnNoText: this.i18n.translate(_('common.no')),
          size: 'xs',
        }));
      }
    }
    else
      recalculateOnNextSave = true;

    if (recalculateOnNextSave) {
      rowData.itemPrice = undefined;
      rowData.discountedLinePrice = undefined;
      rowData.discountedLinePriceWithTax = undefined;
      rowData.proratedLinePrice = undefined;
      rowData.proratedLinePriceWithTax = undefined;
    }

    rowData.isCalculatedItemPrice = !rowData.isCalculatedItemPrice;
    if (this.editModel()?.id === rowData.id) {
      this.editModel.update((dli) => {
        dli.isCalculatedItemPrice = rowData.isCalculatedItemPrice;
        return dli;
      });
    }

    this.pendingRecalculateTradingSetPrices = recalculateOnNextSave;

    const ipc = this.itemPriceInput();
    if (ipc) {
      ipc.disabled.set(this.itemPriceControlDisabled(rowData));
    }

    // Manual header: enable list/customer prices for editing. Keep the current unit price when the
    // user declined recalculate (itemPrice still set); only auto-pick list/customer when cleared.
    if (this.inEditingSig() && !rowData.isCalculatedItemPrice) {
      await this.loadProductPriceInfos(rowData, this.document());
      if (this.productPriceInfoComponent) {
        if (rowData.itemPrice != null) {
          this.productPriceInfoComponent.writeValue(rowData.itemPrice);
        } else {
          this.productPriceInfoComponent.preSelectByCustomerOrListPrice();
        }
      }
    }

    this.cdRef.markForCheck();
  }

  getDistinctLinks(rowData: DocumentLineItemDisplayClass): DliRelationLinkInformation[] {
    return rowData.getDocumentLineItemPredecessorRelationLinks?.filter((x, index, self) =>
      index === self.findIndex((t) => t.sourceDocumentId === x.sourceDocumentId)
    );
  }
}